/**
 * Tote Ally Yours — storefront tote builder (bag + patch variants → cart.js)
 * Wizard: bag → design → review. Patches draggable; placement serialized on bag line item.
 */
function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function randomCustomizationId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tay-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function formatMoneyCents(cents, currency) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(
      Number(cents) / 100
    );
  } catch {
    return (Number(cents) / 100).toFixed(2);
  }
}

/**
 * @returns {string[]}
 */
function getCartSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    const sid = node.dataset.sectionId;
    if (sid && !ids.includes(sid)) ids.push(sid);
  });
  return ids;
}

function cartAddUrl() {
  if (typeof window !== 'undefined' && window.TAY_ROUTES && window.TAY_ROUTES.cartAdd) {
    return window.TAY_ROUTES.cartAdd;
  }
  return '/cart/add.js';
}

/**
 * @param {string | null | undefined} value
 * @param {number} max
 */
function clipLineProperty(value, max) {
  const s = String(value ?? '').trim();
  if (!s) return '';
  return s.length <= max ? s : s.slice(0, max);
}

/**
 * @param {any} product
 * @returns {{ label: string; shipsOn: string; note: string; badge: string } | null}
 */
function readPreOrder(product) {
  const po = product && product.pre_order;
  if (!po || typeof po !== 'object') return null;
  const label = String(po.label || '').trim();
  const shipsOn = String(po.ships_on || '').trim();
  const note = String(po.note || '').trim();
  const badge = String(po.badge || '').trim();
  if (!label && !shipsOn && !note && !badge) return null;
  return { label, shipsOn, note, badge };
}

/**
 * @param {{ label: string; shipsOn: string; note: string; badge: string } | null} po
 */
function preOrderChipHtml(po) {
  if (!po) return '';
  const text = po.label || (po.shipsOn ? `Ships ${po.shipsOn}` : '');
  if (!text && !po.badge) return '';
  const icon = po.badge
    ? `<img class="bbs-preorder-chip__art" src="${escapeHtml(po.badge)}" alt="" width="18" height="18">`
    : `<span class="material-symbols-outlined bbs-preorder-chip__icon" aria-hidden="true">schedule</span>`;
  const labelHtml = text ? `<span class="bbs-preorder-chip__text">${escapeHtml(text)}</span>` : '';
  return `<span class="bbs-preorder-chip">${icon}${labelHtml}</span>`;
}

/**
 * @param {any} variant
 * @returns {number | null}
 */
function inventoryQty(variant) {
  if (!variant || !variant.inventory_management) return null;
  const qty = Number(variant.inventory_quantity);
  return Number.isFinite(qty) ? qty : null;
}

/**
 * Priority: sold out → pre-order → low stock (1–9).
 * @param {any} product
 * @param {any} [variant]
 * @param {Record<string, string>} [labels]
 */
function statusChipHtml(product, variant, labels) {
  const v = variant || firstAvailableVariant(product?.variants || []) || product?.variants?.[0];
  const soldOut = !v || !v.available;
  if (soldOut) {
    return `<span class="bbs-preorder-chip bbs-preorder-chip--sold-out"><span class="bbs-preorder-chip__text">${escapeHtml((labels && labels.soldOut) || 'Sold out')}</span></span>`;
  }
  const po = readPreOrder(product);
  if (po) return preOrderChipHtml(po);
  const qty = inventoryQty(v);
  if (qty != null && qty > 0 && qty <= 9) {
    const tpl = String((labels && labels.lowStock) || 'Only [count] left').trim();
    let text = tpl
      .replace(/\{\{\s*count\s*\}\}/gi, String(qty))
      .replace(/\[count\]/gi, String(qty));
    if (!text.includes(String(qty)) || text === 'Only  left') {
      text = `Only ${qty} left`;
    }
    return `<span class="bbs-preorder-chip bbs-preorder-chip--low-stock"><span class="bbs-preorder-chip__text">${escapeHtml(text)}</span></span>`;
  }
  return '';
}

/**
 * @param {{ label: string; shipsOn: string; note: string; badge: string } | null} po
 */
function preOrderDetailsHtml(po) {
  if (!po) return '';
  const parts = [];
  if (po.label && po.shipsOn) {
    parts.push(`<p class="bbs-preorder-ships">Ships ${escapeHtml(po.shipsOn)}</p>`);
  }
  if (po.note) {
    parts.push(`<p class="bbs-preorder-note">${escapeHtml(po.note)}</p>`);
  }
  if (!parts.length) return '';
  return `<div class="bbs-preorder-details">${parts.join('')}</div>`;
}

/**
 * @param {Record<string, string>} props
 * @param {any} product
 */
function applyPreOrderLineProps(props, product) {
  const po = readPreOrder(product);
  if (!po) return;
  if (po.label) props['Pre-order'] = clipLineProperty(po.label, 200);
  if (po.shipsOn) props.Ships = clipLineProperty(po.shipsOn, 80);
  if (po.note) props['Pre-order note'] = clipLineProperty(po.note, 240);
}

/**
 * @param {string} url
 * @returns {Promise<HTMLImageElement>}
 */
function loadImageForCanvas(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}

/**
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Could not read preview image'));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read preview image'));
    reader.readAsDataURL(blob);
  });
}

/**
 * @param {Array<{ id: number; title: string; price: number; available: boolean; sku: string; image: string | null }>} variants
 */
function firstAvailableVariant(variants) {
  return variants.find((v) => v.available) ?? null;
}

function plainTextFromHtml(html) {
  if (!html) return '';
  return String(html)
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeInchText(text) {
  return plainTextFromHtml(text)
    .replace(/×/g, 'x')
    .replace(/[″”]/g, '"')
    .replace(/[’]/g, "'");
}

function positiveInches(value) {
  const n = Number(value);
  return n > 0 && n < 80 ? n : 0;
}

/**
 * First `W x H` pair in product copy. Matches the iOS catalog parser.
 * @param {string | null | undefined} html
 * @returns {{ w: number, h: number } | null}
 */
function parseSimpleWxH(html) {
  const m = normalizeInchText(html).match(/([\d.]+)\s*["']?\s*[xX]\s*([\d.]+)/);
  if (!m) return null;
  const w = Number(m[1]);
  const h = Number(m[2]);
  if (!(w > 0) || !(h > 0) || w > 40 || h > 40) return null;
  return { w, h };
}

/**
 * `9"W x 6"H` / `13.5"W × 13"H` — do not match words like "Woven".
 * @param {string | null | undefined} html
 * @returns {{ w: number, h: number } | null}
 */
function parseLabeledBagInches(html) {
  const t = normalizeInchText(html);
  const wM = t.match(/([\d.]+)\s*["']?\s*[wW](?![a-zA-Z])/);
  const hM = t.match(/([\d.]+)\s*["']?\s*[hH](?![a-zA-Z])/);
  if (!wM || !hM) return null;
  const w = Number(wM[1]);
  const h = Number(hM[1]);
  if (!(w > 0) || !(h > 0) || w > 40 || h > 40) return null;
  return { w, h };
}

/**
 * `11" x 7" x 3"` (W×H×D) or `21 x 6 x 11` (W×D×H when the middle is depth).
 * @param {string | null | undefined} html
 * @returns {{ w: number, h: number } | null}
 */
function parseTripleBagInches(html) {
  const m = normalizeInchText(html).match(
    /([\d.]+)\s*["']?\s*[xX]\s*([\d.]+)\s*["']?\s*[xX]\s*([\d.]+)/
  );
  if (!m) return null;
  const a = Number(m[1]);
  const b = Number(m[2]);
  const c = Number(m[3]);
  if (!(a > 0) || !(b > 0) || !(c > 0) || a > 40 || b > 40 || c > 40) return null;
  const minFace = Math.min(a, c);
  if (b < a && b < c && b <= minFace * 0.55) return { w: a, h: c };
  return { w: a, h: b };
}

function inchesFromDescriptionHTML(html) {
  return parseSimpleWxH(html);
}

function bagInchesFromDescription(html) {
  return parseLabeledBagInches(html) || parseTripleBagInches(html) || parseSimpleWxH(html);
}

function bagInchesFromTagsTitle(tags, title, handle) {
  const tagSet = new Set((Array.isArray(tags) ? tags : []).map((t) => String(t).toLowerCase()));
  if (tagSet.has('bag-size-small') || tagSet.has('app-size-small')) return { w: 18, h: 12 };
  if (tagSet.has('bag-size-medium') || tagSet.has('app-size-medium')) return { w: 22.5, h: 14.5 };
  if (tagSet.has('bag-size-large') || tagSet.has('app-size-large')) return { w: 25, h: 14 };
  const combined = `${title || ''} ${handle || ''}`.toLowerCase();
  if (combined.includes('mini') || combined.includes('small pocket')) return { w: 18, h: 12 };
  if (/\blarge\b/.test(combined)) return { w: 25, h: 14 };
  if (/\bmedium\b/.test(combined)) return { w: 22.5, h: 14.5 };
  if (/\bsmall\b/.test(combined)) return { w: 18, h: 12 };
  return null;
}

function resolvePatchInches(product, cfg) {
  const w = positiveInches(product?.patch_width_in);
  const h = positiveInches(product?.patch_height_in);
  if (w && h) return { w, h };
  const parsed = inchesFromDescriptionHTML(product?.description);
  if (parsed) return parsed;
  return {
    w: positiveInches(cfg?.defaultPatchWidthIn) || 2,
    h: positiveInches(cfg?.defaultPatchHeightIn) || 2,
  };
}

function patchCatalogImageUrl(product, variant) {
  return (variant && variant.image) || product?.featured_image || null;
}

function patchOnBagImageUrl(product, variant) {
  return (variant && variant.preview_image) || product?.preview_image || patchCatalogImageUrl(product, variant);
}

function resolveBagInches(bag, cfg) {
  const w = positiveInches(bag?.bag_width_in);
  const h = positiveInches(bag?.bag_height_in);
  if (w && h) return { w, h };
  const parsed = bagInchesFromDescription(bag?.description);
  if (parsed) return parsed;
  const tagged = bagInchesFromTagsTitle(bag?.tags, bag?.title, bag?.handle);
  if (tagged) return tagged;
  return {
    w: positiveInches(cfg?.defaultBagWidthIn) || 22.5,
    h: positiveInches(cfg?.defaultBagHeightIn) || 14.5,
  };
}

/**
 * @param {number} n
 */
function clamp01(n) {
  return Math.min(1, Math.max(0, n));
}

function clampNormRect(x, y, w, h) {
  const width = Number(w);
  const height = Number(h);
  if (!(width > 0.05) || !(height > 0.05)) return null;
  const nx = Math.max(0, Math.min(1, Number(x)));
  const ny = Math.max(0, Math.min(1, Number(y)));
  const nw = Math.max(0.05, Math.min(1 - nx, width));
  const nh = Math.max(0.05, Math.min(1 - ny, height));
  return nw > 0.05 && nh > 0.05 ? { x: nx, y: ny, w: nw, h: nh } : null;
}

function parseBodyRect(raw) {
  if (raw == null || raw === '') return null;
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (Array.isArray(value) && value.length >= 4) {
    return clampNormRect(value[0], value[1], value[2], value[3]);
  }
  if (value && typeof value === 'object') {
    return clampNormRect(value.x, value.y, value.width ?? value.w, value.height ?? value.h);
  }
  return null;
}

function polygonBBox(points) {
  if (!Array.isArray(points) || points.length < 3) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    const x = Number(Array.isArray(p) ? p[0] : p?.x);
    const y = Number(Array.isArray(p) ? p[1] : p?.y);
    if (!(x >= 0) || !(y >= 0)) continue;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return null;
  return clampNormRect(minX, minY, maxX - minX, maxY - minY);
}

function parsePrintableBBox(raw) {
  if (raw == null || raw === '') return null;
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (Array.isArray(value)) return polygonBBox(value);
  if (value && typeof value === 'object') {
    return polygonBBox(value.points || value.polygon);
  }
  return null;
}

/**
 * Shared strap catalog (synced from shared/bag-strap-zones.json via sync-bag-strap-zones.py).
 * Loaded by bag-strap-zones.boot.js before this file. Shopify custom.strap_polygons still wins.
 */
const TAY_STRAP_ZONE_CATALOG = window.TAY_BAG_STRAP_ZONES || {};
const TAY_PLAIN_FACE_HANDLES = new Set(
  Array.isArray(TAY_STRAP_ZONE_CATALOG.plain_face_handles)
    ? TAY_STRAP_ZONE_CATALOG.plain_face_handles.map((h) => String(h).toLowerCase())
    : []
);
const TAY_FULL_BODY_STRAP_OVERRIDES = TAY_STRAP_ZONE_CATALOG.full_body_overrides || {};
const TAY_HANDLES_ONLY_STRAP_OVERRIDES = TAY_STRAP_ZONE_CATALOG.handles_only_overrides || {};
// Printable-area fallbacks (hats, etc.) used when a product has no custom.body_rect.
const TAY_BODY_RECT_OVERRIDES = TAY_STRAP_ZONE_CATALOG.body_rect_overrides || {};
const TAY_BODY_RECT_PRESETS = TAY_STRAP_ZONE_CATALOG.body_rect_presets || {};

function bagHandleKey(bag) {
  return String(bag?.handle || '')
    .trim()
    .toLowerCase();
}

function placementPresetOf(bag) {
  const handle = bagHandleKey(bag);
  if (handle && TAY_PLAIN_FACE_HANDLES.has(handle)) {
    return 'plain_face';
  }
  const raw = String(bag?.placement_preset || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (raw === 'plain_face' || raw === 'plainface' || raw === 'plain' || raw === 'no_straps') {
    return 'plain_face';
  }
  if (raw === 'full_body_straps' || raw === 'fullbodystraps' || raw === 'full_straps' || raw === 'body_straps') {
    return 'full_body_straps';
  }
  if (raw === 'handles_only' || raw === 'handlesonly' || raw === 'handles') {
    return 'handles_only';
  }
  if (raw && Object.prototype.hasOwnProperty.call(TAY_BODY_RECT_PRESETS, raw)) {
    // Strapless item with a catalog printable area, e.g. hat_front_panels.
    return raw;
  }
  if (handle && Object.prototype.hasOwnProperty.call(TAY_FULL_BODY_STRAP_OVERRIDES, handle)) {
    return 'full_body_straps';
  }
  const tags = Array.isArray(bag?.tags) ? bag.tags.map((t) => String(t).toLowerCase()) : [];
  if (tags.some((t) => t.includes('geometry-plain-face') || t.includes('geometry-plainface') || t === 'no-straps' || t === 'strapless' || t === 'bag-no-straps')) {
    return 'plain_face';
  }
  if (tags.some((t) => t.includes('geometry-full-straps') || t.includes('geometry-full-body'))) {
    return 'full_body_straps';
  }
  if (tags.some((t) => t.includes('geometry-handles-only'))) {
    return 'handles_only';
  }
  return 'handles_only';
}

/**
 * @param {number} x0
 * @param {number} x1
 * @param {number} [y0]
 * @param {number} [y1]
 * @returns {Array<{ x: number, y: number }>}
 */
function strapColumnPolygon(x0, x1, y0 = 0, y1 = 0.96) {
  const left = clamp01(Math.min(x0, x1));
  const right = clamp01(Math.max(x0, x1));
  const top = clamp01(y0);
  const bottom = clamp01(y1);
  return [
    { x: left, y: top },
    { x: right, y: top },
    { x: right, y: bottom },
    { x: left, y: bottom },
  ];
}

/**
 * Default strap no-go polygons in 0…1 photo space.
 * @param {'handles_only' | 'full_body_straps' | 'plain_face' | string} preset
 * @returns {Array<Array<{ x: number, y: number }>>}
 */
function defaultStrapPolygonsForPreset(preset) {
  if (preset === 'plain_face') return [];
  if (Object.prototype.hasOwnProperty.call(TAY_BODY_RECT_PRESETS, preset)) return [];
  const defaults = TAY_STRAP_ZONE_CATALOG.defaults || {};
  if (preset === 'full_body_straps') {
    const d = defaults.full_body_straps;
    if (d && Array.isArray(d.left) && Array.isArray(d.right)) {
      const y0 = Array.isArray(d.y) ? Number(d.y[0]) : 0;
      const y1 = Array.isArray(d.y) ? Number(d.y[1]) : 0.96;
      return [
        strapColumnPolygon(d.left[0], d.left[1], y0, y1),
        strapColumnPolygon(d.right[0], d.right[1], y0, y1),
      ];
    }
    return [strapColumnPolygon(0.22, 0.34), strapColumnPolygon(0.66, 0.78)];
  }
  const d = defaults.handles_only;
  if (d && Array.isArray(d.left) && Array.isArray(d.right)) {
    const y0 = Array.isArray(d.y) ? Number(d.y[0]) : 0;
    const y1 = Array.isArray(d.y) ? Number(d.y[1]) : 0.26;
    return [
      strapColumnPolygon(d.left[0], d.left[1], y0, y1),
      strapColumnPolygon(d.right[0], d.right[1], y0, y1),
    ];
  }
  return [strapColumnPolygon(0.22, 0.34, 0, 0.26), strapColumnPolygon(0.66, 0.78, 0, 0.26)];
}

/**
 * @param {{ left: [number, number], right: [number, number], y?: [number, number] }} override
 * @param {number} [defaultY0]
 * @param {number} [defaultY1]
 * @returns {Array<Array<{ x: number, y: number }>>}
 */
function polygonsFromStrapOverride(override, defaultY0 = 0, defaultY1 = 0.96) {
  const y0 = Array.isArray(override.y) ? Number(override.y[0]) : defaultY0;
  const y1 = Array.isArray(override.y) ? Number(override.y[1]) : defaultY1;
  const top = Number.isFinite(y0) ? y0 : defaultY0;
  const bottom = Number.isFinite(y1) ? y1 : defaultY1;
  return [
    strapColumnPolygon(override.left[0], override.left[1], top, bottom),
    strapColumnPolygon(override.right[0], override.right[1], top, bottom),
  ];
}

/**
 * @param {{ handle?: string, strap_polygons?: unknown, placement_preset?: string, tags?: string[] } | null | undefined} bag
 * @returns {Array<Array<{ x: number, y: number }>>}
 */
function strapPolygonsForBagHandle(bag) {
  const handle = bagHandleKey(bag);
  if (!handle) return [];
  const override = TAY_FULL_BODY_STRAP_OVERRIDES[handle];
  if (!override) return [];
  return polygonsFromStrapOverride(override);
}

/**
 * @param {{ handle?: string, strap_polygons?: unknown, placement_preset?: string, tags?: string[] } | null | undefined} bag
 * @returns {Array<Array<{ x: number, y: number }>>}
 */
function strapPolygonsForHandlesOnlyHandle(bag) {
  const handle = bagHandleKey(bag);
  if (!handle) return [];
  const override = TAY_HANDLES_ONLY_STRAP_OVERRIDES[handle];
  if (!override) return [];
  return polygonsFromStrapOverride(override, 0, 0.26);
}

/**
 * @param {unknown} value
 * @returns {Array<Array<{ x: number, y: number }>>}
 */
function parseStrapPolygons(value) {
  let raw = value;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return [];
    try {
      raw = JSON.parse(s);
    } catch {
      return [];
    }
  }
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    raw = raw.straps || raw.polygons || raw.rings || null;
  }
  if (!Array.isArray(raw)) return [];

  const rings = [];
  for (const ring of raw) {
    if (!Array.isArray(ring) || ring.length < 3) continue;
    const pts = [];
    for (const pt of ring) {
      if (Array.isArray(pt) && pt.length >= 2) {
        const x = Number(pt[0]);
        const y = Number(pt[1]);
        if (Number.isFinite(x) && Number.isFinite(y)) pts.push({ x: clamp01(x), y: clamp01(y) });
        continue;
      }
      if (pt && typeof pt === 'object') {
        const x = Number(pt.x ?? pt.X);
        const y = Number(pt.y ?? pt.Y);
        if (Number.isFinite(x) && Number.isFinite(y)) pts.push({ x: clamp01(x), y: clamp01(y) });
      }
    }
    if (pts.length >= 3) rings.push(pts);
  }
  return rings;
}

/**
 * @param {{ handle?: string, strap_polygons?: unknown, placement_preset?: string, tags?: string[] } | null | undefined} bag
 * @returns {Array<Array<{ x: number, y: number }>>}
 */
function resolveStrapPolygons(bag) {
  const handle = bagHandleKey(bag);
  if (handle && TAY_PLAIN_FACE_HANDLES.has(handle)) return [];
  const custom = parseStrapPolygons(bag?.strap_polygons);
  if (custom.length) return custom;
  const preset = placementPresetOf(bag);
  if (preset === 'plain_face') return [];
  if (preset === 'full_body_straps') {
    const byHandle = strapPolygonsForBagHandle(bag);
    if (byHandle.length) return byHandle;
  }
  if (preset === 'handles_only') {
    const byHandle = strapPolygonsForHandlesOnlyHandle(bag);
    if (byHandle.length) return byHandle;
  }
  return defaultStrapPolygonsForPreset(preset);
}

/**
 * @param {{ x: number, y: number, w: number, h: number }} a
 * @param {{ x: number, y: number, w: number, h: number }} b
 */
function aabbsOverlap(a, b) {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/**
 * @param {number} nx
 * @param {number} ny
 * @param {number} hx
 * @param {number} hy
 * @param {Array<Array<{ x: number, y: number }>>} straps
 */
function patchHitsStrapZones(nx, ny, hx, hy, straps) {
  if (!straps || !straps.length) return false;
  const patch = { x: nx - hx, y: ny - hy, w: hx * 2, h: hy * 2 };
  for (const ring of straps) {
    const bb = polygonBBox(ring);
    if (bb && aabbsOverlap(patch, bb)) return true;
  }
  return false;
}

/**
 * One SVG for many strap no-go boxes (e.g. a curved hat panel built from thin strips):
 * hatched fill plus a single dashed line along the outer edge, instead of a dashed box per strip.
 * @param {Array<Array<{ x: number, y: number }>>} rings
 * @param {(x: number) => string} px
 * @param {(y: number) => string} py
 */
function strapZonesUnionSvg(rings, px, py) {
  const boxes = rings.map(polygonBBox).filter((bb) => bb && bb.w > 0 && bb.h > 0);
  const inAny = (x, y) => boxes.some((b) => x > b.x && x < b.x + b.w && y > b.y && y < b.y + b.h);
  const onBorder = (v) => v <= 0.001 || v >= 0.999;
  const eps = 0.0005;
  const fill = boxes
    .map((b) => `M${px(b.x)} ${py(b.y)} H${px(b.x + b.w)} V${py(b.y + b.h)} H${px(b.x)} Z`)
    .join(' ');
  const lines = [];
  for (const b of boxes) {
    const edges = [
      [b.x, b.y, b.x + b.w, b.y, 0, -eps],
      [b.x, b.y + b.h, b.x + b.w, b.y + b.h, 0, eps],
      [b.x, b.y, b.x, b.y + b.h, -eps, 0],
      [b.x + b.w, b.y, b.x + b.w, b.y + b.h, eps, 0],
    ];
    for (const [x1, y1, x2, y2, ox, oy] of edges) {
      if ((x1 === x2 && onBorder(x1)) || (y1 === y2 && onBorder(y1))) continue;
      const n = 24;
      let start = -1;
      for (let i = 0; i <= n; i++) {
        let open = false;
        if (i < n) {
          const t = (i + 0.5) / n;
          open = !inAny(x1 + (x2 - x1) * t + ox, y1 + (y2 - y1) * t + oy);
        }
        if (open && start < 0) start = i;
        if (!open && start >= 0) {
          const a = start / n;
          const z = i / n;
          lines.push(`M${px(x1 + (x2 - x1) * a)} ${py(y1 + (y2 - y1) * a)} L${px(x1 + (x2 - x1) * z)} ${py(y1 + (y2 - y1) * z)}`);
          start = -1;
        }
      }
    }
  }
  return `<svg class="tay-builder-canvas__strap-union" viewBox="0 0 100 100" preserveAspectRatio="none" style="position:absolute;inset:0;width:100%;height:100%;overflow:visible"><defs><pattern id="tay-strap-hatch" width="2" height="2" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)"><rect width="1" height="2" fill="rgba(180,52,52,0.16)"></rect><rect x="1" width="1" height="2" fill="rgba(180,52,52,0.05)"></rect></pattern></defs><path d="${fill}" fill="url(#tay-strap-hatch)"></path><path d="${lines.join(' ')}" fill="none" stroke="rgba(180,52,52,0.85)" stroke-width="1.5" stroke-dasharray="5 4" vector-effect="non-scaling-stroke"></path></svg>`;
}

/**
 * Push a patch center off strap no-go zones (and keep it in 0…1).
 * @param {number} nx
 * @param {number} ny
 * @param {{ hx: number, hy: number }} half
 * @param {Array<Array<{ x: number, y: number }>>} straps
 * @returns {{ nx: number, ny: number }}
 */
/**
 * Parse custom.printable_polygon into points (0…1 photo space). Accepts [[x,y],…], [{x,y},…] or { points }.
 * @param {unknown} raw
 * @returns {Array<{ x: number, y: number }> | null}
 */
function parsePrintablePolygon(raw) {
  if (raw == null || raw === '') return null;
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) value = value.points || value.polygon;
  if (!Array.isArray(value)) return null;
  const pts = [];
  for (const pt of value) {
    const x = Number(Array.isArray(pt) ? pt[0] : pt?.x);
    const y = Number(Array.isArray(pt) ? pt[1] : pt?.y);
    if (Number.isFinite(x) && Number.isFinite(y)) pts.push({ x: clamp01(x), y: clamp01(y) });
  }
  return pts.length >= 3 ? pts : null;
}

/**
 * @param {number} x
 * @param {number} y
 * @param {Array<{ x: number, y: number }>} poly
 */
function pointInPolygon(x, y, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i];
    const b = poly[j];
    if (a.y > y !== b.y > y && x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y || 1e-9) + a.x) inside = !inside;
  }
  return inside;
}

/**
 * True when the patch box (center ± half extents) sits fully inside the printable polygon.
 * @param {number} nx
 * @param {number} ny
 * @param {number} hx
 * @param {number} hy
 * @param {Array<{ x: number, y: number }>} poly
 */
function patchInsidePrintable(nx, ny, hx, hy, poly) {
  const steps = 4;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const px = nx - hx + t * 2 * hx;
    const py = ny - hy + t * 2 * hy;
    if (!pointInPolygon(px, ny - hy, poly) || !pointInPolygon(px, ny + hy, poly)) return false;
    if (!pointInPolygon(nx - hx, py, poly) || !pointInPolygon(nx + hx, py, poly)) return false;
  }
  return true;
}

/**
 * Move a patch center to the nearest spot inside the printable polygon that also avoids strap zones.
 * @param {number} nx
 * @param {number} ny
 * @param {{ hx: number, hy: number }} half
 * @param {Array<{ x: number, y: number }>} poly
 * @param {Array<Array<{ x: number, y: number }>>} straps
 * @returns {{ nx: number, ny: number }}
 */
function clampCenterInsidePrintable(nx, ny, half, poly, straps) {
  const hx = Math.max(0.01, Number(half?.hx) || 0.04);
  const hy = Math.max(0.01, Number(half?.hy) || 0.04);
  const ok = (x, y) => patchInsidePrintable(x, y, hx, hy, poly) && !patchHitsStrapZones(x, y, hx, hy, straps);
  const x0 = clamp01(nx);
  const y0 = clamp01(ny);
  if (ok(x0, y0)) return { nx: x0, ny: y0 };
  for (let ring = 1; ring < 120; ring++) {
    const step = ring * 0.008;
    for (let k = 0; k < 32; k++) {
      const ang = (k / 32) * Math.PI * 2;
      const cx = clamp01(x0 + Math.cos(ang) * step);
      const cy = clamp01(y0 + Math.sin(ang) * step);
      if (ok(cx, cy)) return { nx: cx, ny: cy };
    }
  }
  const bb = polygonBBox(poly);
  return bb ? { nx: bb.x + bb.w / 2, ny: bb.y + bb.h / 2 } : { nx: x0, ny: y0 };
}

function clampCenterOffStraps(nx, ny, half, straps) {
  let x = clamp01(nx);
  let y = clamp01(ny);
  const hx = Math.max(0.01, Number(half?.hx) || 0.04);
  const hy = Math.max(0.01, Number(half?.hy) || 0.04);
  if (!straps.length || !patchHitsStrapZones(x, y, hx, hy, straps)) {
    return { nx: x, ny: y };
  }

  for (let ring = 1; ring < 96; ring++) {
    const step = ring * 0.012;
    for (let k = 0; k < 28; k++) {
      const ang = (k / 28) * Math.PI * 2;
      const cx = clamp01(x + Math.cos(ang) * step);
      const cy = clamp01(y + Math.sin(ang) * step);
      if (!patchHitsStrapZones(cx, cy, hx, hy, straps)) {
        return { nx: cx, ny: cy };
      }
    }
  }

  /* Prefer the open center column for full-body strap bags. */
  if (!patchHitsStrapZones(0.5, y, hx, hy, straps)) return { nx: 0.5, ny: y };
  if (!patchHitsStrapZones(0.5, 0.55, hx, hy, straps)) return { nx: 0.5, ny: 0.55 };
  return { nx: x, ny: y };
}

/**
 * Where the physical canvas sits in the product photo (0–1).
 * Strap photos are taller than W/H, so leftover height stays above the body.
 * Strapless / cropped-handle photos keep the body filling the frame.
 */
function inferredBagBodyNormRect(bagWIn, bagHIn, photoAspect, preset) {
  const sideInset = 0.01;
  const bottomInset = 0.005;
  const topInset = 0.02;
  const bodyAspect = bagWIn / Math.max(bagHIn, 0.01);
  const photo = Math.max(Number(photoAspect) || bodyAspect, 0.05);
  const bodyWidth = 1 - 2 * sideInset;
  const bodyHeight = bodyWidth * (photo / bodyAspect);
  const usable = 1 - topInset - bottomInset;
  const strapless = preset === 'plain_face' || bodyHeight >= usable - 0.18;
  if (strapless) return { x: 0, y: 0, w: 1, h: 1 };
  const h = Math.min(Math.max(bodyHeight, 0.42), usable);
  const y = Math.max(topInset, 1 - bottomInset - h);
  return clampNormRect(sideInset, y, bodyWidth, h) || { x: 0, y: 0, w: 1, h: 1 };
}

function resolveBagBodyNormRect(bagProduct, bagWIn, bagHIn, photoAspect) {
  const explicit = parseBodyRect(bagProduct?.body_rect);
  if (explicit) return explicit;
  const printable = parsePrintableBBox(bagProduct?.printable_polygon);
  if (printable) return printable;
  const byHandle = parseBodyRect(TAY_BODY_RECT_OVERRIDES[bagHandleKey(bagProduct)]);
  if (byHandle) return byHandle;
  const byPreset = parseBodyRect(TAY_BODY_RECT_PRESETS[placementPresetOf(bagProduct)]);
  if (byPreset) return byPreset;
  return inferredBagBodyNormRect(bagWIn, bagHIn, photoAspect, placementPresetOf(bagProduct));
}

function bagBodyDisplayRect(photoRect, bagProduct, bagWIn, bagHIn) {
  const aspect = photoRect.width / Math.max(photoRect.height, 1);
  const n = resolveBagBodyNormRect(bagProduct, bagWIn, bagHIn, aspect);
  return {
    left: photoRect.left + n.x * photoRect.width,
    top: photoRect.top + n.y * photoRect.height,
    width: n.w * photoRect.width,
    height: n.h * photoRect.height,
  };
}

function patchBoxPxForBagBody(pwIn, phIn, photoRect, bagProduct, bagWIn, bagHIn, cfg) {
  const wIn = Number(pwIn) || Number(cfg?.defaultPatchWidthIn) || 2;
  const hIn = Number(phIn) || Number(cfg?.defaultPatchHeightIn) || 2;
  const body = bagBodyDisplayRect(photoRect, bagProduct, bagWIn, bagHIn);
  const ppi = Math.min(body.width / bagWIn, body.height / bagHIn);
  return { boxW: Math.max(8, wIn * ppi), boxH: Math.max(8, hIn * ppi) };
}

function normalizeBuilderConfig(cfg) {
  if (!cfg) return;
  for (const bag of cfg.bags || []) {
    const size = resolveBagInches(bag, cfg);
    bag.bag_width_in = size.w;
    bag.bag_height_in = size.h;
  }
  for (const patch of cfg.patches || []) {
    const size = resolvePatchInches(patch, cfg);
    patch.patch_width_in = size.w;
    patch.patch_height_in = size.h;
  }
}

/** Max chars per line-item property value (stay under Shopify limits). */
const TAY_MAX_PROPERTY_LENGTH = 450;
const TAY_MAX_PLACEMENT_LENGTH = 3800;

/** Normalized bag-space nudge per arrow tap (≈2% of bag width/height). */
const TAY_NUDGE_NORM = 0.02;

/** Matches tote-builder.css mobile layout breakpoint. */
const TAY_MOBILE_MEDIA = '(max-width: 989px)';
/** Bag preview width for full-screen zoom (3×) without upscaling blur. */
const TAY_FS_BAG_IMAGE_WIDTH = 2400;
/** Patch thumbs on full-screen canvas. */
const TAY_FS_PATCH_IMAGE_WIDTH = 960;
/** Exported checkout preview width (bag + patches composite). */
const TAY_PREVIEW_EXPORT_WIDTH = 1200;
/** Shopify line item / cart attribute value cap (matches iOS). */
const TAY_PREVIEW_URL_MAX = 255;
const TAY_DRAFT_STORAGE_KEY = 'tay-tote-builder-draft-v1';

/**
 * @param {Array<{ variantId: number; nx: number; ny: number; rot: number; side?: string }>} patches
 * @param {TayBagFace} side
 */
function placementsJSONForSide(patches, side) {
  const payload = patches
    .filter((p) => patchSideOf(p) === side)
    .map((p) => ({
      patch_id: String(p.variantId),
      x: typeof p.nx === 'number' ? p.nx : 0.5,
      y: typeof p.ny === 'number' ? p.ny : 0.5,
      scale: 1,
      rotation_deg: normalizeRotationDeg(p.rot),
      side,
    }));
  try {
    return JSON.stringify(payload);
  } catch {
    return '[]';
  }
}

/**
 * @param {Record<string, unknown>} item
 * @param {string} key
 */
function cartItemProp(item, key) {
  const props = item && typeof item.properties === 'object' && item.properties ? item.properties : {};
  const val = props[key] ?? props[`_${key}`];
  return typeof val === 'string' ? val.trim() : '';
}

/**
 * Request a larger width from Shopify CDN URLs (supports `?width=` and `_480x` path tokens).
 * @param {string | null | undefined} url
 * @param {number} width
 * @returns {string | null}
 */
function shopifyImageUrlWithWidth(url, width) {
  if (!url || !width) return url ?? null;
  const w = Math.max(1, Math.round(width));
  let out = String(url);
  out = out.replace(/_(\d+)x(\d+)?\./gi, `_${w}x.`);
  if (/[?&]width=\d+/i.test(out)) {
    out = out.replace(/([?&])width=\d+/gi, `$1width=${w}`);
  } else {
    out += (out.includes('?') ? '&' : '?') + `width=${w}`;
  }
  return out;
}

/** Source width used to trim patch photos for on-bag rendering. */
const TAY_ONBAG_PATCH_PROCESS_WIDTH = 800;
const TAY_PATCH_ALPHA_MIN = 18;
const TAY_PATCH_MAX_PROCESS_EDGE = 640;

/** @type {{ ready: Map<string, string>, inflight: Map<string, Promise<string>> }} */
const tayPatchArtworkCache = {
  ready: new Map(),
  inflight: new Map(),
};

/**
 * @param {string | null | undefined} url
 * @returns {string | null}
 */
function onBagPatchSourceUrl(url) {
  if (!url) return null;
  return shopifyImageUrlWithWidth(url, TAY_ONBAG_PATCH_PROCESS_WIDTH) || url;
}

/**
 * @param {HTMLCanvasElement} canvas
 * @returns {Promise<string>}
 */
function canvasToObjectUrl(canvas) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        resolve(canvas.toDataURL('image/png'));
        return;
      }
      resolve(URL.createObjectURL(blob));
    }, 'image/png');
  });
}

/**
 * @param {ImageData} imageData
 * @param {number} alphaMin
 * @returns {{ minX: number, minY: number, maxX: number, maxY: number } | null}
 */
function opaqueBounds(imageData, alphaMin) {
  const { data, width: w, height: h } = imageData;
  let minX = w;
  let minY = h;
  let maxX = 0;
  let maxY = 0;
  let found = false;
  for (let y = 0; y < h; y++) {
    const row = y * w * 4;
    for (let x = 0; x < w; x++) {
      if (data[row + x * 4 + 3] >= alphaMin) {
        found = true;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (!found || maxX < minX || maxY < minY) return null;
  return { minX, minY, maxX, maxY };
}

/**
 * @param {{ minX: number, minY: number, maxX: number, maxY: number }} bounds
 * @param {number} origArea
 */
function isUsefulPatchCrop(bounds, origArea) {
  const area = (bounds.maxX - bounds.minX + 1) * (bounds.maxY - bounds.minY + 1);
  const coverage = area / Math.max(origArea, 1);
  return coverage > 0.02 && coverage < 0.985;
}

/**
 * @param {ImageData} imageData
 * @param {{ minX: number, minY: number, maxX: number, maxY: number }} bounds
 * @returns {HTMLCanvasElement}
 */
function cropImageDataToCanvas(imageData, bounds) {
  const cw = bounds.maxX - bounds.minX + 1;
  const ch = bounds.maxY - bounds.minY + 1;
  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  const cropped = ctx.createImageData(cw, ch);
  const src = imageData.data;
  const sw = imageData.width;
  const dst = cropped.data;
  for (let y = 0; y < ch; y++) {
    const srcOff = ((bounds.minY + y) * sw + bounds.minX) * 4;
    dst.set(src.subarray(srcOff, srcOff + cw * 4), y * cw * 4);
  }
  ctx.putImageData(cropped, 0, 0);
  return canvas;
}

/**
 * Tight-crop transparent padding on cutout patch PNGs. Does not strip white
 * fabric / embroidery — on-bag assets are expected to already be cutouts.
 * @param {CanvasImageSource} image
 * @param {number} naturalWidth
 * @param {number} naturalHeight
 * @returns {HTMLCanvasElement | null}
 */
function trimPatchArtwork(image, naturalWidth, naturalHeight) {
  const nw = naturalWidth || 0;
  const nh = naturalHeight || 0;
  if (nw < 4 || nh < 4) return null;

  const scale = Math.min(1, TAY_PATCH_MAX_PROCESS_EDGE / Math.max(nw, nh));
  const w = Math.max(4, Math.round(nw * scale));
  const h = Math.max(4, Math.round(nh * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(image, 0, 0, w, h);

  let imageData;
  try {
    imageData = ctx.getImageData(0, 0, w, h);
  } catch {
    return null;
  }

  const bounds = opaqueBounds(imageData, TAY_PATCH_ALPHA_MIN);
  if (!bounds) return null;
  if (!isUsefulPatchCrop(bounds, w * h)) return null;
  return cropImageDataToCanvas(imageData, bounds);
}

/**
 * On-bag size is the physical inch rectangle. Do not shrink to the photo's
 * aspect — square letter PNGs would otherwise render at width-in tall instead
 * of height-in. Trimmed artwork is stretched into this box (object-fit: fill).
 * @param {number} boxW
 * @param {number} boxH
 * @returns {{ w: number, h: number }}
 */
function scaledPatchDisplaySize(boxW, boxH) {
  return { w: Math.max(1, boxW), h: Math.max(1, boxH) };
}

/**
 * Extra pixels a rotated w×h box extends past its unrotated top/bottom.
 * Used to park the action pill clear of the rotated artwork.
 * @param {number} w
 * @param {number} h
 * @param {number} [rotDeg]
 * @returns {number}
 */
function rotatedPatchAabbExtraY(w, h, rotDeg) {
  const rad = (normalizeRotationDeg(rotDeg) * Math.PI) / 180;
  const aabbH = Math.abs(w * Math.sin(rad)) + Math.abs(h * Math.cos(rad));
  return Math.max(0, (aabbH - h) / 2);
}

/**
 * Keep --tay-patch-rot / --tay-pill-clear in sync when size or angle changes.
 * @param {HTMLElement} el
 * @param {number} [rotDeg]
 * @param {{ w?: number, h?: number }} [size]
 */
function applyPatchRotationChrome(el, rotDeg, size) {
  const rot = normalizeRotationDeg(rotDeg);
  const w = size && typeof size.w === 'number' ? size.w : el.offsetWidth;
  const h = size && typeof size.h === 'number' ? size.h : el.offsetHeight;
  el.style.setProperty('--tay-patch-rot', `${rot}deg`);
  el.style.setProperty('--tay-pill-clear', `${rotatedPatchAabbExtraY(w, h, rot)}px`);
}

/**
 * Sit the selected patch’s action pill above the box, or below if the stage would clip it.
 * @param {HTMLElement} stage
 */
function updatePatchActionPills(stage) {
  if (!(stage instanceof HTMLElement)) return;
  const stageRect = stage.getBoundingClientRect();
  const pad = 6;
  stage.querySelectorAll('.tay-builder-canvas-patch').forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    const handles = node.querySelector('.tay-builder-canvas-patch__handles');
    if (!(handles instanceof HTMLElement)) return;
    if (!node.classList.contains('is-selected')) {
      node.classList.remove('is-pill-below');
      return;
    }
    node.classList.remove('is-pill-below');
    const aboveRect = handles.getBoundingClientRect();
    if (aboveRect.top >= stageRect.top + pad) return;
    node.classList.add('is-pill-below');
    const belowRect = handles.getBoundingClientRect();
    if (belowRect.bottom <= stageRect.bottom - pad) return;
    const aboveClip = stageRect.top + pad - aboveRect.top;
    const belowClip = belowRect.bottom - (stageRect.bottom - pad);
    if (aboveClip <= belowClip) node.classList.remove('is-pill-below');
  });
}

/**
 * @param {HTMLElement} el
 * @param {{ patch_width_in?: number, patch_height_in?: number, nx?: number, ny?: number, rot?: number }} p
 * @param {{ left: number, top: number, width: number, height: number }} bag
 * @param {number} bagWIn
 * @param {number} bagHIn
 * @param {number} sw
 * @param {number} sh
 * @param {{ defaultPatchWidthIn?: number, defaultPatchHeightIn?: number } | null | undefined} cfg
 * @param {{ body_rect?: unknown, printable_polygon?: unknown, placement_preset?: string, tags?: string[] } | null | undefined} bagProduct
 */
function layoutOnBagPatchElement(el, p, bag, bagWIn, bagHIn, sw, sh, cfg, bagProduct) {
  const pwIn = Number(p.patch_width_in) || Number(cfg?.defaultPatchWidthIn) || 2;
  const phIn = Number(p.patch_height_in) || Number(cfg?.defaultPatchHeightIn) || 2;
  const box = patchBoxPxForBagBody(pwIn, phIn, bag, bagProduct, bagWIn, bagHIn, cfg);
  const size = scaledPatchDisplaySize(box.boxW, box.boxH);

  const displayW = Math.max(16, size.w);
  const displayH = Math.max(16, size.h);
  el.style.width = `${displayW}px`;
  el.style.height = `${displayH}px`;

  const nx = clamp01(typeof p.nx === 'number' ? p.nx : 0.5);
  const ny = clamp01(typeof p.ny === 'number' ? p.ny : 0.5);
  const cx = bag.left + nx * bag.width;
  const cy = bag.top + ny * bag.height;

  el.style.left = `${(cx / sw) * 100}%`;
  el.style.top = `${(cy / sh) * 100}%`;
  applyPatchRotationChrome(el, p.rot, { w: displayW, h: displayH });
}

/**
 * @param {string} url
 * @returns {Promise<string>}
 */
function processPatchSource(url) {
  return loadImageForCanvas(url)
    .then((img) => {
      const trimmed = trimPatchArtwork(img, img.naturalWidth, img.naturalHeight);
      if (!trimmed) return url;
      return canvasToObjectUrl(trimmed);
    })
    .catch(() => url);
}

/**
 * Tight-cropped on-bag artwork for a patch photo. Falls back to the original URL.
 * @param {string | null | undefined} url
 * @returns {Promise<string | null>}
 */
function getOnBagPatchUrl(url) {
  const key = onBagPatchSourceUrl(url);
  if (!key) return Promise.resolve(null);
  const hit = tayPatchArtworkCache.ready.get(key);
  if (hit) return Promise.resolve(hit);
  const pending = tayPatchArtworkCache.inflight.get(key);
  if (pending) return pending;
  const work = processPatchSource(key).then((out) => {
    const resolved = out || key;
    tayPatchArtworkCache.ready.set(key, resolved);
    tayPatchArtworkCache.inflight.delete(key);
    return resolved;
  });
  tayPatchArtworkCache.inflight.set(key, work);
  return work;
}

/**
 * Visually disabled but still clickable so we can show a toast explaining why.
 * @param {boolean} canProceed
 * @returns {string}
 */
function blockedActionAttrs(canProceed) {
  return canProceed ? '' : ' aria-disabled="true"';
}

/**
 * @param {HTMLButtonElement} btn
 * @returns {boolean}
 */
function isBlockedAction(btn) {
  return btn.getAttribute('aria-disabled') === 'true' || btn.disabled;
}

function normalizeRotationDeg(deg) {
  const r = Math.round(Number(deg) || 0);
  return ((r % 360) + 360) % 360;
}

/**
 * Pixel rect of content with object-fit: contain inside a WxH box.
 */
function computeObjectFitContainRect(containerW, containerH, contentW, contentH) {
  if (!containerW || !containerH) {
    return { left: 0, top: 0, width: 0, height: 0 };
  }
  const cw = Math.max(1, Number(contentW) || containerW);
  const ch = Math.max(1, Number(contentH) || containerH);
  const scale = Math.min(containerW / cw, containerH / ch);
  const w = cw * scale;
  const h = ch * scale;
  const left = (containerW - w) / 2;
  const top = (containerH - h) / 2;
  return { left, top, width: w, height: h };
}

/**
 * Pointer → normalized coordinates on the bag image rect (may be outside 0–1).
 * Aligns mouse coords with `computeObjectFitContainRect(stage.clientWidth, …)` using the stage client box.
 * @returns {{ nx: number; ny: number; bag: { left: number; top: number; width: number; height: number } } | null}
 */
function pointerEventToBagNorm(stage, img, e) {
  if (!(stage instanceof HTMLElement) || !(img instanceof HTMLImageElement)) return null;
  const rect = stage.getBoundingClientRect();
  if (rect.width < 1 || rect.height < 1) return null;
  const mx = e.clientX - rect.left - stage.clientLeft;
  const my = e.clientY - rect.top - stage.clientTop;
  const bag = computeObjectFitContainRect(
    stage.clientWidth,
    stage.clientHeight,
    img.naturalWidth || stage.clientWidth,
    img.naturalHeight || stage.clientHeight
  );
  if (bag.width < 1e-9 || bag.height < 1e-9) return null;
  return {
    nx: (mx - bag.left) / bag.width,
    ny: (my - bag.top) / bag.height,
    bag,
  };
}

/**
 * @param {number} index
 */
function defaultPatchPlacement(index) {
  const t = index * 1.85;
  let nx = 0.5 + Math.cos(t) * 0.12;
  let ny = 0.48 + Math.sin(t) * 0.1;
  nx = Math.min(0.85, Math.max(0.15, nx));
  ny = Math.min(0.85, Math.max(0.15, ny));
  const rot = 0;
  return { nx, ny, rot };
}

/**
 * @param {number} min
 * @param {number} max
 */
function randomInRange(min, max) {
  const lo = Number(min);
  const hi = Number(max);
  if (!(hi > lo)) return lo;
  return lo + Math.random() * (hi - lo);
}

/**
 * Half-size of a patch in bag-photo normalized units (0–1 of the preview image).
 * @param {{ patch_width_in?: number, patch_height_in?: number, rot?: number }} p
 * @param {{ x: number, y: number, w: number, h: number }} body
 * @param {number} bagWIn
 * @param {number} bagHIn
 * @param {{ defaultPatchWidthIn?: number, defaultPatchHeightIn?: number } | null | undefined} cfg
 */
function patchHalfNorm(p, body, bagWIn, bagHIn, cfg) {
  const wIn = Number(p.patch_width_in) || Number(cfg?.defaultPatchWidthIn) || 2;
  const hIn = Number(p.patch_height_in) || Number(cfg?.defaultPatchHeightIn) || 2;
  const nxPerIn = body.w / Math.max(bagWIn, 0.01);
  const nyPerIn = body.h / Math.max(bagHIn, 0.01);
  const rad = (normalizeRotationDeg(p.rot) * Math.PI) / 180;
  const aabbW = Math.abs(wIn * Math.cos(rad)) + Math.abs(hIn * Math.sin(rad));
  const aabbH = Math.abs(wIn * Math.sin(rad)) + Math.abs(hIn * Math.cos(rad));
  return {
    hx: Math.max(0.015, aabbW * 0.5 * nxPerIn),
    hy: Math.max(0.015, aabbH * 0.5 * nyPerIn),
  };
}

/**
 * Center range that keeps a patch on the printable bag body.
 * @param {{ x: number, y: number, w: number, h: number }} body
 * @param {{ hx: number, hy: number }} half
 */
function patchShuffleBounds(body, half) {
  const padX = Math.min(0.04 * body.w, Math.max(0.01, half.hx * 0.2));
  const padY = Math.min(0.04 * body.h, Math.max(0.01, half.hy * 0.2));
  let minX = body.x + half.hx + padX;
  let maxX = body.x + body.w - half.hx - padX;
  let minY = body.y + half.hy + padY;
  let maxY = body.y + body.h - half.hy - padY;
  if (minX > maxX) {
    const mid = body.x + body.w / 2;
    minX = mid;
    maxX = mid;
  }
  if (minY > maxY) {
    const mid = body.y + body.h / 2;
    minY = mid;
    maxY = mid;
  }
  return { minX, maxX, minY, maxY };
}

/**
 * @param {{ nx: number, ny: number, hx: number, hy: number }} a
 * @param {{ nx: number, ny: number, hx: number, hy: number }} b
 */
function shuffledPatchesOverlap(a, b) {
  const gap = 0.012;
  return Math.abs(a.nx - b.nx) < a.hx + b.hx + gap && Math.abs(a.ny - b.ny) < a.hy + b.hy + gap;
}

/**
 * Random center on the bag body, preferring spots that do not stack on already-placed patches.
 * @param {{ hx: number, hy: number }} half
 * @param {{ x: number, y: number, w: number, h: number }} body
 * @param {Array<{ nx: number, ny: number, hx: number, hy: number }>} placed
 * @returns {{ nx: number, ny: number }}
 */
function pickShuffledPatchCenter(half, body, placed, straps) {
  const bounds = patchShuffleBounds(body, half);
  const zones = Array.isArray(straps) ? straps : [];
  let fallback = clampCenterOffStraps(
    randomInRange(bounds.minX, bounds.maxX),
    randomInRange(bounds.minY, bounds.maxY),
    half,
    zones
  );
  for (let attempt = 0; attempt < 72; attempt++) {
    const rawNx = randomInRange(bounds.minX, bounds.maxX);
    const rawNy = randomInRange(bounds.minY, bounds.maxY);
    const cleared = clampCenterOffStraps(rawNx, rawNy, half, zones);
    const candidate = { nx: cleared.nx, ny: cleared.ny, hx: half.hx, hy: half.hy };
    if (patchHitsStrapZones(candidate.nx, candidate.ny, half.hx, half.hy, zones)) continue;
    fallback = cleared;
    if (!placed.some((q) => shuffledPatchesOverlap(candidate, q))) return cleared;
  }
  return fallback;
}

/** @typedef {'front' | 'back' | 'left' | 'right'} TayBagFace */

/**
 * @param {unknown} side
 * @returns {side is TayBagFace}
 */
function isBagFace(side) {
  return side === 'front' || side === 'back' || side === 'left' || side === 'right';
}

/**
 * @param {{ side?: string } | null | undefined} p
 * @returns {TayBagFace}
 */
function patchSideOf(p) {
  const side = p && p.side;
  if (side === 'back' || side === 'left' || side === 'right') return side;
  return 'front';
}

/**
 * @param {TayBagFace | string | null | undefined} side
 * @returns {'f' | 'b' | 'l' | 'r'}
 */
function facePlacementCode(side) {
  if (side === 'back') return 'b';
  if (side === 'left') return 'l';
  if (side === 'right') return 'r';
  return 'f';
}

/**
 * @param {string | null | undefined} code
 * @returns {TayBagFace}
 */
function faceFromPlacementCode(code) {
  if (code === 'b') return 'back';
  if (code === 'l') return 'left';
  if (code === 'r') return 'right';
  return 'front';
}

function normalizePatchTag(raw) {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/**
 * @param {string | undefined | null} str
 * @returns {string[]}
 */
function splitCommaConfig(str) {
  if (!str || typeof str !== 'string') return [];
  return str
    .split(',')
    .map((s) => normalizePatchTag(s))
    .filter(Boolean);
}

/**
 * @param {any} cfg
 * @returns {string[]}
 */
function computePatchCategoryTags(cfg) {
  const whitelist = splitCommaConfig(cfg?.patchCategoryWhitelist || '');
  const exclude = new Set(splitCommaConfig(cfg?.patchTagsExcluded || ''));
  const seen = new Set();
  const out = [];
  for (const p of cfg?.patches || []) {
    const tags = Array.isArray(p.tags) ? p.tags : [];
    for (const raw of tags) {
      const t = normalizePatchTag(raw);
      if (!t || exclude.has(t)) continue;
      if (whitelist.length && !whitelist.includes(t)) continue;
      if (!seen.has(t)) {
        seen.add(t);
        out.push(t);
      }
    }
  }
  out.sort((a, b) => a.localeCompare(b));
  return out;
}

function titleCaseTag(tag) {
  return tag.replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * @param {Array<{ variantId: number; nx: number; ny: number; rot: number }>} patches
 */
function serializePatchPlacement(patches) {
  const s = patches
    .map((p) => {
      const nx = clamp01(typeof p.nx === 'number' ? p.nx : 0.5).toFixed(3);
      const ny = clamp01(typeof p.ny === 'number' ? p.ny : 0.5).toFixed(3);
      const rot = normalizeRotationDeg(p.rot);
      const face = facePlacementCode(patchSideOf(p));
      return `${p.variantId}:${nx}:${ny}:${rot}:${face}`;
    })
    .join('|');
  return s.length > TAY_MAX_PLACEMENT_LENGTH ? `${s.slice(0, TAY_MAX_PLACEMENT_LENGTH - 1)}…` : s;
}

/**
 * @param {string | null | undefined} str
 * @returns {Array<{ variantId: number; nx: number; ny: number; rot: number; side: TayBagFace }>}
 */
function parsePatchPlacement(str) {
  const raw = String(str || '')
    .replace(/…$/u, '')
    .trim();
  if (!raw) return [];
  const out = [];
  for (const token of raw.split('|')) {
    const bits = token.split(':');
    if (bits.length < 4) continue;
    const variantId = Number(bits[0]);
    const nx = Number(bits[1]);
    const ny = Number(bits[2]);
    const rot = Number(bits[3]);
    if (!Number.isFinite(variantId) || variantId <= 0) continue;
    out.push({
      variantId,
      nx: Number.isFinite(nx) ? clamp01(nx) : 0.5,
      ny: Number.isFinite(ny) ? clamp01(ny) : 0.5,
      rot: Number.isFinite(rot) ? normalizeRotationDeg(rot) : 0,
      side: faceFromPlacementCode(bits[4]),
    });
  }
  return out;
}

/**
 * @param {string} str
 * @param {TayBagFace} side
 * @returns {Array<{ variantId: number; nx: number; ny: number; rot: number; side: TayBagFace }>}
 */
function parsePlacementsJsonSide(str, side) {
  if (!str) return [];
  try {
    const arr = JSON.parse(str);
    if (!Array.isArray(arr)) return [];
    return arr
      .map((row) => {
        const variantId = Number(row && row.patch_id);
        const nx = Number(row && row.x);
        const ny = Number(row && row.y);
        const rot = Number(row && row.rotation_deg);
        if (!Number.isFinite(variantId) || variantId <= 0) return null;
        return {
          variantId,
          nx: Number.isFinite(nx) ? clamp01(nx) : 0.5,
          ny: Number.isFinite(ny) ? clamp01(ny) : 0.5,
          rot: Number.isFinite(rot) ? normalizeRotationDeg(rot) : 0,
          side,
        };
      })
      .filter(Boolean);
  } catch {
    return [];
  }
}

/**
 * @param {Record<string, unknown>} item
 */
function cartDesignId(item) {
  return cartItemProp(item, 'Design ID') || cartItemProp(item, 'Customization ID');
}

/**
 * @param {any} cfg
 * @param {number} variantId
 */
function findPatchCatalogByVariant(cfg, variantId) {
  const vid = Number(variantId);
  if (!cfg || Number.isNaN(vid)) return null;
  for (const product of cfg.patches || []) {
    const variant = (product.variants || []).find(
      (/** @type {any} */ v) => Number(v.id) === vid
    );
    if (variant) return { product, variant };
  }
  return null;
}

/**
 * @param {string} url
 * @param {string} editId
 */
function withEditQuery(url, editId) {
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('edit', editId);
    return `${u.pathname}${u.search}${u.hash}`;
  } catch {
    const sep = String(url).includes('?') ? '&' : '?';
    return `${url}${sep}edit=${encodeURIComponent(editId)}`;
  }
}

function cartEditIdFromLocation() {
  try {
    return String(new URLSearchParams(window.location.search).get('edit') || '').trim();
  } catch {
    return '';
  }
}

function stripCartEditParam() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('edit')) return;
    url.searchParams.delete('edit');
    const qs = url.searchParams.toString();
    window.history.replaceState({}, '', `${url.pathname}${qs ? `?${qs}` : ''}${url.hash}`);
  } catch {
    /* ignore */
  }
}

async function fetchCartJson() {
  const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('Could not load cart');
  return res.json();
}

/**
 * @param {string} designId
 * @returns {Promise<boolean>}
 */
async function removeCartItemsByDesignId(designId) {
  const id = String(designId || '').trim();
  if (!id) return false;
  const cart = await fetchCartJson();
  /** @type {Record<string, number>} */
  const updates = {};
  for (const line of cart.items || []) {
    if (cartDesignId(line) === id && line.key) {
      updates[String(line.key)] = 0;
    }
  }
  if (!Object.keys(updates).length) return false;
  const res = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ updates }),
  });
  if (!res.ok) throw new Error('Could not update cart');
  return true;
}

/**
 * Loose “Buy patches” line (not sewn onto a bag design).
 * @param {Record<string, unknown>} item
 */
function isLoosePatchesOnlyLine(item) {
  return cartItemProp(item, 'Order type') === 'Patches only';
}

/**
 * @param {string[]} names
 */
function formatNaturalNameList(names) {
  const list = (names || []).map((n) => String(n || '').trim()).filter(Boolean);
  if (!list.length) return '';
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`;
}

/**
 * Bag patches that also exist as loose “Patches only” cart lines.
 * @param {any} cart
 * @param {Array<{ variantId: number; productTitle?: string }>} patches
 * @returns {Array<{ variantId: number; title: string; removeQty: number }>}
 */
function findLoosePatchOverlaps(cart, patches) {
  /** @type {Map<number, { title: string; needed: number }>} */
  const neededByVariant = new Map();
  for (const patch of patches || []) {
    const vid = Number(patch.variantId);
    if (!vid) continue;
    const prev = neededByVariant.get(vid);
    const title = String(patch.productTitle || '').trim() || 'This patch';
    if (prev) {
      prev.needed += 1;
      if (!prev.title || prev.title === 'This patch') prev.title = title;
    } else {
      neededByVariant.set(vid, { title, needed: 1 });
    }
  }
  if (!neededByVariant.size) return [];

  /** @type {Map<number, number>} */
  const looseQtyByVariant = new Map();
  for (const line of cart?.items || []) {
    if (!isLoosePatchesOnlyLine(line)) continue;
    const vid = Number(line.variant_id);
    if (!vid || !neededByVariant.has(vid)) continue;
    looseQtyByVariant.set(vid, (looseQtyByVariant.get(vid) || 0) + (Number(line.quantity) || 0));
  }

  /** @type {Array<{ variantId: number; title: string; removeQty: number }>} */
  const overlaps = [];
  for (const [vid, meta] of neededByVariant) {
    const looseQty = looseQtyByVariant.get(vid) || 0;
    if (looseQty <= 0) continue;
    overlaps.push({
      variantId: vid,
      title: meta.title,
      removeQty: Math.min(looseQty, meta.needed),
    });
  }
  return overlaps;
}

/**
 * Lower quantity on matching loose “Patches only” lines (or remove when qty hits 0).
 * @param {Array<{ variantId: number; removeQty: number }>} overlaps
 */
async function reduceLoosePatchQuantities(overlaps) {
  if (!overlaps?.length) return false;
  const cart = await fetchCartJson();
  /** @type {Record<string, number>} */
  const updates = {};
  for (const overlap of overlaps) {
    let remaining = Number(overlap.removeQty) || 0;
    if (remaining <= 0) continue;
    for (const line of cart.items || []) {
      if (remaining <= 0) break;
      if (Number(line.variant_id) !== Number(overlap.variantId)) continue;
      if (!isLoosePatchesOnlyLine(line) || !line.key) continue;
      const qty = Number(line.quantity) || 0;
      if (qty <= 0) continue;
      const take = Math.min(qty, remaining);
      updates[String(line.key)] = qty - take;
      remaining -= take;
    }
  }
  if (!Object.keys(updates).length) return false;
  const res = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ updates }),
  });
  if (!res.ok) throw new Error('Could not update cart');
  return true;
}

/**
 * @param {TayBagFace} side
 * @param {Record<string, string> | undefined} labels
 */
function bagFaceLabel(side, labels) {
  if (side === 'back') return labels?.bagFaceBack || labels?.patchSideBackAbbr || 'Back';
  if (side === 'left') return 'Left';
  if (side === 'right') return 'Right';
  return labels?.bagFaceFront || labels?.patchSideFrontAbbr || 'Front';
}

/**
 * @param {Array<{ productTitle: string; variantTitle: string; sku: string; side?: string }>} patches
 * @param {Record<string, string> | undefined} labels
 * @returns {string}
 */
function buildPatchListSummary(patches, labels) {
  if (!patches.length) return '';
  const parts = patches.map((p, index) => {
    const vt =
      p.variantTitle && p.variantTitle !== 'Default Title' ? ` (${p.variantTitle})` : '';
    const sku = p.sku ? ` [${p.sku}]` : '';
    const faceLbl = bagFaceLabel(patchSideOf(p), labels);
    return `${index + 1}. ${p.productTitle}${vt}${sku} (${faceLbl})`;
  });
  let out = parts.join(' · ');
  if (out.length > TAY_MAX_PROPERTY_LENGTH) {
    out = `${out.slice(0, TAY_MAX_PROPERTY_LENGTH - 1)}…`;
  }
  return out;
}

class TayToteBuilder extends HTMLElement {
  constructor() {
    super();
    /** @type {any} */
    this._config = null;
    /** @type {number | null} */
    this._bagProductId = null;
    /** @type {number | null} */
    this._bagVariantId = null;
    /** @type {number | null} */
    this._prevBagProductIdForClear = null;
    /** Wizard step 1–3 */
    this._step = 1;
    /** @type {number | null} */
    this._selectedPatchIndex = null;
    /**
     * @type {Array<{
     *   variantId: number;
     *   productTitle: string;
     *   variantTitle: string;
     *   price: number;
     *   image: string | null;
     *   sku: string;
     *   nx: number;
     *   ny: number;
     *   rot: number;
     *   patch_width_in?: number;
     *   patch_height_in?: number;
     *   side?: 'front' | 'back';
     * }>}
     */
    this._patches = [];
    /** Which bag face is shown while placing patches (requires preview_image_back). */
    this._bagSide = 'front';
    /** @type {{ move: (e: PointerEvent) => void; up: () => void; cancel: () => void } | null} */
    this._dragListeners = null;
    /** @type {number | null} */
    this._dragIndex = null;
    /** Normalized grab offset: patchCenter - pointer while dragging (stable drag UX). */
    this._dragGrabNx = 0;
    this._dragGrabNy = 0;
    /** @type {HTMLElement | null} */
    this._dragCaptureEl = null;
    /** @type {number} */
    this._dragPointerId = -1;
    /** @type {ResizeObserver | null} */
    this._resizeObserver = null;
    /** @type {number | null} */
    this._geoRaf = null;
    /** @type {(() => void) | null} */
    this._imgLoadHandler = null;
    /** @type {boolean} */
    this._canvasReadOnly = false;
    /** Patch indices included in tidy / align / distribute (subset of placed patches). */
    /** @type {Set<number>} */
    this._tidyIndices = new Set();
    /** @type {string} */
    this._patchLibraryCategory = 'all';
    /** @type {string} */
    this._patchLibraryQuery = '';
    /** Preserved across full re-renders so selecting a patch does not jump the library. */
    this._patchLibDesktopScrollTop = 0;
    this._patchLibSheetScrollTop = 0;
    /** @type {number[]} */
    this._patchLibCatScrollLeft = [];
    /** Monotonic stacking for canvas paint order. */
    this._patchStackSeq = Math.floor(Date.now() % 1e9);
    /** Mobile patch catalog sheet open (step 2). */
    this._patchSheetOpen = false;
    /** Price breakdown expanded under canvas. */
    this._summaryExpanded = false;
    /** @type {((e: KeyboardEvent) => void) | null} */
    this._sheetKeyHandler = null;
    /** @type {number | null} */
    this._toastTimer = null;
    /** Body scroll lock while patch sheet is open (iOS). */
    this._bodyScrollLockY = 0;
    /** @type {boolean} */
    this._patchSheetSwipeBound = false;
    /** User closed patch sheet on step 2 (do not auto-reopen until re-entering step). */
    this._patchSheetUserDismissed = false;
    /** Mobile price breakdown sheet. */
    this._breakdownSheetOpen = false;
    /** Full-screen tote preview overlay. */
    this._fullscreenOpen = false;
    this._fsZoom = 1;
    this._fsPanX = 0;
    this._fsPanY = 0;
    /** Desktop Adjust patch <details> open. */
    this._editorDetailsOpen = false;
    /** Desktop Advanced layout / tidy <details> open. */
    this._tidyDetailsOpen = false;
    /** Mobile tidy drawer open (step 2). */
    this._tidySheetOpen = false;
    /** Object URLs for review-step front/back composites (revoked on leave step 3). */
    /** @type {{ front: string | null; back: string | null }} */
    this._reviewPreviewObjectUrls = { front: null, back: null, left: null, right: null };
    /** Bumps when review previews should be discarded (step change / re-render). */
    this._reviewPreviewGen = 0;
    this._designTitle = '';
    this._showAddedPanel = false;
    this._patchLibraryLoading = false;
    /** @type {{ title: string; front: string | null; back: string | null; customizationId: string } | null} */
    this._lastAdded = null;
    /** Cart Design ID to replace on the next add-to-cart. */
    this._replaceDesignId = '';
    /** @type {Array<{ patches: any[]; selected: number | null; tidy: number[]; side: string }>} */
    this._undoStack = [];
    /** @type {((e: KeyboardEvent) => void) | null} */
    this._undoKeyHandler = null;
    /** @type {((e: Event) => void) | null} */
    this._cartUpdatedHandler = null;
    /** @type {number | null} */
    this._persistTimer = null;
  }

  /**
   * @returns {string}
   */
  orderSubmitLabel() {
    const labels = this._config?.labels || {};
    if (this._replaceDesignId) return labels.updateOrder || 'Save to order';
    return labels.addToCart || 'Add to cart';
  }

  /**
   * @returns {boolean}
   */
  isMobileLayout() {
    return typeof window !== 'undefined' && window.matchMedia(TAY_MOBILE_MEDIA).matches;
  }


  /**
   * @param {string} message
   * @param {'info' | 'error' | 'success'} [type]
   */
  showToast(message, type = 'info') {
    if (!message) return;
    const host = this.querySelector('[data-ref="toast"]');
    if (!(host instanceof HTMLElement)) return;
    host.hidden = false;
    host.textContent = message;
    host.classList.remove('is-error', 'is-success');
    if (type === 'error') host.classList.add('is-error');
    if (type === 'success') host.classList.add('is-success');
    if (this._toastTimer != null) clearTimeout(this._toastTimer);
    this._toastTimer = window.setTimeout(() => {
      this._toastTimer = null;
      host.hidden = true;
      host.textContent = '';
      host.classList.remove('is-error', 'is-success');
    }, type === 'error' ? 4200 : 2800);
  }

  /**
   * @param {string} message
   */
  showError(message) {
    if (!message) return;
    const err = this.querySelector('[data-ref="error"]');
    if (err instanceof HTMLElement) {
      err.hidden = false;
      err.textContent = message;
    }
    this.showToast(message, 'error');
  }

  /**
   * Calm sheet when bag patches already exist as loose “Buy patches” lines.
   * @param {Array<{ variantId: number; title: string; removeQty: number }>} overlaps
   * @returns {Promise<'sew' | 'keep' | 'cancel'>}
   */
  promptLoosePatchReuse(overlaps) {
    const labels = this._config?.labels || {};
    const names = overlaps.map((o) => o.title);
    const nameList = formatNaturalNameList(names);
    const many = overlaps.length > 1 || overlaps.some((o) => o.removeQty > 1);
    const title = labels.looseReuseTitle || 'Already in your cart';
    const bodyTemplate = many
      ? labels.looseReuseBodyMany ||
        'You already have {names} in your cart. We’ll sew them onto this bag — you don’t need to buy them again.'
      : labels.looseReuseBodyOne ||
        'You already have {names} in your cart. We’ll sew it onto this bag — you don’t need to buy it again.';
    const body = String(bodyTemplate).replace(/\{names\}/g, nameList || 'this patch');
    const sewLabel = many
      ? labels.looseReuseSewMany || 'Sew them on this bag'
      : labels.looseReuseSewOne || 'Sew it on this bag';
    const keepLabel = labels.looseReuseKeep || 'Keep a spare unattached';
    const cancelLabel = labels.looseReuseCancel || labels.closeSheet || 'Cancel';

    return new Promise((resolve) => {
      const existing = document.querySelector('.tay-builder-reuse-sheet');
      if (existing) existing.remove();

      const root = document.createElement('div');
      root.className = 'tay-builder-reuse-sheet is-open';
      root.setAttribute('role', 'presentation');
      root.innerHTML = `
        <button type="button" class="tay-builder-reuse-sheet__backdrop" data-action="reuse-cancel" aria-label="${escapeHtml(cancelLabel)}"></button>
        <div class="tay-builder-reuse-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="tay-reuse-sheet-title">
          <div class="tay-builder-reuse-sheet__grab" aria-hidden="true"></div>
          <header class="tay-builder-reuse-sheet__head">
            <h2 class="tay-builder-reuse-sheet__title" id="tay-reuse-sheet-title">${escapeHtml(title)}</h2>
            <button type="button" class="tay-builder-reuse-sheet__close" data-action="reuse-cancel" aria-label="${escapeHtml(cancelLabel)}">×</button>
          </header>
          <div class="tay-builder-reuse-sheet__body">
            <p class="tay-builder-reuse-sheet__lede">${escapeHtml(body)}</p>
            <button type="button" class="tay-builder-reuse-sheet__primary" data-action="reuse-sew">${escapeHtml(sewLabel)}</button>
            <button type="button" class="tay-builder-reuse-sheet__secondary" data-action="reuse-keep">${escapeHtml(keepLabel)}</button>
            <button type="button" class="tay-builder-reuse-sheet__ghost" data-action="reuse-cancel">${escapeHtml(cancelLabel)}</button>
          </div>
        </div>
      `;

      /** @param {'sew' | 'keep' | 'cancel'} choice */
      const finish = (choice) => {
        document.removeEventListener('keydown', onKey);
        root.remove();
        resolve(choice);
      };

      /** @param {KeyboardEvent} e */
      const onKey = (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          finish('cancel');
        }
      };

      root.addEventListener('click', (e) => {
        const target = e.target;
        if (!(target instanceof Element)) return;
        const action = target.closest('[data-action]')?.getAttribute('data-action');
        if (action === 'reuse-sew') finish('sew');
        else if (action === 'reuse-keep') finish('keep');
        else if (action === 'reuse-cancel') finish('cancel');
      });

      document.addEventListener('keydown', onKey);
      document.body.appendChild(root);
      requestAnimationFrame(() => {
        const primary = root.querySelector('[data-action="reuse-sew"]');
        if (primary instanceof HTMLButtonElement) primary.focus();
      });
    });
  }

  /**
   * @param {Record<string, string>} labels
   * @param {number} minP
   * @param {number} count
   */
  formatMinPatchesMessage(labels, minP, count) {
    const base = labels.minPatchesError || `Add at least ${minP} patches to continue.`;
    const trimmed = base.replace(/\s*\(.*\)\s*$/, '').replace(/\.$/, '');
    return `${trimmed} (${count}/${minP}).`;
  }

  /**
   * @returns {string | null}
   */
  getStep1BlockerMessage() {
    const labels = this._config?.labels || {};
    const bagVariant = this.getBagVariant();
    if (!bagVariant?.available) {
      return labels.selectBagError || 'Choose an available bag color to continue.';
    }
    return null;
  }

  /**
   * @returns {string | null}
   */
  getStep2BlockerMessage() {
    const cfg = this._config;
    if (!cfg) return null;
    const labels = cfg.labels || {};
    const bagVariant = this.getBagVariant();
    const minP = this.getMinPatches();
    const count = this._patches.length;
    if (!bagVariant?.available) {
      return labels.selectBagError || 'Choose an available bag color to continue.';
    }
    if (count < minP) {
      return this.formatMinPatchesMessage(labels, minP, count);
    }
    if (count > cfg.maxPatches) {
      return labels.maxPatchesError || `You can add at most ${cfg.maxPatches} patches.`;
    }
    return null;
  }

  /**
   * @returns {string | null}
   */
  getSubmitBlockerMessage() {
    return this.getStep2BlockerMessage();
  }

  /**
   * @param {'align' | 'distribute' | 'stagger' | 'delete'} kind
   * @returns {string | null}
   */
  getTidyBlockerMessage(kind) {
    const labels = this._config?.labels || {};
    const targets = this.getTidyTargetsForActiveSide();
    const anyTidy = this.getTidyTargets();
    if (kind === 'delete' && anyTidy.length === 0) {
      return labels.tidySelectHint || 'Select patches on your bag first (checkmark on each patch).';
    }
    if (targets.length === 0) {
      return (
        labels.tidySelectHint ||
        'Select patches on this side of the bag first (checkmark on each patch).'
      );
    }
    if ((kind === 'distribute' || kind === 'stagger') && targets.length < 2) {
      return labels.tidyNeedTwo || 'Select at least two patches to use spacing tools.';
    }
    return null;
  }

  /**
   * @returns {string | null}
   */
  getShuffleBlockerMessage() {
    const labels = this._config?.labels || {};
    if (this.getActiveSidePatchIndices().length < 2) {
      return labels.shuffleNeedTwo || 'Add at least two patches on this side to shuffle.';
    }
    return null;
  }

  /**
   * @param {string | null} message
   * @returns {boolean} true if blocked
   */
  notifyIfBlocked(message) {
    if (!message) return false;
    this.showError(message);
    return true;
  }

  clearError() {
    const err = this.querySelector('[data-ref="error"]');
    if (err instanceof HTMLElement) {
      err.hidden = true;
      err.textContent = '';
    }
  }

  lockBodyForSheet() {
    if (typeof window === 'undefined' || !this.isMobileLayout()) return;
    const body = document.body;
    if (body.classList.contains('tay-builder-sheet-open')) return;
    this._bodyScrollLockY = window.scrollY;
    body.style.position = 'fixed';
    body.style.top = `-${this._bodyScrollLockY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.classList.add('tay-builder-sheet-open');
  }

  unlockBodyForSheet() {
    if (typeof window === 'undefined') return;
    const body = document.body;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    body.classList.remove('tay-builder-sheet-open');
    if (this._bodyScrollLockY > 0) {
      window.scrollTo(0, this._bodyScrollLockY);
    }
    this._bodyScrollLockY = 0;
  }

  scrollPreviewIntoView() {
    if (!this.isMobileLayout()) return;
    const panel = this.querySelector('.tay-builder-panel--preview');
    if (panel instanceof HTMLElement) {
      panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /** Capture patch-library scroll positions before a full DOM rebuild. */
  capturePatchLibraryScroll() {
    const desktopGrid = this.querySelector('.tay-builder-desktop-library .tay-builder-patch-grid');
    if (desktopGrid instanceof HTMLElement) {
      this._patchLibDesktopScrollTop = desktopGrid.scrollTop;
    }
    const sheetBody = this.querySelector('.tay-builder-patch-sheet__body');
    if (sheetBody instanceof HTMLElement) {
      this._patchLibSheetScrollTop = sheetBody.scrollTop;
    }
    this._patchLibCatScrollLeft = Array.from(this.querySelectorAll('.tay-builder-cat-row')).map((el) =>
      el instanceof HTMLElement ? el.scrollLeft : 0
    );
  }

  /** Restore patch-library scroll positions after a full DOM rebuild. */
  restorePatchLibraryScroll() {
    const desktopGrid = this.querySelector('.tay-builder-desktop-library .tay-builder-patch-grid');
    if (desktopGrid instanceof HTMLElement) {
      desktopGrid.scrollTop = this._patchLibDesktopScrollTop;
    }
    const sheetBody = this.querySelector('.tay-builder-patch-sheet__body');
    if (sheetBody instanceof HTMLElement) {
      sheetBody.scrollTop = this._patchLibSheetScrollTop;
    }
    const catLefts = this._patchLibCatScrollLeft;
    this.querySelectorAll('.tay-builder-cat-row').forEach((el, i) => {
      if (!(el instanceof HTMLElement)) return;
      const left = catLefts[i];
      if (typeof left === 'number') el.scrollLeft = left;
    });
  }

  focusPatchSheetSearch() {
    if (!this._patchSheetOpen) return;
    const sheet = this.querySelector('[data-ref="patch-sheet"]');
    const input = sheet?.querySelector('[data-ref="patch-search"]');
    if (input instanceof HTMLInputElement) {
      requestAnimationFrame(() => input.focus({ preventScroll: true }));
    }
  }

  disconnectedCallback() {
    this.persistDraftNow();
    this.closePatchSheet();
    this.closeTidySheet();
    if (this._cartUpdatedHandler) {
      document.removeEventListener('tay:cart-updated', this._cartUpdatedHandler);
      this._cartUpdatedHandler = null;
    }
    if (this._undoKeyHandler) {
      document.removeEventListener('keydown', this._undoKeyHandler);
      this._undoKeyHandler = null;
    }
    if (this._toastTimer != null) {
      clearTimeout(this._toastTimer);
      this._toastTimer = null;
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    if (this._geoRaf != null) {
      cancelAnimationFrame(this._geoRaf);
      this._geoRaf = null;
    }
    const stage = this.querySelector('[data-ref="canvas-stage"]');
    const img = stage?.querySelector('.tay-builder-canvas__bag');
    if (img instanceof HTMLImageElement && this._imgLoadHandler) {
      img.removeEventListener('load', this._imgLoadHandler);
      this._imgLoadHandler = null;
    }
  }

  connectedCallback() {
    const sid = this.dataset.sectionId;
    if (!sid) return;

    const scriptEl = document.getElementById(`tay-builder-config-${sid}`);
    if (!scriptEl?.textContent) return;

    try {
      this._config = JSON.parse(scriptEl.textContent);
      normalizeBuilderConfig(this._config);
    } catch (e) {
      console.error('[tay-tote-builder] Invalid JSON', e);
      this.innerHTML = `<p class="tay-builder-error" role="alert">Configuration error.</p>`;
      return;
    }

    const cfg = this._config;

    if (cfg.initialBagVariantId != null) {
      const wantId = Number(cfg.initialBagVariantId);
      if (!Number.isNaN(wantId)) {
        for (const p of cfg.bags || []) {
          const match = p.variants.find((/** @type {any} */ v) => v.id === wantId);
          if (match) {
            this._bagProductId = p.id;
            this._bagVariantId = match.id;
            break;
          }
        }
      }
    }

    if (cfg.bags?.length > 0 && this._bagProductId == null) {
      const first = cfg.bags.find((/** @type {any} */ p) => p.variants.some((/** @type {any} */ v) => v.available));
      if (first) {
        this._bagProductId = first.id;
        const fv = firstAvailableVariant(first.variants);
        this._bagVariantId = fv?.id ?? null;
      }
    }

    this._prevBagProductIdForClear = this._bagProductId;

    const editId = cartEditIdFromLocation();
    if (editId) {
      this.render();
      void this.hydrateFromCartEdit(editId);
    } else {
      void this.restoreDraft().then(() => this.render());
    }
    this.loadExtraPatchLibrary();
    this.bindUndoShortcut();
    this.bindCartUpdatedListener();
  }

  bindCartUpdatedListener() {
    if (this._cartUpdatedHandler) return;
    this._cartUpdatedHandler = (e) => {
      void this.handleCartUpdated(e);
    };
    document.addEventListener('tay:cart-updated', this._cartUpdatedHandler);
  }

  /**
   * If the carted design this editor is bound to was removed, start a blank bag.
   * @param {Event} e
   */
  async handleCartUpdated(e) {
    const replaceId = String(this._replaceDesignId || '').trim();
    if (!replaceId) return;
    const detail = e instanceof CustomEvent ? e.detail || {} : {};
    if (Number(detail.itemCount) === 0) {
      this.resetBuilderForAnotherBag();
      this.render();
      return;
    }
    try {
      const cart = await fetchCartJson();
      const stillInCart = (cart.items || []).some((line) => cartDesignId(line) === replaceId);
      if (!stillInCart) {
        this.resetBuilderForAnotherBag();
        this.render();
      }
    } catch {
      /* cart lookup failed — leave the in-progress canvas */
    }
  }

  bindUndoShortcut() {
    if (this._undoKeyHandler) return;
    this._undoKeyHandler = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 'z' || e.shiftKey) return;
      const t = e.target;
      if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement) return;
      e.preventDefault();
      this.undoLastChange();
    };
    document.addEventListener('keydown', this._undoKeyHandler);
  }

  /**
   * Liquid only inlines the first 50 collection products. Fetch remaining
   * pages from the metafield JSON view so letters and later patches appear.
   */
  async loadExtraPatchLibrary() {
    const cfg = this._config;
    if (!cfg) return;
    const handle = String(cfg.patchesCollectionHandle || cfg.collectionHandle || '').trim();
    const known = Number(cfg.allProductsCount || 0);
    const current = Array.isArray(cfg.patches) ? cfg.patches.length : 0;
    if (!handle) return;
    if (Number.isFinite(known) && known > 0 && known <= current) return;
    if (this._patchLibraryLoading) return;
    this._patchLibraryLoading = true;

    const byId = new Map();
    (cfg.patches || []).forEach((p) => {
      if (p && p.id != null) byId.set(Number(p.id), p);
    });

    try {
      let page = current >= 50 ? 2 : 1;
      let pages = 20;
      while (page <= pages && page <= 20) {
        const res = await fetch(
          `/collections/${encodeURIComponent(handle)}?view=tay-patches&page=${page}`,
          { credentials: 'same-origin' }
        );
        if (!res.ok) break;
        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch {
          break;
        }
        const products = Array.isArray(data.products) ? data.products : [];
        const reportedPages = Number(data.pages);
        if (Number.isFinite(reportedPages) && reportedPages > 0) pages = reportedPages;
        for (const product of products) {
          if (product && product.id != null) byId.set(Number(product.id), product);
        }
        if (products.length === 0 || page >= pages) break;
        page += 1;
      }
      cfg.patches = Array.from(byId.values());
      normalizeBuilderConfig(cfg);
      this.refreshPlacedPatchInchesFromCatalog();
      this.upgradePatchesFromCatalog();
      if (!this._showAddedPanel) this.render();
    } catch (e) {
      console.warn('[tay-tote-builder] Could not load extra patches', e);
    } finally {
      this._patchLibraryLoading = false;
    }
  }

  draftStorageKey() {
    const sid = this.dataset.sectionId || 'default';
    return `${TAY_DRAFT_STORAGE_KEY}:${sid}`;
  }

  persistDraft() {
    if (this._showAddedPanel) return;
    if (this._persistTimer != null) clearTimeout(this._persistTimer);
    this._persistTimer = window.setTimeout(() => {
      this._persistTimer = null;
      this.persistDraftNow();
    }, 200);
  }

  persistDraftNow() {
    if (typeof sessionStorage === 'undefined') return;
    if (this._showAddedPanel) {
      this.clearDraftStorage();
      return;
    }
    try {
      const payload = {
        step: this._step,
        bagProductId: this._bagProductId,
        bagVariantId: this._bagVariantId,
        bagSide: this._bagSide,
        designTitle: this._designTitle,
        patches: this._patches,
        replaceDesignId: this._replaceDesignId,
      };
      sessionStorage.setItem(this.draftStorageKey(), JSON.stringify(payload));
    } catch {
      /* quota / private mode */
    }
  }

  clearDraftStorage() {
    if (typeof sessionStorage === 'undefined') return;
    try {
      sessionStorage.removeItem(this.draftStorageKey());
    } catch {
      /* ignore */
    }
  }

  /**
   * Restore in-progress work only. If this draft was already added to cart and
   * that design is gone — or this is a different bag product — start blank.
   */
  async restoreDraft() {
    if (typeof sessionStorage === 'undefined') return;
    let raw = '';
    try {
      raw = sessionStorage.getItem(this.draftStorageKey()) || '';
    } catch {
      return;
    }
    if (!raw) return;
    try {
      const data = JSON.parse(raw);
      const bags = this._config?.bags || [];
      const pid = Number(data.bagProductId);
      const vid = Number(data.bagVariantId);
      const product = bags.find((/** @type {any} */ p) => p.id === pid);
      const variant = product?.variants?.find((/** @type {any} */ v) => v.id === vid);
      if (!product || !variant) {
        this.clearDraftStorage();
        return;
      }

      const replaceId = typeof data.replaceDesignId === 'string' ? data.replaceDesignId.trim() : '';
      if (replaceId) {
        try {
          const cart = await fetchCartJson();
          const stillInCart = (cart.items || []).some((line) => cartDesignId(line) === replaceId);
          if (!stillInCart) {
            this.clearDraftStorage();
            return;
          }
        } catch {
          this.clearDraftStorage();
          return;
        }
      }

      this._bagProductId = pid;
      this._bagVariantId = vid;
      this._prevBagProductIdForClear = pid;
      const step = Number(data.step);
      if (step === 1 || step === 2 || step === 3) this._step = step;
      if (isBagFace(data.bagSide)) this._bagSide = data.bagSide;
      if (typeof data.designTitle === 'string') this._designTitle = data.designTitle;
      if (replaceId) this._replaceDesignId = replaceId;
      if (Array.isArray(data.patches)) {
        this._patches = data.patches.filter((p) => p && Number(p.variantId) > 0);
        this.refreshPlacedPatchInchesFromCatalog();
        this.upgradePatchesFromCatalog();
        this._patches.forEach((p) => this.applyStrapClampToPatch(p));
      }
    } catch {
      /* ignore corrupt draft */
    }
  }

  /**
   * Fill catalog artwork / titles onto patches restored from cart lines.
   */
  upgradePatchesFromCatalog() {
    const cfg = this._config;
    if (!cfg) return;
    for (const p of this._patches) {
      const found = findPatchCatalogByVariant(cfg, p.variantId);
      if (!found) continue;
      p.productId = found.product.id;
      p.productTitle = found.product.title || p.productTitle;
      p.variantTitle = found.variant.title || p.variantTitle;
      p.price = found.variant.price ?? p.price;
      p.sku = found.variant.sku ?? p.sku;
      const img = patchOnBagImageUrl(found.product, found.variant);
      if (img) p.image = img;
    }
  }

  /**
   * Reopen a carted tote in this customizer. Leaves the original lines in cart
   * until the shopper saves again, then replaces them.
   * @param {string} editId
   */
  async hydrateFromCartEdit(editId) {
    const cfg = this._config;
    const id = String(editId || '').trim();
    if (!cfg || !id) return;

    try {
      const cart = await fetchCartJson();
      const items = Array.isArray(cart.items) ? cart.items : [];
      const matches = items.filter((line) => cartDesignId(line) === id);
      const bag =
        matches.find((line) => cartItemProp(line, 'Part') === 'Bag') ||
        matches.find(
          (line) =>
            cartItemProp(line, 'Preview front URL') ||
            cartItemProp(line, 'Preview back URL')
        );
      if (!bag) {
        throw new Error('That bag is no longer in your order.');
      }

      const bagProductId = Number(bag.product_id);
      const bagInCatalog = (cfg.bags || []).some(
        (/** @type {any} */ p) => Number(p.id) === bagProductId
      );
      if (!bagInCatalog && bag.url) {
        window.location.replace(withEditQuery(String(bag.url), id));
        return;
      }

      const variantId = Number(bag.variant_id || bag.id);
      if (bagInCatalog) {
        this._bagProductId = bagProductId;
        this._prevBagProductIdForClear = bagProductId;
      }
      if (Number.isFinite(variantId) && variantId > 0) {
        this._bagVariantId = variantId;
      }

      this._designTitle = cartItemProp(bag, 'Design title') || this._designTitle;
      this._replaceDesignId = id;
      this._showAddedPanel = false;
      this._lastAdded = null;
      this._selectedPatchIndex = null;
      this._tidyIndices = new Set();
      this._bagSide = 'front';

      const patchLines = matches.filter((line) => cartItemProp(line, 'Part') === 'Patch');
      let placements = parsePatchPlacement(cartItemProp(bag, 'Patch placement'));
      if (!placements.length) {
        const fromFront = parsePlacementsJsonSide(cartItemProp(bag, 'Placements front'), 'front');
        const fromBack = parsePlacementsJsonSide(cartItemProp(bag, 'Placements back'), 'back');
        const fromLeft = parsePlacementsJsonSide(cartItemProp(bag, 'Placements left'), 'left');
        const fromRight = parsePlacementsJsonSide(cartItemProp(bag, 'Placements right'), 'right');
        placements = fromFront.concat(fromBack, fromLeft, fromRight);
      }
      const usedLineKeys = new Set();
      /** @type {typeof this._patches} */
      const nextPatches = [];

      const takeLineForVariant = (vid) => {
        const hit = patchLines.find(
          (line) =>
            !usedLineKeys.has(String(line.key)) &&
            Number(line.variant_id || line.id) === Number(vid)
        );
        if (hit?.key) usedLineKeys.add(String(hit.key));
        return hit || null;
      };

      if (placements.length) {
        for (const place of placements) {
          nextPatches.push(
            this.buildPatchFromCart(place.variantId, {
              nx: place.nx,
              ny: place.ny,
              rot: place.rot,
              side: place.side,
              line: takeLineForVariant(place.variantId),
            })
          );
        }
      } else {
        patchLines.forEach((line, index) => {
          const face = cartItemProp(line, 'Bag face').toLowerCase();
          const place = defaultPatchPlacement(index);
          nextPatches.push(
            this.buildPatchFromCart(Number(line.variant_id || line.id), {
              nx: place.nx,
              ny: place.ny,
              rot: place.rot,
              side: face.indexOf('right') >= 0
                ? 'right'
                : face.indexOf('left') >= 0
                  ? 'left'
                  : face.indexOf('back') >= 0
                    ? 'back'
                    : 'front',
              line,
            })
          );
        });
      }

      this._patches = nextPatches.filter((p) => p && Number(p.variantId) > 0);
      this._patches.forEach((_, i) => this._tidyIndices.add(i));
      this.refreshPlacedPatchInchesFromCatalog();
      this.upgradePatchesFromCatalog();
      this._patches.forEach((p) => this.applyStrapClampToPatch(p));
      this._step = 2;
      this._patchSheetUserDismissed = true;
      this.ensurePatchSelection();
      this.persistDraftNow();
      stripCartEditParam();
      this.render();
      this.showToast(
        cfg.labels.editingToast ||
          'Editing your bag. Save it to your order when you’re done.',
        'success'
      );
    } catch (e) {
      console.warn('[tay-tote-builder] cart edit', e);
      this._replaceDesignId = '';
      await this.restoreDraft();
      this.render();
      this.showError(
        cfg.labels.editLoadFailed ||
          'Could not reopen that bag. You can still design a new one.'
      );
    }
  }

  /**
   * @param {number} variantId
   * @param {{ nx: number; ny: number; rot: number; side: 'front' | 'back'; line?: Record<string, unknown> | null }} opts
   */
  buildPatchFromCart(variantId, opts) {
    const cfg = this._config;
    const found = findPatchCatalogByVariant(cfg, variantId);
    const product = found?.product;
    const variant = found?.variant;
    const line = opts.line || null;
    this._patchStackSeq += 1;
    const size = product ? resolvePatchInches(product, cfg) : null;
    const lineImage =
      (typeof line?.image === 'string' && line.image) ||
      (typeof line?.featured_image === 'string' && line.featured_image) ||
      null;
    return {
      productId: product?.id ?? Number(line?.product_id) ?? 0,
      variantId: Number(variantId),
      productTitle: product?.title || String(line?.product_title || 'Patch'),
      variantTitle: variant?.title || String(line?.variant_title || ''),
      price: Number(variant?.price ?? line?.final_price ?? line?.price ?? 0),
      image: (product && variant ? patchOnBagImageUrl(product, variant) : null) || lineImage,
      sku: String(variant?.sku ?? line?.sku ?? ''),
      nx: opts.nx,
      ny: opts.ny,
      rot: opts.rot,
      patch_width_in: size?.w,
      patch_height_in: size?.h,
      stackOrder: this._patchStackSeq,
      side: opts.side,
    };
  }

  resetBuilderForAnotherBag() {
    this._showAddedPanel = false;
    this._lastAdded = null;
    this._replaceDesignId = '';
    this._step = 1;
    this._patches = [];
    this._selectedPatchIndex = null;
    this._tidyIndices = new Set();
    this._designTitle = '';
    this._bagSide = 'front';
    this._patchSheetOpen = false;
    this._tidySheetOpen = false;
    this._editorDetailsOpen = false;
    this._tidyDetailsOpen = false;
    this._undoStack = [];
    this.revokeReviewPreviewUrls();
    this.clearDraftStorage();
  }

  pushUndo() {
    this._undoStack.push({
      patches: this._patches.map((p) => Object.assign({}, p)),
      selected: this._selectedPatchIndex,
      tidy: Array.from(this._tidyIndices),
      side: this._bagSide,
    });
    if (this._undoStack.length > 25) this._undoStack.shift();
  }

  undoLastChange() {
    const snap = this._undoStack.pop();
    if (!snap) {
      this.showToast(this._config?.labels?.undoEmpty || 'Nothing to undo.', 'info');
      return;
    }
    this._patches = snap.patches.map((p) => Object.assign({}, p));
    this._selectedPatchIndex = snap.selected;
    this._tidyIndices = new Set(snap.tidy);
    this._bagSide = isBagFace(snap.side) ? snap.side : 'front';
    this.ensurePatchSelection();
    this.persistDraftNow();
    this.render();
    this.showToast(this._config?.labels?.undoToast || 'Undone.', 'success');
  }

  /**
   * Reopen the bag that was just added, without wiping placement.
   */
  reopenLastAddedBag() {
    const cfg = this._config;
    const addedId = this._lastAdded?.customizationId || '';
    this._showAddedPanel = false;
    this._replaceDesignId = addedId;
    this._step = this._patches.length ? 2 : 1;
    this._patchSheetUserDismissed = true;
    this._editorDetailsOpen = false;
    this._tidyDetailsOpen = false;
    this.persistDraftNow();
    this.render();
    if (addedId && this._patches.length) {
      this.showToast(
        cfg?.labels?.editingToast ||
          'Editing your bag. Save it to your order when you’re done.',
        'success'
      );
      return;
    }
    if (addedId) {
      void this.hydrateFromCartEdit(addedId);
    }
  }

  refreshPlacedPatchInchesFromCatalog() {
    const cfg = this._config;
    if (!cfg) return;
    for (const p of this._patches) {
      const prod =
        cfg.patches.find((/** @type {any} */ x) => x.id === p.productId) ||
        cfg.patches.find((/** @type {any} */ x) =>
          (x.variants || []).some((/** @type {any} */ v) => v.id === Number(p.variantId))
        );
      if (!prod) continue;
      const size = resolvePatchInches(prod, cfg);
      p.patch_width_in = size.w;
      p.patch_height_in = size.h;
    }
  }

  getBagProduct() {
    if (!this._config || this._bagProductId == null) return null;
    return this._config.bags.find((/** @type {any} */ p) => p.id === this._bagProductId) ?? null;
  }

  getBagVariant() {
    const product = this.getBagProduct();
    if (!product || this._bagVariantId == null) return null;
    return product.variants.find((/** @type {any} */ v) => v.id === this._bagVariantId) ?? null;
  }

  /**
   * Front canvas: this color’s crop, else the product crop, else the variant/listing photo.
   * @returns {string | null}
   */
  getBagFrontPreviewUrl() {
    const bag = this.getBagProduct();
    const variant = this.getBagVariant();
    if (variant?.preview_image) return variant.preview_image;
    if (bag?.preview_image) return bag.preview_image;
    return variant?.image ?? bag?.featured_image ?? null;
  }

  /**
   * Back canvas: this color’s back crop, else the product back crop, else the front crop.
   * @returns {string | null}
   */
  getBagBackPreviewUrl() {
    const bag = this.getBagProduct();
    const variant = this.getBagVariant();
    if (variant?.preview_image_back) return variant.preview_image_back;
    if (bag?.preview_image_back) return bag.preview_image_back;
    return this.getBagFrontPreviewUrl();
  }

  /**
   * Dedicated back crop only (no front fallback).
   * @returns {string | null}
   */
  getBagDedicatedBackPreviewUrl() {
    const bag = this.getBagProduct();
    const variant = this.getBagVariant();
    return variant?.preview_image_back || bag?.preview_image_back || null;
  }

  /**
   * @returns {string | null}
   */
  getBagLeftPreviewUrl() {
    const bag = this.getBagProduct();
    const variant = this.getBagVariant();
    return variant?.preview_image_left || bag?.preview_image_left || null;
  }

  /**
   * @returns {string | null}
   */
  getBagRightPreviewUrl() {
    const bag = this.getBagProduct();
    const variant = this.getBagVariant();
    return variant?.preview_image_right || bag?.preview_image_right || null;
  }

  /**
   * Faces the shopper can design on, in tab order.
   * Only includes faces with a real preview image — no synthetic Front|Back pair.
   * @returns {TayBagFace[]}
   */
  getAvailableBagFaces() {
    /** @type {TayBagFace[]} */
    const faces = [];
    if (this.getBagFrontPreviewUrl()) faces.push('front');
    if (this.getBagDedicatedBackPreviewUrl()) faces.push('back');
    if (this.getBagLeftPreviewUrl()) faces.push('left');
    if (this.getBagRightPreviewUrl()) faces.push('right');
    return faces;
  }

  /**
   * Multi-face toggle when more than one populated preview exists.
   * Front-only products show a single canvas with no face capsule tabs.
   */
  hasBagBackPreview() {
    return this.getAvailableBagFaces().length > 1;
  }

  /**
   * Keep `_bagSide` on an available face (e.g. after variant change).
   */
  ensureBagSideValid() {
    const faces = this.getAvailableBagFaces();
    if (!faces.length) {
      this._bagSide = 'front';
      return;
    }
    if (!faces.includes(this._bagSide)) {
      this._bagSide = faces[0];
    }
  }

  /**
   * Preview image for the canvas: step 1 is always front; steps 2–3 follow `_bagSide`.
   * @param {boolean} step1
   * @param {{ hiRes?: boolean }} [options]
   */
  getCanvasBagPreviewImage(step1, options = {}) {
    const hiRes = options.hiRes === true;
    const width = hiRes ? TAY_FS_BAG_IMAGE_WIDTH : null;
    const front = this.getBagFrontPreviewUrl();
    if (step1) return hiRes ? shopifyImageUrlWithWidth(front, width) : front;
    this.ensureBagSideValid();
    const url = this.getBagPreviewUrlForSide(this._bagSide);
    return hiRes ? shopifyImageUrlWithWidth(url, width) : url;
  }

  /**
   * Bag photo URL for a given face (used for checkout preview renders).
   * @param {TayBagFace} side
   * @param {{ hiRes?: boolean }} [options]
   * @returns {string | null}
   */
  getBagPreviewUrlForSide(side, options = {}) {
    const hiRes = options.hiRes === true;
    const width = hiRes ? TAY_FS_BAG_IMAGE_WIDTH : null;
    /** @type {string | null} */
    let url = null;
    if (side === 'front') {
      url = this.getBagFrontPreviewUrl();
    } else if (side === 'left') {
      url = this.getBagLeftPreviewUrl();
    } else if (side === 'right') {
      url = this.getBagRightPreviewUrl();
    } else if (side === 'back') {
      url = this.getBagDedicatedBackPreviewUrl() || this.getBagBackPreviewUrl();
    }
    return hiRes ? shopifyImageUrlWithWidth(url, width) : url;
  }

  /**
   * Renders bag + patches for one face to a PNG blob (same geometry as the live canvas).
   * @param {'front' | 'back'} side
   * @returns {Promise<Blob | null>}
   */
  async captureDesignPreviewBlob(side) {
    const bagUrl = this.getBagPreviewUrlForSide(side, { hiRes: true });
    if (!bagUrl) return null;

    const cfg = this._config;
    const bagProduct = this.getBagProduct();
    const bagWIn = Number(bagProduct?.bag_width_in) || Number(cfg?.defaultBagWidthIn) || 22.5;
    const bagHIn = Number(bagProduct?.bag_height_in) || Number(cfg?.defaultBagHeightIn) || 14.5;

    let bagImg;
    try {
      bagImg = await loadImageForCanvas(bagUrl);
    } catch (e) {
      console.warn('[tay-tote-builder] preview bag image failed', e);
      return null;
    }

    const nw = bagImg.naturalWidth || TAY_PREVIEW_EXPORT_WIDTH;
    const nh = bagImg.naturalHeight || Math.round(TAY_PREVIEW_EXPORT_WIDTH * (bagHIn / bagWIn));
    const exportW = TAY_PREVIEW_EXPORT_WIDTH;
    const exportH = Math.max(1, Math.round((exportW * nh) / nw));

    const canvas = document.createElement('canvas');
    canvas.width = exportW;
    canvas.height = exportH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const bagRect = computeObjectFitContainRect(exportW, exportH, nw, nh);
    ctx.drawImage(bagImg, bagRect.left, bagRect.top, bagRect.width, bagRect.height);

    const patches = this._patches
      .map((p, index) => ({ p, index }))
      .filter(({ p }) => !this.hasBagBackPreview() || patchSideOf(p) === side);

    patches.sort((a, b) => {
      const za = typeof a.p.stackOrder === 'number' ? a.p.stackOrder : a.index;
      const zb = typeof b.p.stackOrder === 'number' ? b.p.stackOrder : b.index;
      return za === zb ? a.index - b.index : za - zb;
    });

    for (const { p } of patches) {
      if (!p.image) continue;

      let patchImg;
      try {
        const onBagUrl = await getOnBagPatchUrl(p.image);
        if (!onBagUrl) continue;
        patchImg = await loadImageForCanvas(onBagUrl);
      } catch (e) {
        console.warn('[tay-tote-builder] preview patch image failed', e);
        continue;
      }

      const pwIn = Number(p.patch_width_in) || Number(cfg?.defaultPatchWidthIn) || 2;
      const phIn = Number(p.patch_height_in) || Number(cfg?.defaultPatchHeightIn) || 2;
      const box = patchBoxPxForBagBody(pwIn, phIn, bagRect, bagProduct, bagWIn, bagHIn, cfg);
      const size = scaledPatchDisplaySize(box.boxW, box.boxH);
      const nx = clamp01(typeof p.nx === 'number' ? p.nx : 0.5);
      const ny = clamp01(typeof p.ny === 'number' ? p.ny : 0.5);
      const cx = bagRect.left + nx * bagRect.width;
      const cy = bagRect.top + ny * bagRect.height;
      const rot = (normalizeRotationDeg(p.rot) * Math.PI) / 180;
      const dw = size.w;
      const dh = size.h;

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(rot);
      ctx.drawImage(patchImg, -dw / 2, -dh / 2, dw, dh);
      ctx.restore();
    }

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png', 0.92);
    });
  }

  /**
   * @param {string} customizationId
   * @param {TayBagFace} side
   * @param {Blob} blob
   * @returns {Promise<string | null>}
   */
  async uploadDesignPreview(customizationId, side, blob) {
    const uploadUrl = String(this._config?.previewUploadUrl ?? '').trim();
    if (!uploadUrl) return null;

    const imageBase64 = await blobToBase64(blob);
    const response = await fetch(uploadUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ customizationId, side, imageBase64 }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const msg = data.error || data.message || `Upload failed (${response.status})`;
      throw new Error(msg);
    }
    const url = typeof data.url === 'string' ? data.url.trim() : '';
    return url || null;
  }

  /**
   * @param {string} customizationId
   * @returns {Promise<{ front: string | null; back: string | null; left: string | null; right: string | null }>}
   */
  async uploadCheckoutPreviews(customizationId) {
    const empty = { front: null, back: null, left: null, right: null };
    const uploadUrl = String(this._config?.previewUploadUrl ?? '').trim();
    if (!uploadUrl) return empty;

    const sides = this.getAvailableBagFaces();
    /** @type {{ front: string | null; back: string | null; left: string | null; right: string | null }} */
    const results = { front: null, back: null, left: null, right: null };

    await Promise.all(
      sides.map(async (side) => {
        const blob = await this.captureDesignPreviewBlob(side);
        if (!blob) return;
        const url = await this.uploadDesignPreview(customizationId, side, blob);
        results[side] = url ? clipLineProperty(url, TAY_PREVIEW_URL_MAX) : null;
      })
    );

    return results;
  }

  revokeReviewPreviewUrls() {
    for (const side of /** @type {const} */ (['front', 'back', 'left', 'right'])) {
      const url = this._reviewPreviewObjectUrls[side];
      if (url) URL.revokeObjectURL(url);
      this._reviewPreviewObjectUrls[side] = null;
    }
  }

  scheduleReviewPreviews() {
    if (this._step !== 3) return;
    const faces = this.getAvailableBagFaces();
    const ready = faces.length > 0 && faces.every((side) => this._reviewPreviewObjectUrls[side]);
    if (ready) {
      this.updateReviewPreviewDom();
      return;
    }
    void this.ensureReviewPreviews();
  }

  async ensureReviewPreviews() {
    if (this._step !== 3) return;
    const gen = ++this._reviewPreviewGen;
    const root = this.querySelector('[data-ref="review-design-previews"]');
    if (root instanceof HTMLElement) {
      root.classList.add('is-loading');
    }

    const faces = this.getAvailableBagFaces();
    /** @type {{ front: string | null; back: string | null; left: string | null; right: string | null }} */
    const nextUrls = { front: null, back: null, left: null, right: null };

    await Promise.all(
      faces.map(async (side) => {
        const blob = await this.captureDesignPreviewBlob(side);
        if (gen !== this._reviewPreviewGen || this._step !== 3) return;
        if (blob) nextUrls[side] = URL.createObjectURL(blob);
      })
    );

    if (gen !== this._reviewPreviewGen || this._step !== 3) {
      for (const side of /** @type {const} */ (['front', 'back', 'left', 'right'])) {
        if (nextUrls[side]) URL.revokeObjectURL(nextUrls[side]);
      }
      return;
    }

    this.revokeReviewPreviewUrls();
    this._reviewPreviewObjectUrls = nextUrls;
    this.updateReviewPreviewDom();
  }

  updateReviewPreviewDom() {
    const root = this.querySelector('[data-ref="review-design-previews"]');
    if (!(root instanceof HTMLElement)) return;
    root.classList.remove('is-loading');
    root.setAttribute('aria-busy', 'false');
    const statusEl = this.querySelector('[data-ref="review-design-status"]');
    if (statusEl instanceof HTMLElement) {
      statusEl.textContent =
        this._config?.labels?.reviewDesignReady || 'Design previews ready.';
    }

    const labels = this._config?.labels || {};

    root.querySelectorAll('[data-review-face]').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const side = node.dataset.reviewFace;
      if (!isBagFace(side)) return;
      const url = this._reviewPreviewObjectUrls[side];
      const lbl = bagFaceLabel(side, labels);
      const frame = node.querySelector('.tay-builder-review-design__frame');
      if (!(frame instanceof HTMLElement)) return;

      frame.classList.toggle('is-loading', !url);
      frame.classList.toggle('is-ready', Boolean(url));

      if (url) {
        let img = frame.querySelector('img');
        if (img instanceof HTMLImageElement) {
          img.src = url;
          img.alt = `${lbl} design preview`;
        } else {
          frame.innerHTML = `<img src="${escapeHtml(url)}" alt="${escapeHtml(`${lbl} design preview`)}" width="600" height="750" loading="lazy">`;
        }
      } else {
        frame.innerHTML =
          '<span class="tay-builder-review-design__spinner" aria-hidden="true"></span>';
      }
    });
  }

  /**
   * @param {Record<string, string>} labels
   */
  buildReviewDesignPreviewsHtml(labels) {
    const heading = labels.reviewDesignHeading || 'How your bag will look';
    const hint = labels.reviewDesignHint || 'These match what we send with your order.';
    const loadingLbl = labels.reviewDesignLoading || 'Generating preview…';
    const expandLbl = labels.reviewDesignExpand || 'View larger';
    const faces = this.getAvailableBagFaces();

    const figure = (/** @type {TayBagFace} */ side) => {
      const lbl = bagFaceLabel(side, labels);
      const url = this._reviewPreviewObjectUrls[side];
      const frameInner = url
        ? `<img src="${escapeHtml(url)}" alt="${escapeHtml(`${lbl} design preview`)}" width="600" height="750" loading="lazy">`
        : '<span class="tay-builder-review-design__spinner" aria-hidden="true"></span>';
      return `
        <figure class="tay-builder-review-design" data-review-face="${side}">
          <button type="button" class="tay-builder-review-design__frame${url ? ' is-ready' : ' is-loading'}" data-action="open-review-face-preview" data-face="${side}" aria-label="${escapeHtml(expandLbl)} — ${escapeHtml(lbl)}">
            ${frameInner}
          </button>
          <figcaption class="tay-builder-review-design__label">${escapeHtml(lbl)}</figcaption>
        </figure>`;
    };

    return `
      <section class="tay-builder-review-designs is-loading" data-ref="review-design-previews" aria-busy="true" aria-label="${escapeHtml(heading)}">
        <p class="tay-builder-review-designs__heading">${escapeHtml(heading)}</p>
        <p class="tay-builder-review-designs__hint">${escapeHtml(hint)}</p>
        <p class="tay-builder-review-designs__status tay-builder-sr-only" data-ref="review-design-status">${escapeHtml(loadingLbl)}</p>
        <div class="tay-builder-review-designs__grid">
          ${faces.map((side) => figure(side)).join('')}
        </div>
      </section>`;
  }

  /**
   * @returns {number[]}
   */
  getTidyTargetsForActiveSide() {
    return this.getTidyTargets().filter((i) => {
      const p = this._patches[i];
      return p && patchSideOf(p) === this._bagSide;
    });
  }

  /**
   * Every placed patch on the face currently shown (front or back).
   * @returns {number[]}
   */
  getActiveSidePatchIndices() {
    const idxs = [];
    this._patches.forEach((p, i) => {
      if (p && patchSideOf(p) === this._bagSide) idxs.push(i);
    });
    return idxs;
  }

  selectAllTidyOnActiveSide() {
    const next = new Set();
    this._patches.forEach((p, i) => {
      if (patchSideOf(p) === this._bagSide) next.add(i);
    });
    this._tidyIndices = next;
  }

  /**
   * @returns {number}
   */
  getMinPatches() {
    const cfg = this._config;
    const raw = cfg?.minPatches;
    const m = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(m) || m < 1) return 3;
    const maxRaw = cfg?.maxPatches;
    const maxP = typeof maxRaw === 'number' ? maxRaw : Number(maxRaw);
    const cap = Number.isFinite(maxP) ? maxP : 30;
    return Math.min(Math.floor(m), cap);
  }

  computeTotalCents() {
    let total = 0;
    const v = this.getBagVariant();
    if (v) total += v.price;
    this._patches.forEach((p) => {
      total += p.price;
    });
    return total;
  }

  /**
   * @param {Record<string, string>} labels
   * @returns {Array<{ title: string; meta: string | null; amount: number; image: string | null }>}
   */
  buildPriceBreakdownLines(labels) {
    const lines = [];
    const bagVariant = this.getBagVariant();
    const bagProduct = this.getBagProduct();
    if (bagVariant && bagProduct) {
      lines.push({
        title: bagProduct.title,
        meta: bagVariant.title,
        amount: bagVariant.price,
        image: bagVariant.image || bagProduct.featured_image || null,
      });
    }
    for (const p of this._patches) {
      const meta =
        p.variantTitle && p.variantTitle !== 'Default Title' ? p.variantTitle : null;
      lines.push({
        title: p.productTitle,
        meta,
        amount: p.price,
        image: p.image || p.featured_image || null,
      });
    }
    return lines;
  }

  /**
   * @param {{ title: string; meta: string | null; amount: number; image?: string | null }} line
   * @param {string} currency
   * @param {{ isTotal?: boolean }} [opts]
   */
  breakdownLineHtml(line, currency, opts = {}) {
    const meta = line.meta
      ? `<span class="tay-builder-summary-breakdown__meta">${escapeHtml(line.meta)}</span>`
      : '';
    const thumb = line.image
      ? `<span class="tay-builder-summary-breakdown__thumb"><img src="${escapeHtml(line.image)}" alt=""></span>`
      : `<span class="tay-builder-summary-breakdown__thumb tay-builder-summary-breakdown__thumb--empty" aria-hidden="true"></span>`;
    const totalClass = opts.isTotal ? ' tay-builder-summary-breakdown__line--total' : '';
    const thumbHtml = opts.isTotal ? '' : thumb;
    return `<li class="tay-builder-summary-breakdown__line${totalClass}">
      ${thumbHtml}
      <span class="tay-builder-summary-breakdown__info">
        <span class="tay-builder-summary-breakdown__title">${escapeHtml(line.title)}</span>
        ${meta}
      </span>
      <span class="tay-builder-summary-breakdown__price">${escapeHtml(formatMoneyCents(line.amount, currency))}</span>
    </li>`;
  }

  /**
   * @param {Record<string, string>} labels
   * @param {string} currency
   * @param {{ minP?: number; patchCount?: number; hideCaption?: boolean }} [opts]
   */
  buildSummaryBarHtml(labels, currency, opts = {}) {
    const total = this.computeTotalCents();
    const caption = labels.totalCaption || 'Bag + patches';
    const hideCaption = opts.hideCaption === true;
    const seeMore = labels.seeBreakdown || 'See more';
    const seeLess = labels.seeBreakdownLess || 'See less';
    const lines = this.buildPriceBreakdownLines(labels);
    const hasBreakdown = lines.length > 0;
    const expanded = this._summaryExpanded && hasBreakdown;
    const minP = opts.minP ?? 0;
    const patchCount = opts.patchCount ?? this._patches.length;
    const progressHtml =
      minP > 0 && patchCount < minP
        ? `<p class="tay-builder-summary-bar__progress" role="status">
            <span>${escapeHtml(labels.minPatchesProgress || 'Patches on bag')}</span>
            <strong>${patchCount}/${minP}</strong>
          </p>`
        : '';

    const breakdownHtml = expanded
      ? `<ul class="tay-builder-summary-breakdown" data-ref="summary-breakdown">
          ${lines.map((line) => this.breakdownLineHtml(line, currency)).join('')}
        </ul>`
      : '';

    const toggleBtn = hasBreakdown
      ? `<button type="button" class="tay-builder-summary-bar__toggle" data-action="toggle-summary-breakdown" aria-expanded="${expanded ? 'true' : 'false'}">
          <span class="tay-builder-summary-bar__toggle-text">${escapeHtml(expanded ? seeLess : seeMore)}</span>
          <span class="tay-builder-summary-bar__toggle-chevron" aria-hidden="true"></span>
        </button>`
      : '';

    const captionHtml = hideCaption
      ? ''
      : `<span class="tay-builder-summary-bar__caption">${escapeHtml(caption)}</span>`;

    return `
      <div class="tay-builder-summary-bar" aria-live="polite">
        <div class="tay-builder-summary-bar__row">
          <div class="tay-builder-summary-bar__labels">
            <span class="tay-builder-summary-bar__title">${escapeHtml(labels.total || 'Total')}</span>
            ${captionHtml}
            ${preOrderChipHtml(readPreOrder(this.getBagProduct()))}
          </div>
          <div class="tay-builder-summary-bar__end">
            <strong class="tay-builder-summary-bar__amount" data-ref="summary-total">${escapeHtml(formatMoneyCents(total, currency))}</strong>
            ${toggleBtn}
          </div>
        </div>
        ${progressHtml}
        ${breakdownHtml}
      </div>`;
  }

  /**
   * @param {Record<string, string>} labels
   * @param {string} currency
   */
  buildPriceBreakdownListHtml(labels, currency) {
    const lines = this.buildPriceBreakdownLines(labels);
    const total = this.computeTotalCents();
    return `
      <ul class="tay-builder-summary-breakdown tay-builder-summary-breakdown--sheet">
        ${lines.map((line) => this.breakdownLineHtml(line, currency)).join('')}
        ${this.breakdownLineHtml(
          { title: labels.total || 'Estimated total', meta: null, amount: total, image: null },
          currency,
          { isTotal: true }
        )}
      </ul>`;
  }

  /**
   * @param {Record<string, string>} labels
   */
  buildMobileStep2ActionsHtml(labels) {
    const openLbl = labels.openPatchLibrary || labels.patchLibrary || 'Add patches';
    const tidyRaw = labels.tidyHeading || 'Tidy';
    const tidyLbl = tidyRaw.replace(/\s+patches$/i, '').trim() || 'Tidy';
    const canTidy = this._patches.length > 0;
    return `
      <div class="tay-builder-mobile-back-wrap">
        <button type="button" class="tay-builder-mobile-back" data-action="wizard-back">${escapeHtml(labels.back || 'Back')}</button>
      </div>
      <div class="tay-builder-mobile-actions">
        <button type="button" class="tay-builder-add-patches-bar" data-action="open-patch-sheet">
          <span class="tay-builder-add-patches-bar__icon" aria-hidden="true"></span>
          <span class="tay-builder-add-patches-bar__label">${escapeHtml(openLbl)}</span>
        </button>
        <button type="button" class="tay-builder-tidy-bar" data-action="open-tidy-sheet"${blockedActionAttrs(canTidy)}>
          <span class="tay-builder-tidy-bar__label">${escapeHtml(tidyLbl)}</span>
        </button>
      </div>`;
  }

  /**
   * @param {Record<string, string>} labels
   * @param {string} currency
   */
  buildBreakdownSheetHtml(labels, currency) {
    const open = this._breakdownSheetOpen;
    const closeLbl = labels.closeSheet || labels.back || 'Close';
    const title = labels.priceBreakdownTitle || labels.total || 'Estimated total';
    return `
      <div class="tay-builder-breakdown-sheet${open ? ' is-open' : ''}" data-ref="breakdown-sheet" aria-hidden="${open ? 'false' : 'true'}">
        <button type="button" class="tay-builder-breakdown-sheet__backdrop" data-action="close-breakdown-sheet" aria-label="${escapeHtml(closeLbl)}"></button>
        <div class="tay-builder-breakdown-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="tay-breakdown-sheet-title">
          <div class="tay-builder-breakdown-sheet__grab" aria-hidden="true"></div>
          <header class="tay-builder-breakdown-sheet__head">
            <h2 class="tay-builder-breakdown-sheet__title" id="tay-breakdown-sheet-title">${escapeHtml(title)}</h2>
            <button type="button" class="tay-builder-breakdown-sheet__close" data-action="close-breakdown-sheet" aria-label="${escapeHtml(closeLbl)}">×</button>
          </header>
          <div class="tay-builder-breakdown-sheet__body">
            ${this.buildPriceBreakdownListHtml(labels, currency)}
          </div>
        </div>
      </div>`;
  }

  /**
   * Slim bottom action bar — estimated total + review on mobile step 2.
   * @param {Record<string, string>} labels
   * @param {number} minP
   * @param {boolean} canReview
   * @param {string} currency
   */
  buildMobileDockHtml(labels, minP, canReview, currency) {
    const reviewLbl = labels.reviewShort || 'Review';
    const totalLbl = labels.total || 'Estimated total';
    const totalAmt = formatMoneyCents(this.computeTotalCents(), currency);
    const patchCount = this._patches.length;
    const reviewHint = !canReview
      ? escapeHtml(labels.minPatchesError || `Add at least ${minP} patches to continue.`)
      : '';
    const reviewMeta =
      patchCount < minP ? `<span class="tay-builder-mobile-dock__btn-meta">${patchCount}/${minP}</span>` : '';
    return `
      <div class="tay-builder-mobile-dock tay-builder-mobile-dock--design" role="toolbar" aria-label="${escapeHtml(labels.progressPatches || 'Design')}">
        <div class="tay-builder-mobile-dock__actions tay-builder-mobile-dock__actions--pills">
          <button type="button" class="tay-builder-mobile-dock__total" data-action="open-breakdown-sheet" aria-label="${escapeHtml(labels.seeBreakdown || 'View price breakdown')}">
            <span class="tay-builder-mobile-dock__total-copy">
              <span class="tay-builder-mobile-dock__total-label">${escapeHtml(totalLbl)}</span>
            </span>
            <strong class="tay-builder-mobile-dock__total-amount" data-ref="dock-total">${escapeHtml(totalAmt)}</strong>
          </button>
          <button type="button" class="tay-builder-mobile-dock__review${canReview ? ' is-ready' : ''}" data-action="wizard-next" data-mobile-review="true"${blockedActionAttrs(canReview)} title="${reviewHint}" aria-describedby="${canReview ? '' : 'tay-mobile-review-hint'}">
            <span class="tay-builder-mobile-dock__btn-label">${escapeHtml(reviewLbl)}</span>
            ${reviewMeta}
          </button>
        </div>
        ${!canReview ? `<p id="tay-mobile-review-hint" class="tay-builder-sr-only">${reviewHint}</p>` : ''}
      </div>`;
  }

  /**
   * @param {Record<string, string>} labels
   * @param {boolean} canContinue
   */
  buildMobileStep1DockHtml(labels, canContinue) {
    return `
      <div class="tay-builder-mobile-dock tay-builder-mobile-dock--step1" role="toolbar" aria-label="${escapeHtml(labels.stepBag || 'Bag')}">
        <div class="tay-builder-mobile-dock__actions tay-builder-mobile-dock__actions--pills tay-builder-mobile-dock__actions--step1">
          <button type="button" class="tay-builder-mobile-dock__back" data-action="wizard-back">
            <span class="tay-builder-mobile-dock__btn-label">${escapeHtml(labels.back || 'Back')}</span>
          </button>
          <button type="button" class="tay-builder-mobile-dock__continue" data-action="wizard-next"${blockedActionAttrs(canContinue)}>
            <span class="tay-builder-mobile-dock__btn-label">${escapeHtml(labels.nextShort || 'Next')}</span>
          </button>
        </div>
      </div>`;
  }

  /**
   * Leave step 1 for the bag picker / previous screen.
   */
  exitStep1ToBrowse() {
    const cfg = this._config;
    const url = String(cfg?.bagsBrowseUrl || cfg?.startOverUrl || '').trim();
    if (url) {
      window.location.href = url;
      return;
    }
    if (typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back();
    }
  }

  /**
   * @param {Record<string, string>} labels
   * @param {boolean} canSubmit
   */
  buildMobileReviewDockHtml(labels, canSubmit) {
    return `
      <div class="tay-builder-mobile-dock tay-builder-mobile-dock--review" role="toolbar" aria-label="${escapeHtml(labels.stepReview || 'Review')}">
        <div class="tay-builder-mobile-dock__actions tay-builder-mobile-dock__actions--pills tay-builder-mobile-dock__actions--review">
          <button type="button" class="tay-builder-mobile-dock__back" data-action="wizard-back">
            <span class="tay-builder-mobile-dock__btn-label">${escapeHtml(labels.back || 'Back')}</span>
          </button>
          <button type="button" class="tay-builder-mobile-dock__submit${canSubmit ? ' is-ready' : ''}" data-ref="submit"${blockedActionAttrs(canSubmit)}>
            <span class="tay-builder-mobile-dock__btn-label">${escapeHtml(this.orderSubmitLabel())}</span>
          </button>
        </div>
      </div>`;
  }


  /**
   * @param {any} cfg
   * @param {Record<string, string>} labels
   * @param {string} sectionId
   * @param {string} patchesHtml
   * @param {string} libraryChrome
   */
  buildPatchSheetHtml(_cfg, labels, sectionId, patchesHtml, libraryChrome) {
    const open = this._patchSheetOpen;
    const closeLbl = labels.closeSheet || labels.back || 'Close';
    const grabHint = escapeHtml(labels.sheetGrabHint || 'Swipe down or tap outside to close');
    return `
      <div class="tay-builder-patch-sheet${open ? ' is-open' : ''}" data-ref="patch-sheet" aria-hidden="${open ? 'false' : 'true'}">
        <button type="button" class="tay-builder-patch-sheet__backdrop" data-action="close-patch-sheet" aria-label="${escapeHtml(closeLbl)}"></button>
        <div class="tay-builder-patch-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="tay-patch-sheet-title-${sectionId}">
          <div class="tay-builder-patch-sheet__grab" aria-hidden="true" title="${grabHint}"></div>
          <header class="tay-builder-patch-sheet__head">
            <h2 class="tay-builder-patch-sheet__title" id="tay-patch-sheet-title-${sectionId}">${escapeHtml(labels.patchLibrary)}</h2>
            <button type="button" class="tay-builder-patch-sheet__close" data-action="close-patch-sheet" aria-label="${escapeHtml(closeLbl)}">×</button>
          </header>
          <div class="tay-builder-patch-sheet__body">
            ${libraryChrome}
            <div class="tay-builder-patch-grid tay-builder-patch-grid--sheet">${patchesHtml}</div>
          </div>
        </div>
      </div>`;
  }

  /**
   * @param {Record<string, string>} labels
   */
  buildTidySheetHtml(labels) {
    const open = this._tidySheetOpen;
    const closeLbl = labels.closeSheet || labels.back || 'Close';
    const tidyRaw = labels.tidyHeading || 'Tidy';
    const tidyLbl = tidyRaw.replace(/\s+patches$/i, '').trim() || 'Tidy';
    return `
      <div class="tay-builder-tidy-sheet${open ? ' is-open' : ''}" data-ref="tidy-sheet" aria-hidden="${open ? 'false' : 'true'}">
        <button type="button" class="tay-builder-tidy-sheet__backdrop" data-action="close-tidy-sheet" aria-label="${escapeHtml(closeLbl)}"></button>
        <div class="tay-builder-tidy-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="tay-tidy-sheet-title">
          <div class="tay-builder-tidy-sheet__grab" aria-hidden="true"></div>
          <header class="tay-builder-tidy-sheet__head">
            <h2 class="tay-builder-tidy-sheet__title" id="tay-tidy-sheet-title">${escapeHtml(tidyLbl)}</h2>
            <button type="button" class="tay-builder-tidy-sheet__close" data-action="close-tidy-sheet" aria-label="${escapeHtml(closeLbl)}">×</button>
          </header>
          <div class="tay-builder-tidy-sheet__body">
            ${this.buildPatchStripHtml(labels)}
            ${this.buildTidyToolbarHtml(labels, { plain: true })}
          </div>
        </div>
      </div>`;
  }

  /**
   * @param {HTMLElement | null} card
   * @returns {boolean}
   */
  tryAddPatchFromCard(card) {
    const cfg = this._config;
    if (!cfg) return false;

    if (this._patches.length >= cfg.maxPatches) {
      this.showError(cfg.labels.maxPatchesError);
      return false;
    }

    const productId = Number(card?.dataset.patchProductId);
    const product = cfg.patches.find((/** @type {any} */ p) => p.id === productId);
    if (!product) return false;

    const select = card?.querySelector('[data-patch-variant-select]');
    let variant = null;
    if (select instanceof HTMLSelectElement) {
      variant = product.variants.find((/** @type {any} */ v) => v.id === Number(select.value)) ?? null;
    } else {
      variant = firstAvailableVariant(product.variants);
    }

    if (!variant || !variant.available) {
      this.showError(cfg.labels.patchUnavailable || 'Sold out.');
      return false;
    }

    const place = defaultPatchPlacement(this._patches.length);
    const size = resolvePatchInches(product, cfg);
    const pwIn = size.w;
    const phIn = size.h;
    const cleared = this.clampPatchPlacement(place.nx, place.ny, {
      patch_width_in: pwIn,
      patch_height_in: phIn,
      rot: place.rot,
    });

    this._patchStackSeq += 1;
    const newIndex = this._patches.length;
    this.pushUndo();
    this._patches.push({
      productId: product.id,
      variantId: variant.id,
      productTitle: product.title,
      variantTitle: variant.title,
      price: variant.price,
      image: patchOnBagImageUrl(product, variant),
      sku: variant.sku ?? '',
      nx: cleared.nx,
      ny: cleared.ny,
      rot: place.rot,
      patch_width_in: pwIn,
      patch_height_in: phIn,
      stackOrder: this._patchStackSeq,
      side: this.hasBagBackPreview() ? this._bagSide : 'front',
    });
    getOnBagPatchUrl(patchOnBagImageUrl(product, variant));
    this._tidyIndices.add(newIndex);
    this._selectedPatchIndex = newIndex;
    this.clearError();

    const addedLbl = cfg.labels.patchAddedToast || 'Added to your bag';
    const name = variant.title && variant.title !== 'Default Title'
      ? `${product.title} (${variant.title})`
      : product.title;
    if (this.isMobileLayout()) {
      this.showToast(`${addedLbl}: ${name}`, 'success');
    }
    return true;
  }

  closePatchSheet() {
    this._patchSheetOpen = false;
    if (this._step === 2) this._patchSheetUserDismissed = true;
    if (!this._tidySheetOpen) {
      this.unbindSheetKeyHandler();
      this.unlockBodyForSheet();
    }
  }

  openPatchSheet() {
    if (!this.isMobileLayout()) {
      const lib = this.querySelector('.tay-builder-desktop-library');
      if (lib instanceof HTMLElement) {
        lib.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      return;
    }
    this._tidySheetOpen = false;
    this._patchSheetOpen = true;
    this.lockBodyForSheet();
    this.bindSheetKeyHandler();
  }

  closeTidySheet() {
    this._tidySheetOpen = false;
    if (!this._patchSheetOpen) {
      this.unbindSheetKeyHandler();
      this.unlockBodyForSheet();
    }
  }

  openTidySheet() {
    this._patchSheetOpen = false;
    this._tidySheetOpen = true;
    if (this.getTidyTargetsForActiveSide().length === 0) {
      this.selectAllTidyOnActiveSide();
    }
    this.lockBodyForSheet();
    this.bindSheetKeyHandler();
  }

  closeBreakdownSheet() {
    this._breakdownSheetOpen = false;
    this.unlockBodyForSheet();
  }

  openBreakdownSheet() {
    this._breakdownSheetOpen = true;
    this.lockBodyForSheet();
  }

  closeFullscreenPreview() {
    this._fullscreenOpen = false;
    this._fsZoom = 1;
    this._fsPanX = 0;
    this._fsPanY = 0;
    document.body.classList.remove('tay-builder-fs-open');
    this.unlockBodyForSheet();
  }

  openFullscreenPreview() {
    this._fullscreenOpen = true;
    this._fsZoom = 1;
    this._fsPanX = 0;
    this._fsPanY = 0;
    document.body.classList.add('tay-builder-fs-open');
    this.lockBodyForSheet();
  }

  bindPatchSheetSwipe() {
    if (this._patchSheetSwipeBound || !this.isMobileLayout()) return;
    this._patchSheetSwipeBound = true;
    let startY = 0;
    let lastDy = 0;
    let dragging = false;
    /** @type {HTMLElement | null} */
    let activePanel = null;

    this.addEventListener(
      'touchstart',
      (e) => {
        if ((!this._patchSheetOpen && !this._tidySheetOpen) || e.touches.length !== 1) return;
        const panel =
          e.target instanceof Element
            ? e.target.closest('.tay-builder-patch-sheet__panel, .tay-builder-tidy-sheet__panel')
            : null;
        if (!(panel instanceof HTMLElement)) return;
        const body = panel.querySelector('.tay-builder-patch-sheet__body, .tay-builder-tidy-sheet__body');
        if (body instanceof HTMLElement && body.scrollTop > 4) return;
        activePanel = panel;
        startY = e.touches[0].clientY;
        lastDy = 0;
        dragging = true;
        panel.style.transition = 'none';
      },
      { passive: true }
    );
    this.addEventListener(
      'touchmove',
      (e) => {
        if (!dragging || !activePanel || e.touches.length !== 1) return;
        lastDy = Math.max(0, e.touches[0].clientY - startY);
        activePanel.style.transform = `translateY(${lastDy}px)`;
      },
      { passive: true }
    );
    const endSwipe = () => {
      if (!dragging || !activePanel) return;
      const panel = activePanel;
      dragging = false;
      activePanel = null;
      panel.style.transition = '';
      const dy = lastDy;
      lastDy = 0;
      panel.style.transform = '';
      if (dy > 72) {
        if (this._tidySheetOpen) this.closeTidySheet();
        else this.closePatchSheet();
        this.render();
      }
    };
    this.addEventListener('touchend', endSwipe, { passive: true });
    this.addEventListener('touchcancel', endSwipe, { passive: true });
  }

  bindSheetKeyHandler() {
    this.unbindSheetKeyHandler();
    this._sheetKeyHandler = (e) => {
      if (e.key !== 'Escape') return;
      if (this._fullscreenOpen) {
        this.closeFullscreenPreview();
        this.render();
        return;
      }
      if (this._breakdownSheetOpen) {
        this.closeBreakdownSheet();
        this.render();
        return;
      }
      if (this._tidySheetOpen) {
        this.closeTidySheet();
        this.render();
        return;
      }
      if (this._patchSheetOpen) {
        this.closePatchSheet();
        this.render();
      }
    };
    document.addEventListener('keydown', this._sheetKeyHandler);
  }

  unbindSheetKeyHandler() {
    if (this._sheetKeyHandler) {
      document.removeEventListener('keydown', this._sheetKeyHandler);
      this._sheetKeyHandler = null;
    }
  }

  /**
   * @param {number | null} idx
   */
  updateStripSelectionVisual(idx) {
    this.querySelectorAll('.tay-builder-strip-chip').forEach((chip, i) => {
      if (!(chip instanceof HTMLElement)) return;
      chip.classList.toggle('is-selected', idx !== null && i === idx);
      const main = chip.querySelector('[data-action="select-patch"]');
      if (main instanceof HTMLButtonElement) {
        main.setAttribute('aria-pressed', idx !== null && i === idx ? 'true' : 'false');
      }
    });
  }

  ensurePatchSelection() {
    if (this._patches.length === 0) {
      this._selectedPatchIndex = null;
      return;
    }
    if (
      this._selectedPatchIndex == null ||
      this._selectedPatchIndex < 0 ||
      this._selectedPatchIndex >= this._patches.length
    ) {
      this._selectedPatchIndex = this._patches.length - 1;
    }
  }

  ensureSelectionMatchesBagSide() {
    if (!this.hasBagBackPreview()) return;
    if (this._step !== 2 && this._step !== 3) return;
    const side = this._bagSide;
    const sel = this._selectedPatchIndex;
    const selOk = sel != null && this._patches[sel] && patchSideOf(this._patches[sel]) === side;
    if (!selOk) {
      const found = this._patches.findIndex((p) => patchSideOf(p) === side);
      this._selectedPatchIndex = found >= 0 ? found : null;
    }
  }

  sanitizeTidyIndices() {
    const n = this._patches.length;
    const next = new Set();
    this._tidyIndices.forEach((i) => {
      if (i >= 0 && i < n) next.add(i);
    });
    this._tidyIndices = next;
  }

  /**
   * @returns {number[]}
   */
  getTidyTargets() {
    this.sanitizeTidyIndices();
    return Array.from(this._tidyIndices).sort((a, b) => a - b);
  }

  /**
   * @param {number} index
   */
  removePatchAt(index) {
    if (Number.isNaN(index) || index < 0 || !this._patches[index]) return;
    this.pushUndo();
    this._patches.splice(index, 1);
    this.remapTidyIndicesAfterRemove(index);
    if (this._selectedPatchIndex != null) {
      if (this._patches.length === 0) this._selectedPatchIndex = null;
      else if (this._selectedPatchIndex === index)
        this._selectedPatchIndex = Math.min(index, this._patches.length - 1);
      else if (this._selectedPatchIndex > index) this._selectedPatchIndex -= 1;
    }
    this.render();
  }

  remapTidyIndicesAfterRemove(removedIndex) {
    const next = new Set();
    this._tidyIndices.forEach((j) => {
      if (j === removedIndex) return;
      next.add(j > removedIndex ? j - 1 : j);
    });
    this._tidyIndices = next;
  }

  /**
   * @param {any} product
   */
  patchLibraryProductMatches(product) {
    const cat = this._patchLibraryCategory;
    const q = this._patchLibraryQuery;
    const qq = q.trim().toLowerCase();
    if (qq && !String(product.title || '').toLowerCase().includes(qq)) return false;
    if (cat !== 'all') {
      const tags = (product.tags || []).map((/** @type {unknown} */ t) => normalizePatchTag(t));
      if (!tags.includes(cat)) return false;
    }
    return true;
  }

  applyPatchLibraryFilter() {
    const cfg = this._config;
    if (!cfg) return;
    this.querySelectorAll('[data-patch-product-id]').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const pid = Number(node.dataset.patchProductId);
      const product = cfg.patches.find((/** @type {any} */ p) => p.id === pid);
      const ok = product ? this.patchLibraryProductMatches(product) : false;
      node.classList.toggle('tay-builder-patch-card--filtered-out', !ok);
    });
  }

  /**
   * @param {string} mode
   */
  runTidyAlign(mode) {
    const idxs = this.getTidyTargetsForActiveSide();
    if (idxs.length === 0) return;
    if (mode === 'left') {
      const v = Math.min(...idxs.map((i) => this._patches[i].nx));
      idxs.forEach((i) => {
        this._patches[i].nx = clamp01(v);
      });
    } else if (mode === 'right') {
      const v = Math.max(...idxs.map((i) => this._patches[i].nx));
      idxs.forEach((i) => {
        this._patches[i].nx = clamp01(v);
      });
    } else if (mode === 'hcenter') {
      const v = idxs.reduce((s, i) => s + this._patches[i].nx, 0) / idxs.length;
      idxs.forEach((i) => {
        this._patches[i].nx = clamp01(v);
      });
    } else if (mode === 'top') {
      const v = Math.min(...idxs.map((i) => this._patches[i].ny));
      idxs.forEach((i) => {
        this._patches[i].ny = clamp01(v);
      });
    } else if (mode === 'bottom') {
      const v = Math.max(...idxs.map((i) => this._patches[i].ny));
      idxs.forEach((i) => {
        this._patches[i].ny = clamp01(v);
      });
    } else if (mode === 'vcenter') {
      const v = idxs.reduce((s, i) => s + this._patches[i].ny, 0) / idxs.length;
      idxs.forEach((i) => {
        this._patches[i].ny = clamp01(v);
      });
    }
    idxs.forEach((i) => this.applyStrapClampToPatch(this._patches[i]));
    this.updateCanvasGeometry();
  }

  /**
   * @param {'h' | 'v'} axis
   */
  runTidyDistribute(axis) {
    const idxs = this.getTidyTargetsForActiveSide();
    if (idxs.length < 2) return;
    if (axis === 'h') {
      const sorted = [...idxs].sort((a, b) => this._patches[a].nx - this._patches[b].nx);
      const nxMin = this._patches[sorted[0]].nx;
      const nxMax = this._patches[sorted[sorted.length - 1]].nx;
      sorted.forEach((pi, j) => {
        const t = sorted.length === 1 ? 0 : j / (sorted.length - 1);
        this._patches[pi].nx = clamp01(nxMin + t * (nxMax - nxMin));
      });
    } else {
      const sorted = [...idxs].sort((a, b) => this._patches[a].ny - this._patches[b].ny);
      const nyMin = this._patches[sorted[0]].ny;
      const nyMax = this._patches[sorted[sorted.length - 1]].ny;
      sorted.forEach((pi, j) => {
        const t = sorted.length === 1 ? 0 : j / (sorted.length - 1);
        this._patches[pi].ny = clamp01(nyMin + t * (nyMax - nyMin));
      });
    }
    idxs.forEach((i) => this.applyStrapClampToPatch(this._patches[i]));
    this.updateCanvasGeometry();
  }

  runTidyStagger() {
    const idxs = this.getTidyTargetsForActiveSide();
    if (idxs.length < 2) return;
    const sorted = [...idxs].sort((a, b) => a - b);
    const step = 0.07;
    sorted.forEach((pi, j) => {
      this._patches[pi].nx = clamp01(this._patches[pi].nx + j * step * 0.55);
      this._patches[pi].ny = clamp01(this._patches[pi].ny + j * step);
      this.applyStrapClampToPatch(this._patches[pi]);
    });
    this.updateCanvasGeometry();
  }

  /**
   * Photo aspect of the live bag preview (width / height). Used so shuffle
   * stays on the same printable body the canvas uses.
   * @param {number} bagWIn
   * @param {number} bagHIn
   */
  getCanvasPhotoAspect(bagWIn, bagHIn) {
    const stage = this.querySelector('[data-ref="canvas-stage"]');
    const img = stage?.querySelector('.tay-builder-canvas__bag');
    if (img instanceof HTMLImageElement && img.naturalWidth > 0 && img.naturalHeight > 0) {
      return img.naturalWidth / img.naturalHeight;
    }
    return bagWIn / Math.max(bagHIn, 0.01);
  }

  /**
   * Keep a patch center off strap no-go zones for the selected bag.
   * @param {number} nx
   * @param {number} ny
   * @param {{ patch_width_in?: number, patch_height_in?: number, rot?: number } | null | undefined} patchLike
   * @returns {{ nx: number, ny: number }}
   */
  clampPatchPlacement(nx, ny, patchLike) {
    const cfg = this._config;
    const bagProduct = this.getBagProduct();
    const bagWIn = Number(bagProduct?.bag_width_in) || Number(cfg?.defaultBagWidthIn) || 22.5;
    const bagHIn = Number(bagProduct?.bag_height_in) || Number(cfg?.defaultBagHeightIn) || 14.5;
    const aspect = this.getCanvasPhotoAspect(bagWIn, bagHIn);
    const body =
      resolveBagBodyNormRect(bagProduct, bagWIn, bagHIn, aspect) || { x: 0, y: 0, w: 1, h: 1 };
    const half = patchHalfNorm(patchLike || {}, body, bagWIn, bagHIn, cfg);
    const straps = resolveStrapPolygons(bagProduct);
    const printable = parsePrintablePolygon(bagProduct?.printable_polygon);
    // custom.printable_polygon (e.g. a hat's front panel) keeps the whole patch inside its curved outline.
    if (printable) return clampCenterInsidePrintable(nx, ny, half, printable, straps);
    return clampCenterOffStraps(nx, ny, half, straps);
  }

  /**
   * @param {{ nx?: number, ny?: number, patch_width_in?: number, patch_height_in?: number, rot?: number } | null | undefined} patch
   */
  applyStrapClampToPatch(patch) {
    if (!patch) return;
    const cleared = this.clampPatchPlacement(
      typeof patch.nx === 'number' ? patch.nx : 0.5,
      typeof patch.ny === 'number' ? patch.ny : 0.5,
      patch
    );
    patch.nx = cleared.nx;
    patch.ny = cleared.ny;
  }

  /**
   * Draw / refresh strap no-go overlays on a canvas stage.
   * @param {HTMLElement} stage
   * @param {{ left: number, top: number, width: number, height: number }} bagRect
   */
  updateStrapZoneOverlay(stage, bagRect) {
    const overlay = stage.querySelector('[data-ref="strap-zones"]');
    if (!(overlay instanceof HTMLElement)) return;
    const straps = resolveStrapPolygons(this.getBagProduct());
    const printable = parsePrintablePolygon(this.getBagProduct()?.printable_polygon);
    if ((!straps.length && !printable) || !bagRect || bagRect.width <= 0 || bagRect.height <= 0) {
      overlay.innerHTML = '';
      overlay.hidden = true;
      return;
    }
    const sw = stage.clientWidth || 1;
    const sh = stage.clientHeight || 1;
    const label = escapeHtml(this._config?.labels?.strapNoGoLabel || 'No patches');
    overlay.hidden = false;
    let mask = '';
    if (printable) {
      // Shade everything outside the printable outline; dashed line marks where patches can go.
      const px = (p) => (((bagRect.left + p.x * bagRect.width) / sw) * 100).toFixed(2);
      const py = (p) => (((bagRect.top + p.y * bagRect.height) / sh) * 100).toFixed(2);
      const ring = printable.map((p, i) => `${i ? 'L' : 'M'}${px(p)} ${py(p)}`).join(' ') + ' Z';
      const outer = `M0 0 H100 V100 H0 Z`;
      mask = `<svg class="tay-builder-canvas__printable-mask" viewBox="0 0 100 100" preserveAspectRatio="none" style="position:absolute;inset:0;width:100%;height:100%;overflow:visible"><defs><pattern id="tay-nogo-hatch" width="2" height="2" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)"><rect width="1" height="2" fill="rgba(180,52,52,0.16)"></rect><rect x="1" width="1" height="2" fill="rgba(180,52,52,0.05)"></rect></pattern></defs><path d="${outer} ${ring}" fill="url(#tay-nogo-hatch)" fill-rule="evenodd"></path><path d="${ring}" fill="none" stroke="rgba(180,52,52,0.85)" stroke-width="1.5" stroke-dasharray="5 4" vector-effect="non-scaling-stroke"></path></svg>`;
    }
    if (straps.length > 6) {
      const px = (x) => (((bagRect.left + x * bagRect.width) / sw) * 100).toFixed(2);
      const py = (y) => (((bagRect.top + y * bagRect.height) / sh) * 100).toFixed(2);
      overlay.innerHTML = mask + strapZonesUnionSvg(straps, px, py);
      return;
    }
    overlay.innerHTML = mask + straps
      .map((ring) => {
        const bb = polygonBBox(ring);
        if (!bb) return '';
        const left = ((bagRect.left + bb.x * bagRect.width) / sw) * 100;
        const top = ((bagRect.top + bb.y * bagRect.height) / sh) * 100;
        const width = (bb.w * bagRect.width) / sw * 100;
        const height = (bb.h * bagRect.height) / sh * 100;
        if (!(width > 0.2) || !(height > 0.2)) return '';
        return `<div class="tay-builder-canvas__strap-zone" style="left:${left}%;top:${top}%;width:${width}%;height:${height}%;"><span class="tay-builder-canvas__strap-zone-label">${label}</span></div>`;
      })
      .join('');
  }

  /**
   * Scatter every patch on the active bag face. Requires two or more.
   * Positions stay on the printable body and avoid stacking when there is room.
   */
  runShufflePatches() {
    const idxs = this.getActiveSidePatchIndices();
    if (idxs.length < 2) return;

    const cfg = this._config;
    const bagProduct = this.getBagProduct();
    const bagWIn = Number(bagProduct?.bag_width_in) || Number(cfg?.defaultBagWidthIn) || 22.5;
    const bagHIn = Number(bagProduct?.bag_height_in) || Number(cfg?.defaultBagHeightIn) || 14.5;
    const aspect = this.getCanvasPhotoAspect(bagWIn, bagHIn);
    const body =
      resolveBagBodyNormRect(bagProduct, bagWIn, bagHIn, aspect) || { x: 0, y: 0, w: 1, h: 1 };
    const straps = resolveStrapPolygons(bagProduct);

    const order = [...idxs].sort((a, b) => {
      const ha = patchHalfNorm(this._patches[a], body, bagWIn, bagHIn, cfg);
      const hb = patchHalfNorm(this._patches[b], body, bagWIn, bagHIn, cfg);
      return hb.hx * hb.hy - ha.hx * ha.hy;
    });

    /** @type {Array<{ nx: number, ny: number, hx: number, hy: number }>} */
    const placed = [];
    order.forEach((pi) => {
      const p = this._patches[pi];
      if (!p) return;
      const half = patchHalfNorm(p, body, bagWIn, bagHIn, cfg);
      const picked = pickShuffledPatchCenter(half, body, placed, straps);
      const next = this.clampPatchPlacement(picked.nx, picked.ny, p);
      p.nx = clamp01(next.nx);
      p.ny = clamp01(next.ny);
      placed.push({ nx: p.nx, ny: p.ny, hx: half.hx, hy: half.hy });
    });

    this.updateCanvasGeometry();
    if (this._fullscreenOpen) this.updateFullscreenGeometry();
    this.persistDraft();
  }

  /**
   * @param {any} cfg
   * @param {Record<string, string>} labels
   * @param {string} sectionId
   * @param {string} [currency]
   */
  buildPatchCardsHtml(cfg, labels, sectionId, currency) {
    const cur = currency || cfg.currency || 'USD';
    const onBagByProduct = {};
    this._patches.forEach((pl) => {
      const pid = pl.productId;
      if (pid == null) return;
      onBagByProduct[pid] = (onBagByProduct[pid] || 0) + 1;
    });

    return cfg.patches
      .map((/** @type {any} */ p) => {
        const hasAvailable = p.variants.some((/** @type {any} */ v) => v.available);
        const defaultV = firstAvailableVariant(p.variants);
        const thumbSrc = patchCatalogImageUrl(p, defaultV);
        const imgHtml = thumbSrc
          ? `<img data-patch-preview-img src="${escapeHtml(thumbSrc)}" alt="" loading="lazy" width="200" height="200">`
          : `<div data-patch-preview-placeholder aria-hidden="true" style="width:100%;height:100%;min-height:100px;background:rgba(26,36,32,0.06);border-radius:8px;"></div>`;

        let variantBlock = '';
        if (p.variants.length > 1) {
          const opts = p.variants
            .map((/** @type {any} */ v) => {
              const disabled = v.available ? '' : ' disabled';
              const sel = defaultV && v.id === defaultV.id ? ' selected' : '';
              return `<option value="${v.id}"${disabled}${sel}>${escapeHtml(v.title)}${v.available ? '' : ' — sold out'}</option>`;
            })
            .join('');
          variantBlock = `
        <div class="tay-builder-patch-variant">
          <label for="patch-var-${p.id}-${sectionId}">${escapeHtml(labels.patchOption)}</label>
          <select id="patch-var-${p.id}-${sectionId}" class="tay-builder-field-select" data-patch-variant-select data-product-id="${p.id}">
            ${opts}
          </select>
        </div>`;
        }

        const cardDisabled = !hasAvailable ? ' tay-builder-patch-card--disabled' : '';
        const bagCount = onBagByProduct[p.id] || 0;
        const badge =
          bagCount > 0
            ? `<span class="tay-builder-patch-card__badge" aria-label="${bagCount} on bag">${bagCount}</span>`
            : '';
        const patchPreOrder = readPreOrder(p);
        const statusChip = statusChipHtml(p, defaultV, {
          soldOut: labels.patchUnavailable || 'Sold out',
          lowStock: labels.lowStock || 'Only [count] left',
        });

        return `
      <div class="tay-builder-patch-card tay-builder-patch-card--tap${cardDisabled}" data-patch-product-id="${p.id}">
        <div class="tay-builder-patch-image">${imgHtml}${badge}${statusChip ? `<span class="bbs-product-card__flags">${statusChip}</span>` : ''}</div>
        <p class="tay-builder-patch-title">${escapeHtml(p.title)}</p>
        ${
          defaultV && Number.isFinite(Number(defaultV.price))
            ? `<p class="tay-builder-patch-price" data-patch-price>${escapeHtml(formatMoneyCents(defaultV.price, cur))}</p>`
            : ''
        }
        ${preOrderDetailsHtml(patchPreOrder)}
        ${variantBlock}
        <button type="button" class="tay-btn tay-btn--secondary tay-builder-patch-add" data-action="add-patch-product" data-product-id="${p.id}">
          ${escapeHtml(labels.addPatch)}
        </button>
      </div>`;
      })
      .join('');
  }

  /**
   * @param {HTMLElement} card
   */
  syncPatchCard(card) {
    const cfg = this._config;
    if (!cfg) return;

    const pid = Number(card.dataset.patchProductId);
    const product = cfg.patches.find((/** @type {any} */ p) => p.id === pid);
    if (!product) return;

    const wrap = card.querySelector('.tay-builder-patch-image');
    const select = card.querySelector('[data-patch-variant-select]');
    const btn = card.querySelector('[data-action="add-patch-product"]');

    let variant = null;
    if (select instanceof HTMLSelectElement) {
      variant = product.variants.find((/** @type {any} */ v) => v.id === Number(select.value)) ?? null;
    } else {
      variant = firstAvailableVariant(product.variants);
    }

    const url = patchCatalogImageUrl(product, variant);
    if (wrap && url) {
      let img = wrap.querySelector('[data-patch-preview-img]');
      const placeholder = wrap.querySelector('[data-patch-preview-placeholder]');
      if (!img) {
        if (placeholder) placeholder.remove();
        const imgEl = document.createElement('img');
        imgEl.setAttribute('data-patch-preview-img', '');
        imgEl.setAttribute('src', url);
        imgEl.setAttribute('alt', '');
        imgEl.setAttribute('loading', 'lazy');
        imgEl.setAttribute('width', '200');
        imgEl.setAttribute('height', '200');
        wrap.insertBefore(imgEl, wrap.firstChild);
      } else {
        img.setAttribute('src', url);
      }
    }

    if (wrap instanceof HTMLElement) {
      const labels = cfg.labels || {};
      const chipHtml = statusChipHtml(product, variant, {
        soldOut: labels.patchUnavailable || 'Sold out',
        lowStock: labels.lowStock || 'Only {{ count }} left',
      });
      let flags = wrap.querySelector('.bbs-product-card__flags');
      if (chipHtml) {
        if (!(flags instanceof HTMLElement)) {
          flags = document.createElement('span');
          flags.className = 'bbs-product-card__flags';
          wrap.appendChild(flags);
        }
        flags.innerHTML = chipHtml;
      } else if (flags) {
        flags.remove();
      }
    }

    if (btn instanceof HTMLButtonElement) {
      const anyAvailable = product.variants.some((/** @type {any} */ v) => v.available);
      const blocked = !anyAvailable || (variant ? !variant.available : true);
      btn.disabled = false;
      if (blocked) btn.setAttribute('aria-disabled', 'true');
      else btn.removeAttribute('aria-disabled');
    }

    const priceEl = card.querySelector('[data-patch-price]');
    const currency = cfg.currency || 'USD';
    if (priceEl instanceof HTMLElement && variant && Number.isFinite(Number(variant.price))) {
      priceEl.textContent = formatMoneyCents(variant.price, currency);
    }
  }

  /**
   * @param {Record<string, string>} labels
   */
  buildCanvasToolbarHtml(labels) {
    const fsLbl = escapeHtml(labels.fullscreenPreview || 'Full screen preview');
    const undoLbl = escapeHtml(labels.undo || 'Undo');
    const shuffleLbl = escapeHtml(labels.tidyShuffle || 'Shuffle');
    const canUndo = this._undoStack.length > 0;
    const canShuffle = this.getActiveSidePatchIndices().length >= 2;
    return `
      <div class="tay-builder-canvas-toolbar tay-builder-canvas-toolbar--start">
        <button type="button" class="tay-builder-canvas-toolbar__btn tay-builder-canvas-toolbar__btn--label" data-action="shuffle-patches"${canShuffle ? '' : ' disabled'} aria-label="${shuffleLbl}" title="${shuffleLbl}">
          ${shuffleLbl}
        </button>
      </div>
      <div class="tay-builder-canvas-toolbar tay-builder-canvas-toolbar--end">
        <button type="button" class="tay-builder-canvas-toolbar__btn tay-builder-canvas-toolbar__btn--label" data-action="undo-patch"${canUndo ? '' : ' disabled'} aria-label="${undoLbl}" title="${undoLbl}">
          ${undoLbl}
        </button>
        <button type="button" class="tay-builder-canvas-toolbar__btn" data-action="open-fullscreen-preview" aria-label="${fsLbl}" title="${fsLbl}">
          <span class="tay-builder-canvas-toolbar__icon" aria-hidden="true"></span>
        </button>
      </div>`;
  }

  /**
   * @param {string | null} previewImage
   * @param {Record<string, string>} labels
   * @param {'bag' | 'design' | 'review'} mode
   * @param {string} [bagFaceFooterHtml]
   */
  buildFullscreenPreviewHtml(labels) {
    const previewImage = this.getCanvasBagPreviewImage(this._step === 1, { hiRes: true });
    if (!previewImage) return '';
    const open = this._fullscreenOpen;
    const closeLbl = escapeHtml(labels.fullscreenClose || labels.closeSheet || 'Close');
    const title = escapeHtml(labels.fullscreenPreview || labels.previewHeading || 'Preview');
    const zoomPct = Math.round(this._fsZoom * 100);
    const stageHtml = this.buildCanvasHtml(previewImage, labels, 'review', '', {
      stageRef: 'fs-canvas-stage',
      showHint: false,
      showToolbar: false,
      stageOnly: true,
    });
    const faceToggleHtml = this.hasBagBackPreview() ? this.buildBagFaceToggleHtml(labels) : '';
    const transformStyle = `transform: translate(${this._fsPanX}px, ${this._fsPanY}px) scale(${this._fsZoom});`;
    const footerControls = `
          <footer class="tay-builder-fullscreen__foot">
            ${faceToggleHtml ? `<div class="tay-builder-fullscreen__faces">${faceToggleHtml}</div>` : ''}
            <div class="tay-builder-fullscreen__zoom" aria-label="${escapeHtml(labels.zoomControls || 'Zoom')}">
              <button type="button" class="tay-builder-fullscreen__zoom-btn" data-action="fs-zoom-out" aria-label="${escapeHtml(labels.zoomOut || 'Zoom out')}">−</button>
              <span class="tay-builder-fullscreen__zoom-label" data-ref="fs-zoom-label">${zoomPct}%</span>
              <button type="button" class="tay-builder-fullscreen__zoom-btn" data-action="fs-zoom-in" aria-label="${escapeHtml(labels.zoomIn || 'Zoom in')}">+</button>
            </div>
          </footer>`;
    return `
      <div class="tay-builder-fullscreen${open ? ' is-open' : ''}" data-ref="fullscreen" aria-hidden="${open ? 'false' : 'true'}">
        <button type="button" class="tay-builder-fullscreen__backdrop" data-action="close-fullscreen-preview" aria-label="${closeLbl}"></button>
        <div class="tay-builder-fullscreen__panel" role="dialog" aria-modal="true" aria-labelledby="tay-fullscreen-title">
          <header class="tay-builder-fullscreen__head">
            <h2 class="tay-builder-fullscreen__title" id="tay-fullscreen-title">${title}</h2>
            <button type="button" class="tay-builder-fullscreen__close" data-action="close-fullscreen-preview" aria-label="${closeLbl}">×</button>
          </header>
          <div class="tay-builder-fullscreen__viewport" data-ref="fs-viewport">
            <div class="tay-builder-fullscreen__transform" data-ref="fs-transform" style="${transformStyle}">
              ${stageHtml}
            </div>
          </div>
          ${footerControls}
        </div>
      </div>`;
  }

  applyFullscreenTransform() {
    const el = this.querySelector('[data-ref="fs-transform"]');
    const label = this.querySelector('[data-ref="fs-zoom-label"]');
    if (el instanceof HTMLElement) {
      el.style.transform = `translate(${this._fsPanX}px, ${this._fsPanY}px) scale(${this._fsZoom})`;
    }
    if (label) label.textContent = `${Math.round(this._fsZoom * 100)}%`;
  }

  /**
   * @param {number} delta
   */
  adjustFullscreenZoom(delta) {
    const next = Math.min(3, Math.max(1, this._fsZoom + delta));
    if (next === this._fsZoom) return;
    this._fsZoom = next;
    if (this._fsZoom <= 1) {
      this._fsPanX = 0;
      this._fsPanY = 0;
    }
    this.applyFullscreenTransform();
  }

  bindFullscreenInteractions() {
    const viewport = this.querySelector('[data-ref="fs-viewport"]');
    if (!(viewport instanceof HTMLElement)) return;

    let panning = false;
    let startX = 0;
    let startY = 0;
    let startPanX = 0;
    let startPanY = 0;

    const onPointerDown = (/** @type {PointerEvent} */ e) => {
      if (this._fsZoom <= 1) return;
      if (e.target instanceof Element && e.target.closest('.tay-builder-canvas-patch__handles')) return;
      panning = true;
      startX = e.clientX;
      startY = e.clientY;
      startPanX = this._fsPanX;
      startPanY = this._fsPanY;
      viewport.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (/** @type {PointerEvent} */ e) => {
      if (!panning) return;
      this._fsPanX = startPanX + (e.clientX - startX);
      this._fsPanY = startPanY + (e.clientY - startY);
      this.applyFullscreenTransform();
    };

    const endPan = (/** @type {PointerEvent} */ e) => {
      if (!panning) return;
      panning = false;
      try {
        viewport.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    };

    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove);
    viewport.addEventListener('pointerup', endPan);
    viewport.addEventListener('pointercancel', endPan);

    viewport.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        const delta = e.deltaY < 0 ? 0.12 : -0.12;
        this.adjustFullscreenZoom(delta);
      },
      { passive: false }
    );
  }

  updateFullscreenGeometry() {
    const stage = this.querySelector('[data-ref="fs-canvas-stage"]');
    const img = stage?.querySelector('.tay-builder-canvas__bag');
    if (!(stage instanceof HTMLElement) || !(img instanceof HTMLImageElement)) return;

    const sw = stage.clientWidth;
    const sh = stage.clientHeight;
    const nw = img.naturalWidth || sw;
    const nh = img.naturalHeight || sh;
    const bag = computeObjectFitContainRect(sw, sh, nw, nh);

    const cfg = this._config;
    const bagProduct = this.getBagProduct();
    const bagWIn = Number(bagProduct?.bag_width_in) || Number(cfg?.defaultBagWidthIn) || 22.5;
    const bagHIn = Number(bagProduct?.bag_height_in) || Number(cfg?.defaultBagHeightIn) || 14.5;

    this.updateStrapZoneOverlay(stage, bag);

    this._patches.forEach((p, i) => {
      if (this.hasBagBackPreview() && patchSideOf(p) !== this._bagSide) return;
      const el = stage.querySelector(`[data-patch-index="${i}"]`);
      if (!(el instanceof HTMLElement)) return;

      layoutOnBagPatchElement(el, p, bag, bagWIn, bagHIn, sw, sh, cfg, bagProduct);
    });
    updatePatchActionPills(stage);
  }

  /**
   * @param {string | null} previewImage
   * @param {Record<string, string>} labels
   * @param {'bag' | 'design' | 'review'} mode
   * @param {string} [bagFaceFooterHtml] When set, rendered in a bar below the bag stage (same preview card).
   * @param {{ stageRef?: string, showHint?: boolean, showToolbar?: boolean, stageOnly?: boolean }} [options]
   */
  buildCanvasHtml(previewImage, labels, mode, bagFaceFooterHtml = '', options = {}) {
    const stageRef = options.stageRef || 'canvas-stage';
    const showHint = options.showHint !== false;
    const showToolbar = options.showToolbar !== false;
    const stageOnly = options.stageOnly === true;
    const showPatches = mode !== 'bag';
    const interactive = mode === 'design';
    const patches = this._patches;
    const cw = escapeHtml(labels.rotatePatchCw || labels.rotatePatch || 'Rotate clockwise');
    const removeLbl = escapeHtml(labels.removePatchOnBag || labels.remove || 'Remove patch');
    const sel = this._selectedPatchIndex;

    const layerOrder = patches.map((p, index) => ({
      index,
      z: typeof p.stackOrder === 'number' ? p.stackOrder : index,
    }));
    layerOrder.sort((a, b) => (a.z === b.z ? a.index - b.index : a.z - b.z));

    const layers =
      !showPatches || patches.length === 0 || !previewImage
        ? ''
        : layerOrder
            .filter(({ index }) => {
              const p = patches[index];
              if (!this.hasBagBackPreview()) return true;
              return patchSideOf(p) === this._bagSide;
            })
            .map(({ index }) => {
              const p = patches[index];
              const rawUrl = p.image || '';
              const processKey = onBagPatchSourceUrl(rawUrl);
              const cachedOnBag = processKey ? tayPatchArtworkCache.ready.get(processKey) : '';
              const fallbackUrl =
                rawUrl && stageRef === 'fs-canvas-stage'
                  ? shopifyImageUrlWithWidth(rawUrl, TAY_FS_PATCH_IMAGE_WIDTH)
                  : rawUrl;
              const src = cachedOnBag || fallbackUrl;
              const escapedSrc = src ? escapeHtml(src) : '';
              const escapedRaw = rawUrl ? escapeHtml(rawUrl) : '';
              const rot = normalizeRotationDeg(p.rot);
              const innerVisual = escapedSrc
                ? `<img src="${escapedSrc}" data-patch-src="${escapedRaw}" alt="" draggable="false" class="tay-builder-canvas-patch__img">`
                : `<div class="tay-builder-canvas-patch__ph"></div>`;
              const mediaWrap = `<div class="tay-builder-canvas-patch__media">${innerVisual}</div>`;
              const selectedClass = interactive && sel === index ? ' is-selected' : '';
              const handles = interactive
                ? `
        <div class="tay-builder-canvas-patch__handles" role="toolbar" aria-label="${removeLbl}, ${cw}">
          <button type="button" class="tay-builder-canvas-patch__handle tay-builder-canvas-patch__handle--remove" data-action="remove-canvas-patch" data-patch-index="${index}" aria-label="${removeLbl}" title="${removeLbl}">×</button>
          <button type="button" class="tay-builder-canvas-patch__handle tay-builder-canvas-patch__handle--cw" data-action="rotate-patch-cw" data-patch-index="${index}" aria-label="${cw}" title="${cw}">↻</button>
        </div>`
                : '';
              return `
      <div
        class="tay-builder-canvas-patch tay-builder-canvas-patch--scaled${selectedClass}"
        data-patch-index="${index}"
        style="left:50%;top:50%;--tay-patch-rot:${rot}deg;--tay-pill-clear:0px;"
      >
        ${mediaWrap}
        ${handles}
      </div>`;
            })
            .join('');

    let hintKey = 'canvasHintStep1';
    if (mode === 'design') hintKey = 'canvasHintStep2';
    if (mode === 'review') hintKey = 'canvasHintReview';
    let hintText = labels[hintKey] || labels.canvasHint || '';
    if (
      mode === 'design' &&
      labels.canvasHintMobile &&
      typeof window !== 'undefined' &&
      window.matchMedia(TAY_MOBILE_MEDIA).matches
    ) {
      hintText = labels.canvasHintMobile;
    }
    const hint =
      showHint && hintText
        ? `<p class="tay-builder-canvas__hint tay-builder-canvas__hint--${mode}">${escapeHtml(hintText)}</p>`
        : '';

    if (!previewImage) {
      return `
        ${hint}
        <div class="tay-builder-canvas tay-builder-canvas--empty">
          <span class="tay-builder-canvas__placeholder">${escapeHtml(labels.chooseBag)}</span>
        </div>`;
    }

    const roClass = interactive ? '' : ' tay-builder-canvas--readonly';

    const toolbarHtml =
      showToolbar && previewImage && (mode === 'design' || mode === 'review')
        ? this.buildCanvasToolbarHtml(labels)
        : '';

    const facePatchCount = this._patches.filter((p) => patchSideOf(p) === this._bagSide).length;
    const emptyFace =
      mode === 'design' && Boolean(previewImage) && facePatchCount === 0
        ? `<div class="tay-builder-canvas__empty-overlay">
            <p>${escapeHtml(labels.canvasEmptyFace || 'Add a patch to start designing this side.')}</p>
            <button type="button" class="tay-btn tay-btn--primary" data-action="open-patch-sheet">${escapeHtml(labels.addPatchesBar || labels.addPatch || 'Add patches')}</button>
          </div>`
        : '';

    const stageBlock = `
        <div class="tay-builder-canvas__stage-wrap">
          ${toolbarHtml}
          ${emptyFace}
          <div class="tay-builder-canvas__stage" data-ref="${escapeHtml(stageRef)}" tabindex="-1">
          <img class="tay-builder-canvas__bag" src="${escapeHtml(previewImage)}" alt="" width="600" height="750" draggable="false"${stageRef === 'fs-canvas-stage' ? ' decoding="async" fetchpriority="high"' : ''}>
          <div class="tay-builder-canvas__strap-zones" data-ref="strap-zones" aria-hidden="true" hidden></div>
          <div class="tay-builder-canvas__layers" aria-hidden="true">${layers}</div>
          </div>
        </div>`;

    const body =
      bagFaceFooterHtml !== ''
        ? `
        <div class="tay-builder-canvas__bag-area">
          ${stageBlock}
          <div class="tay-builder-canvas__bag-footer">
            ${bagFaceFooterHtml}
          </div>
        </div>`
        : stageBlock;

    if (stageOnly) {
      return `<div class="tay-builder-fullscreen-stage${roClass}">${stageBlock}</div>`;
    }

    return `
      ${hint}
      <div class="tay-builder-canvas${roClass}" data-ref="canvas-root">
        ${body}
      </div>`;
  }

  /**
   * @param {Record<string, string>} labels
   */
  buildBagFaceToggleHtml(labels) {
    if (!this.hasBagBackPreview()) return '';
    this.ensureBagSideValid();
    const faces = this.getAvailableBagFaces();
    const aria = escapeHtml(labels.bagFaceToggleAria || 'Bag side');
    const hint = labels.bagFaceHint
      ? `<p class="tay-builder-bag-face__hint">${escapeHtml(labels.bagFaceHint)}</p>`
      : '';
    const buttons = faces
      .map((side, index) => {
        const lbl = escapeHtml(bagFaceLabel(side, labels));
        const sel = this._bagSide === side ? ' is-selected' : '';
        const ariaSel = this._bagSide === side ? 'true' : 'false';
        const sep =
          index > 0 ? '<span class="tay-builder-bag-face__sep" aria-hidden="true">|</span>' : '';
        return `${sep}
        <button type="button" role="tab" class="tay-builder-bag-face__btn${sel}" data-action="bag-side" data-side="${side}" aria-selected="${ariaSel}">${lbl}</button>`;
      })
      .join('');
    return `
    <div class="tay-builder-bag-face">
      <div class="tay-builder-bag-face__toggle" role="tablist" aria-label="${aria}">
        ${buttons}
      </div>
      ${hint}
    </div>`;
  }

  /**
   * @param {Record<string, string>} labels
   */
  buildProgressHtml(labels) {
    const s = this._step;
    const t1 = escapeHtml(labels.progressBag || 'Bag');
    const t2 = escapeHtml(labels.progressPatches || 'Patches');
    const t3 = escapeHtml(labels.progressReview || 'Review');
    return `
      <nav class="tay-builder-progress" aria-label="${escapeHtml(labels.progressAria || 'Customizer steps')}">
        <ol class="tay-builder-progress__track">
          <li class="tay-builder-progress__step ${s >= 1 ? 'is-reached' : ''} ${s === 1 ? 'is-current' : ''}" data-step-dot="1">
            <span class="tay-builder-progress__num">1</span><span class="tay-builder-progress__label">${t1}</span>
          </li>
          <li class="tay-builder-progress__step ${s >= 2 ? 'is-reached' : ''} ${s === 2 ? 'is-current' : ''}" data-step-dot="2">
            <span class="tay-builder-progress__num">2</span><span class="tay-builder-progress__label">${t2}</span>
          </li>
          <li class="tay-builder-progress__step ${s >= 3 ? 'is-reached' : ''} ${s === 3 ? 'is-current' : ''}" data-step-dot="3">
            <span class="tay-builder-progress__num">3</span><span class="tay-builder-progress__label">${t3}</span>
          </li>
        </ol>
      </nav>`;
  }

  /**
   * @param {any} bagProduct
   * @param {Record<string, string>} labels
   * @param {string} sectionId
   */
  buildBagVariantSwatchesHtml(bagProduct, labels, sectionId) {
    const sid = escapeHtml(sectionId);
    const variants = bagProduct.variants || [];
    const swatches = variants
      .map((/** @type {any} */ v) => {
        const selected = v.id === this._bagVariantId ? ' is-selected' : '';
        const blockedAttrs = v.available ? '' : blockedActionAttrs(false);
        const thumb = v.image
          ? `<img src="${escapeHtml(v.image)}" alt="" loading="lazy" width="96" height="96">`
          : `<span class="tay-builder-bag-variant-fallback">${escapeHtml(String(v.title || '?').slice(0, 2))}</span>`;
        return `
          <button type="button" class="tay-builder-bag-variant-swatch${selected}"
            data-action="pick-bag-variant" data-variant-id="${v.id}" id="tay-bag-var-${v.id}-${sid}"
            ${blockedAttrs} title="${escapeHtml(v.title)}">
            ${thumb}
          </button>`;
      })
      .join('');

    return `
      <div class="tay-builder-bag-tones">
        <p class="tay-builder-bag-tones-label">${escapeHtml(labels.chooseVariant)}</p>
        <div class="tay-builder-bag-variant-swatches custom-scrollbar" role="listbox" aria-label="${escapeHtml(labels.chooseVariant)}">
          ${swatches}
        </div>
      </div>`;
  }

  /**
   * @param {any} cfg
   * @param {Record<string, string>} labels
   */
  buildPatchLibraryChromeHtml(cfg, labels) {
    const cats = computePatchCategoryTags(cfg);
    const hasCats = cats.length > 0;
    const q = escapeHtml(this._patchLibraryQuery || '');
    const ph = escapeHtml(labels.patchSearchPlaceholder || 'Search patches');
    const searchLbl = escapeHtml(labels.patchSearchPlaceholder || 'Search patches');
    const allLbl = escapeHtml(labels.patchCategoryAll || 'All');
    const chips = [
      `<button type="button" class="tay-builder-cat-chip${this._patchLibraryCategory === 'all' ? ' is-selected' : ''}" data-action="patch-filter-cat" data-category="all">${allLbl}</button>`,
    ];
    cats.forEach((t) => {
      const sel = this._patchLibraryCategory === t ? ' is-selected' : '';
      chips.push(
        `<button type="button" class="tay-builder-cat-chip${sel}" data-action="patch-filter-cat" data-category="${escapeHtml(t)}">${escapeHtml(titleCaseTag(t))}</button>`
      );
    });
    return `
      <div class="tay-builder-library-tools">
        <div class="tay-builder-library-search">
          <label class="tay-builder-sr-only" for="tay-patch-search">${searchLbl}</label>
          <input type="search" id="tay-patch-search" class="tay-builder-library-search__input" data-ref="patch-search" placeholder="${ph}" autocomplete="off" value="${q}" />
        </div>
        ${
          hasCats
            ? `<div class="tay-builder-cat-row custom-scrollbar" role="tablist">${chips.join('')}</div>`
            : ''
        }
      </div>`;
  }

  /**
   * @param {Record<string, string>} labels
   * @param {{ plain?: boolean }} [opts]
   */
  buildTidyToolbarHtml(labels, opts = {}) {
    const anyTidy = this.getTidyTargets();
    const targets = this.getTidyTargetsForActiveSide();
    const has = targets.length > 0;
    const pair = targets.length >= 2;
    const disDelete = anyTidy.length === 0 ? blockedActionAttrs(false) : '';
    const dis = blockedActionAttrs(has);
    const disPair = blockedActionAttrs(pair);
    const tidyLbl = escapeHtml(labels.tidyHeading || 'Tidy patches');
    const hint = escapeHtml(labels.tidySelectHint || '');
    const alignLbl = escapeHtml(labels.tidyAlign || 'Align');
    const spaceLbl = escapeHtml(labels.tidySpace || 'Space');

    const btn = (mode, text, extraCls = '') =>
      `<button type="button" class="tay-builder-tidy-btn${extraCls}" data-action="tidy-align" data-tidy-mode="${mode}"${dis}>${escapeHtml(text)}</button>`;

    const dbtn = (axis, text, extraCls = '') =>
      `<button type="button" class="tay-builder-tidy-btn${extraCls}" data-action="tidy-distribute" data-tidy-axis="${axis}"${disPair}>${escapeHtml(text)}</button>`;

    const tidyInner = `
        ${hint ? `<p class="tay-builder-tidy__hint">${hint}</p>` : ''}
        <div class="tay-builder-tidy__row">
          <button type="button" class="tay-btn tay-btn--secondary tay-builder-tidy__mini" data-action="tidy-select-all">${escapeHtml(labels.tidySelectAll || 'Select all')}</button>
          <button type="button" class="tay-btn tay-btn--secondary tay-builder-tidy__mini" data-action="tidy-clear">${escapeHtml(labels.tidyClear || 'Clear')}</button>
        </div>
        <p class="tay-builder-tidy__sub">${alignLbl}</p>
        <div class="tay-builder-tidy__btns" role="group">
          ${btn('left', labels.alignLeft || 'Left')}
          ${btn('hcenter', labels.alignHCenter || 'Center')}
          ${btn('right', labels.alignRight || 'Right')}
          ${btn('top', labels.alignTop || 'Top')}
          ${btn('vcenter', labels.alignVCenter || 'Middle')}
          ${btn('bottom', labels.alignBottom || 'Bottom')}
        </div>
        <p class="tay-builder-tidy__sub">${spaceLbl}</p>
        <div class="tay-builder-tidy__btns" role="group">
          ${dbtn('h', labels.distributeH || 'Even across')}
          ${dbtn('v', labels.distributeV || 'Even down')}
          <button type="button" class="tay-builder-tidy-btn" data-action="tidy-stagger"${disPair}>${escapeHtml(labels.tidyStagger || 'Stagger')}</button>
        </div>
        <button type="button" class="tay-btn tay-btn--danger-outline tay-btn--full tay-builder-tidy__delete" data-action="delete-tidy-patches"${disDelete}>
          ${escapeHtml(labels.deleteTidyPatches || 'Remove selected')}
        </button>`;

    if (opts.plain) {
      return `<div class="tay-builder-tidy">${tidyInner}</div>`;
    }

    return `
      <details class="tay-builder-tidy-details"${this._tidyDetailsOpen ? ' open' : ''}>
        <summary class="tay-builder-tidy-details__summary">${tidyLbl}</summary>
        <div class="tay-builder-tidy">
          ${tidyInner}
        </div>
      </details>`;
  }

  /**
   * @param {Record<string, string>} labels
   */
  buildPatchStripHtml(labels) {
    if (this._patches.length === 0) {
      return `
        <div class="tay-builder-strip-empty tay-builder-strip-empty--card" role="status">
          <p class="tay-builder-strip-empty__title">${escapeHtml(labels.stripEmptyTitle || 'No patches on this bag yet')}</p>
          <p class="tay-builder-strip-empty__body">${escapeHtml(labels.stripEmpty || 'Add patches from the library, then drag them into place.')}</p>
        </div>`;
    }
    const toggleLbl = escapeHtml(labels.toggleTidyPatch || 'Include in tidy tools');
    const chips = this._patches
      .map((p, index) => {
        const thumb = p.image
          ? `<img src="${escapeHtml(p.image)}" alt="">`
          : `<span class="tay-builder-strip-chip__ph"></span>`;
        const sel = this._selectedPatchIndex === index ? ' is-selected' : '';
        const inTidy = this._tidyIndices.has(index);
        const tidyCls = inTidy ? ' is-on' : '';
        const vn =
          p.variantTitle && p.variantTitle !== 'Default Title'
            ? `<span class="tay-builder-strip-chip__sub">${escapeHtml(p.variantTitle)}</span>`
            : '';
        const sideAbbr = this.hasBagBackPreview()
          ? escapeHtml(bagFaceLabel(patchSideOf(p), labels))
          : '';
        const sideBadge = sideAbbr
          ? `<span class="tay-builder-strip-chip__side">${sideAbbr}</span>`
          : '';
        return `
          <div class="tay-builder-strip-chip${sel}">
            <button type="button" class="tay-builder-strip-chip__main" data-action="select-patch" data-index="${index}" aria-pressed="${this._selectedPatchIndex === index ? 'true' : 'false'}">
              <span class="tay-builder-strip-chip__thumb">${thumb}</span>
              <span class="tay-builder-strip-chip__meta">
                <span class="tay-builder-strip-chip__title-row">
                  <span class="tay-builder-strip-chip__title">${escapeHtml(p.productTitle)}</span>
                  ${sideBadge}
                </span>
                ${vn}
              </span>
            </button>
            <button type="button" class="tay-builder-strip-chip__tidy${tidyCls}" data-action="toggle-tidy-patch" data-index="${index}" aria-label="${toggleLbl}" aria-pressed="${inTidy ? 'true' : 'false'}" title="${toggleLbl}">✓</button>
            <button type="button" class="tay-builder-strip-chip__remove" data-action="remove-patch" data-index="${index}" aria-label="${escapeHtml(labels.remove)}">×</button>
          </div>`;
      })
      .join('');
    return `
      <div class="tay-builder-strip-wrap">
        <div class="tay-builder-strip-head">
          <p class="tay-builder-panel-title tay-builder-panel-title--inline">${escapeHtml(labels.stripHeading || labels.yourPatches)}</p>
          <div class="tay-builder-strip-head__actions">
            <button type="button" class="tay-builder-strip-head__link" data-action="tidy-select-all">${escapeHtml(labels.tidySelectAll || 'Select all')}</button>
            <button type="button" class="tay-builder-strip-head__link" data-action="tidy-clear">${escapeHtml(labels.tidyClear || 'Clear')}</button>
          </div>
        </div>
        <div class="tay-builder-strip custom-scrollbar" role="tablist">${chips}</div>
      </div>`;
  }

  /**
   * @param {Record<string, string>} labels
   * @param {{ plain?: boolean }} [opts]
   */
  buildPatchEditorHtml(labels, opts = {}) {
    const plain = Boolean(opts.plain);
    const idx = this._selectedPatchIndex;
    const patch = idx != null ? this._patches[idx] : null;
    const degSuffix = labels.degreeSuffix || '°';

    if (this._patches.length === 0 && !plain) {
      return '';
    }

    const designLbl = escapeHtml(labels.stepPatches || labels.editPatchHeading || 'Design your patches');
    const idleHtml = `
        <div class="tay-builder-editor tay-builder-editor--idle${plain ? '' : ' tay-builder-editor--in-details'}">
          <p class="tay-builder-editor__hint">${escapeHtml(labels.selectPatchHint || 'Tap a patch on the bag or in the list to edit it.')}</p>
        </div>`;

    if (!patch) {
      if (plain) return idleHtml;
      return `
      <details class="tay-builder-editor-details"${this._editorDetailsOpen ? ' open' : ''}>
        <summary class="tay-builder-editor-details__summary">
          <span class="tay-builder-editor-details__label">${designLbl}</span>
        </summary>
        ${idleHtml}
      </details>`;
    }

    const rot = normalizeRotationDeg(patch.rot);
    const title =
      patch.variantTitle && patch.variantTitle !== 'Default Title'
        ? `${patch.productTitle} — ${patch.variantTitle}`
        : patch.productTitle;

    const adjustLbl = escapeHtml(labels.stepPatches || labels.editPatchHeading || 'Design your patches');
    const sliderId = `tay-rot-slider-${idx}`;

    const editorControls = `
        <p class="tay-builder-editor__title">${escapeHtml(title)}</p>

        <p class="tay-builder-editor__label">${escapeHtml(labels.positionLabel || 'Position')}</p>
        <div class="tay-builder-nudge" role="group" aria-label="${escapeHtml(labels.positionLabel || 'Position')}">
          <button type="button" class="tay-builder-nudge__btn tay-builder-nudge__placeholder" disabled aria-hidden="true"></button>
          <button type="button" class="tay-builder-nudge__btn" data-action="nudge-patch" data-dx="0" data-dy="${-TAY_NUDGE_NORM}" aria-label="${escapeHtml(labels.nudgeUp || 'Nudge up')}">↑</button>
          <button type="button" class="tay-builder-nudge__btn tay-builder-nudge__placeholder" disabled aria-hidden="true"></button>
          <button type="button" class="tay-builder-nudge__btn" data-action="nudge-patch" data-dx="${-TAY_NUDGE_NORM}" data-dy="0" aria-label="${escapeHtml(labels.nudgeLeft || 'Nudge left')}">←</button>
          <button type="button" class="tay-builder-nudge__btn tay-builder-nudge__center" disabled aria-hidden="true" title="">·</button>
          <button type="button" class="tay-builder-nudge__btn" data-action="nudge-patch" data-dx="${TAY_NUDGE_NORM}" data-dy="0" aria-label="${escapeHtml(labels.nudgeRight || 'Nudge right')}">→</button>
          <button type="button" class="tay-builder-nudge__btn tay-builder-nudge__placeholder" disabled aria-hidden="true"></button>
          <button type="button" class="tay-builder-nudge__btn" data-action="nudge-patch" data-dx="0" data-dy="${TAY_NUDGE_NORM}" aria-label="${escapeHtml(labels.nudgeDown || 'Nudge down')}">↓</button>
          <button type="button" class="tay-builder-nudge__btn tay-builder-nudge__placeholder" disabled aria-hidden="true"></button>
        </div>

        <div class="tay-builder-rotate-row">
          <label class="tay-builder-editor__label" for="${sliderId}">${escapeHtml(labels.rotationLabel || 'Rotation')}</label>
          <span class="tay-builder-rotate-row__value" data-ref="rotation-degree">${rot}${escapeHtml(degSuffix)}</span>
        </div>
        <input type="range" id="${sliderId}" class="tay-builder-rotate-slider" data-ref="rotation-slider" min="0" max="359" step="1" value="${rot}" aria-valuemin="0" aria-valuemax="359" aria-valuenow="${rot}" />

        <div class="tay-builder-editor__actions tay-builder-editor__actions--row">
          <button type="button" class="tay-btn tay-btn--secondary" data-action="bring-patch-front">${escapeHtml(labels.bringFront || 'Bring to front')}</button>
          <button type="button" class="tay-btn tay-btn--secondary tay-btn--danger-outline" data-action="delete-selected-patch">${escapeHtml(labels.deletePatch || labels.remove)}</button>
        </div>`;

    if (plain) {
      return `<div class="tay-builder-editor">${editorControls}</div>`;
    }

    return `
      <details class="tay-builder-editor-details"${this._editorDetailsOpen ? ' open' : ''}>
        <summary class="tay-builder-editor-details__summary">
          <span class="tay-builder-editor-details__label">${adjustLbl}</span>
          <span class="tay-builder-editor-details__meta">${escapeHtml(title)}</span>
        </summary>
        <div class="tay-builder-editor tay-builder-editor--in-details">
          ${editorControls}
        </div>
      </details>`;
  }

  /**
   * @param {Record<string, string>} labels
   * @param {string} currency
   */

  buildReviewHtml(labels, currency) {
    const bagVariant = this.getBagVariant();
    const bagProduct = this.getBagProduct();
    const bagThumb =
      bagVariant?.image ?? bagProduct?.featured_image
        ? `<img src="${escapeHtml(bagVariant?.image ?? bagProduct?.featured_image ?? '')}" alt="">`
        : '';

    const bagLine =
      bagVariant && bagProduct
        ? `
      <li class="tay-builder-review-line">
        <span class="tay-builder-review-line__thumb">${bagThumb}</span>
        <span class="tay-builder-review-line__info">
          <span class="tay-builder-review-line__title">${escapeHtml(bagProduct.title)}</span>
          ${preOrderChipHtml(readPreOrder(bagProduct))}
          <span class="tay-builder-review-line__meta">${escapeHtml(bagVariant.title)}</span>
          ${preOrderDetailsHtml(readPreOrder(bagProduct))}
        </span>
        <span class="tay-builder-review-line__price">${escapeHtml(formatMoneyCents(bagVariant.price, currency))}</span>
      </li>`
        : '';

    const patchLines = this._patches
      .map((p) => {
        const thumb = p.image ? `<img src="${escapeHtml(p.image)}" alt="">` : '';
        const vn =
          p.variantTitle && p.variantTitle !== 'Default Title'
            ? `<span class="tay-builder-review-line__meta">${escapeHtml(p.variantTitle)}</span>`
            : '';
        const sideFace = this.hasBagBackPreview()
          ? bagFaceLabel(patchSideOf(p), labels)
          : '';
        const sideMeta = sideFace
          ? `<span class="tay-builder-review-line__meta tay-builder-review-line__side">${escapeHtml(sideFace)}</span>`
          : '';
        return `
      <li class="tay-builder-review-line">
        <span class="tay-builder-review-line__thumb">${thumb}</span>
        <span class="tay-builder-review-line__info">
          <span class="tay-builder-review-line__title">${escapeHtml(p.productTitle)}</span>
          ${vn}
          ${sideMeta}
        </span>
        <span class="tay-builder-review-line__price">${escapeHtml(formatMoneyCents(p.price, currency))}</span>
      </li>`;
      })
      .join('');

    const total = this.computeTotalCents();
    const disclaimer = labels.reviewDisclaimer
      ? `<p class="tay-builder-review-disclaimer">${escapeHtml(labels.reviewDisclaimer)}</p>`
      : '';
    const designPreviewsHtml = this.buildReviewDesignPreviewsHtml(labels);
    const titleVal = escapeHtml(this._designTitle || '');
    const titleLbl = labels.designNameLabel || 'Name your bag';
    const titleHint =
      labels.designNameHint || 'This name appears on your order.';

    return `
      <div class="tay-builder-review">
        <p class="tay-builder-panel-title">${escapeHtml(labels.stepReview || 'Review')}</p>
        <p class="tay-builder-review-lede">${escapeHtml(labels.reviewIntro || 'Here is everything in your custom design.')}</p>
        <label class="tay-builder-name">
          <span class="tay-builder-name__label">${escapeHtml(titleLbl)}</span>
          <input type="text" data-ref="design-title" maxlength="80" value="${titleVal}" placeholder="${escapeHtml(labels.designNamePlaceholder || 'My tote')}" autocomplete="off">
          <span class="tay-builder-name__hint">${escapeHtml(titleHint)}</span>
        </label>
        ${designPreviewsHtml}
        <ul class="tay-builder-review-list">${bagLine}${patchLines}</ul>
        <div class="tay-builder-review-total">
          <span>${escapeHtml(labels.total)}</span>
          <strong>${escapeHtml(formatMoneyCents(total, currency))}</strong>
        </div>
        ${disclaimer}
      </div>`;
  }

  /**
   * @param {Record<string, string>} labels
   */
  buildClosetCtaHtml(labels) {
    const appPage = String(this._config?.appPageUrl ?? '').trim();
    const appStore = String(this._config?.appStoreUrl ?? '').trim();
    const href = appPage || appStore;
    if (!href) return '';
    const copy =
      labels.closetCta ||
      'Save designs in the iOS app and reorder anytime.';
    const linkLbl = labels.closetCtaLink || 'Get the app';
    return `
      <p class="tay-builder-closet-cta">
        ${escapeHtml(copy)}
        <a href="${escapeHtml(href)}" ${appPage ? '' : 'target="_blank" rel="noopener noreferrer"'}>${escapeHtml(linkLbl)}</a>
      </p>`;
  }

  /**
   * @param {Record<string, string>} labels
   */
  buildAddedPanelHtml(labels) {
    const added = this._lastAdded;
    const title = added?.title || labels.untitledBag || 'Your tote';
    const front = added?.front;
    const back = added?.back;
    const frontLbl = labels.bagFaceFront || 'Front';
    const backLbl = labels.bagFaceBack || 'Back';
    const thumbs = `
      <div class="tay-builder-added__thumbs">
        ${
          front
            ? `<figure><img src="${escapeHtml(front)}" alt="${escapeHtml(frontLbl)}" width="280" height="350"><figcaption>${escapeHtml(frontLbl)}</figcaption></figure>`
            : ''
        }
        ${
          back
            ? `<figure><img src="${escapeHtml(back)}" alt="${escapeHtml(backLbl)}" width="280" height="350"><figcaption>${escapeHtml(backLbl)}</figcaption></figure>`
            : ''
        }
      </div>`;
    const startUrl = String(this._config?.startOverUrl ?? '').trim();
    const checkoutPath = String(this._config?.checkoutPath || '/checkout').trim() || '/checkout';
    const editLbl = labels.editThisBag || 'Edit this bag';
    return `
      <div class="tay-builder-added" data-ref="added-panel">
        <p class="tay-builder-added__kicker">${escapeHtml(labels.addedKicker || 'Added to your order')}</p>
        <h2 class="tay-builder-added__title">${escapeHtml(title)}</h2>
        <p class="tay-builder-added__lede">${escapeHtml(labels.addedLede || 'Your bag is in your order. You can still edit it before you pay.')}</p>
        ${thumbs}
        <div class="tay-builder-added__actions">
          <button type="button" class="tay-btn tay-btn--primary" data-action="edit-this-bag">${escapeHtml(editLbl)}</button>
          <a class="tay-btn tay-btn--secondary" href="/cart">${escapeHtml(labels.viewOrder || 'View order')}</a>
          <a class="tay-btn tay-btn--secondary" href="${escapeHtml(checkoutPath)}">${escapeHtml(labels.goCheckout || 'Checkout')}</a>
          <button type="button" class="tay-btn tay-btn--ghost" data-action="design-another">${escapeHtml(labels.designAnother || 'Design another bag')}</button>
          ${
            startUrl
              ? `<a class="tay-btn tay-btn--ghost" href="${escapeHtml(startUrl)}">${escapeHtml(labels.chooseDifferentBag || 'Choose a different bag')}</a>`
              : ''
          }
        </div>
        ${this.buildClosetCtaHtml(labels)}
      </div>`;
  }

  refreshPatchActionPills() {
    this.querySelectorAll('[data-ref="canvas-stage"], [data-ref="fs-canvas-stage"]').forEach((stage) => {
      if (stage instanceof HTMLElement) updatePatchActionPills(stage);
    });
  }

  scheduleGeometryUpdate() {
    if (this._geoRaf != null) cancelAnimationFrame(this._geoRaf);
    this._geoRaf = requestAnimationFrame(() => {
      this._geoRaf = null;
      this.updateCanvasGeometry();
      if (this._fullscreenOpen) this.updateFullscreenGeometry();
      this.refreshPatchActionPills();
    });
  }

  updateCanvasGeometry() {
    const stage = this.querySelector('[data-ref="canvas-stage"]');
    const img = stage?.querySelector('.tay-builder-canvas__bag');
    if (!(stage instanceof HTMLElement) || !(img instanceof HTMLImageElement)) return;

    /* During drag, only `pointermove` updates the active patch. A ResizeObserver/image-driven
       full pass would rewrite every patch’s DOM position and makes the others appear to slide. */
    if (this._dragIndex !== null) return;

    const sw = stage.clientWidth;
    const sh = stage.clientHeight;
    const nw = img.naturalWidth || sw;
    const nh = img.naturalHeight || sh;
    const bag = computeObjectFitContainRect(sw, sh, nw, nh);

    const cfg = this._config;
    const bagProduct = this.getBagProduct();
    const bagWIn = Number(bagProduct?.bag_width_in) || Number(cfg?.defaultBagWidthIn) || 22.5;
    const bagHIn = Number(bagProduct?.bag_height_in) || Number(cfg?.defaultBagHeightIn) || 14.5;

    this.updateStrapZoneOverlay(stage, bag);

    this._patches.forEach((p, i) => {
      const el = stage.querySelector(`[data-patch-index="${i}"]`);
      if (!(el instanceof HTMLElement)) return;

      layoutOnBagPatchElement(el, p, bag, bagWIn, bagHIn, sw, sh, cfg, bagProduct);
    });
    updatePatchActionPills(stage);
  }

  bindBagImageGeometry() {
    const stage = this.querySelector('[data-ref="canvas-stage"]');
    const img = stage?.querySelector('.tay-builder-canvas__bag');
    if (!(stage instanceof HTMLElement) || !(img instanceof HTMLImageElement)) return;

    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }

    this._resizeObserver = new ResizeObserver(() => this.scheduleGeometryUpdate());
    this._resizeObserver.observe(stage);

    if (this._imgLoadHandler) {
      img.removeEventListener('load', this._imgLoadHandler);
    }
    this._imgLoadHandler = () => this.scheduleGeometryUpdate();
    img.addEventListener('load', this._imgLoadHandler);

    if (img.complete) this.scheduleGeometryUpdate();
  }

  /**
   * @param {'bag' | 'design' | 'review'} canvasMode
   */
  patchPatchElementPositionsFromState(canvasMode) {
    if (canvasMode === 'bag') return;
    this.updateCanvasGeometry();
  }

  applyOnBagPatchArtwork() {
    this.querySelectorAll('.tay-builder-canvas-patch__img[data-patch-src]').forEach((node) => {
      if (!(node instanceof HTMLImageElement)) return;
      const src = node.getAttribute('data-patch-src');
      if (!src) return;
      const key = onBagPatchSourceUrl(src);
      const ready = key ? tayPatchArtworkCache.ready.get(key) : null;

      const afterArtwork = () => {
        if (!node.isConnected) return;
        this.scheduleGeometryUpdate();
      };

      if (ready) {
        if (node.src !== ready) {
          node.addEventListener('load', afterArtwork, { once: true });
          node.src = ready;
          return;
        }
        if (node.complete && node.naturalWidth) afterArtwork();
        else node.addEventListener('load', afterArtwork, { once: true });
        return;
      }

      getOnBagPatchUrl(src).then((url) => {
        if (!url || !node.isConnected) return;
        if (node.getAttribute('data-patch-src') !== src) return;
        node.addEventListener('load', afterArtwork, { once: true });
        if (node.src !== url) node.src = url;
        else if (node.complete && node.naturalWidth) afterArtwork();
      });
    });
  }

  render() {
    const cfg = this._config;
    if (!cfg) return;

    if (this._step !== 3) {
      this._reviewPreviewGen += 1;
      this.revokeReviewPreviewUrls();
    }

    if (this._step !== 2 && this._patchSheetOpen) {
      this.closePatchSheet();
    }
    if (this._step !== 2 && this._tidySheetOpen) {
      this.closeTidySheet();
    }
    if (this._step !== 2 && this._breakdownSheetOpen) {
      this.closeBreakdownSheet();
    }
    if (!(this._step === 2 || this._step === 3) && this._fullscreenOpen) {
      this.closeFullscreenPreview();
    }

    const labels = cfg.labels;
    const sectionId = this.dataset.sectionId ?? '';

    if (this._showAddedPanel) {
      this.innerHTML = `
        <div class="tay-builder-shell tay-builder-shell--added">
          <div class="tay-builder-layout">
            <div class="tay-builder-panel tay-builder-panel--controls tay-builder-panel--added">
              ${this.buildAddedPanelHtml(labels)}
              <p class="tay-builder-error tay-builder-error--inline" data-ref="error" hidden></p>
            </div>
          </div>
          <p class="tay-builder-toast" data-ref="toast" hidden role="status"></p>
        </div>`;
      this.bind();
      return;
    }

    if (!cfg.bags?.length || !cfg.patches?.length) {
      const msg = !cfg.bags?.length ? labels.emptyBags : labels.emptyPatches;
      this.innerHTML = `<div class="tay-builder-panel"><p role="status">${escapeHtml(msg)}</p></div>`;
      return;
    }

    this.ensurePatchSelection();
    this.sanitizeTidyIndices();
    this.ensureSelectionMatchesBagSide();

    const bagProduct = this.getBagProduct();
    const bagVariant = this.getBagVariant();
    const previewImage = this.getCanvasBagPreviewImage(this._step === 1);

    const singleBag =
      cfg.singleBagMode === true || (Array.isArray(cfg.bags) && cfg.bags.length === 1);

    const bagsHtml = cfg.bags
      .map((/** @type {any} */ p) => {
        const hasStock = p.variants.some((/** @type {any} */ v) => v.available);
        const sel = p.id === this._bagProductId ? ' is-selected' : '';
        const blockedAttrs = !hasStock ? blockedActionAttrs(false) : '';
        const defaultBagV = firstAvailableVariant(p.variants) || p.variants?.[0];
        const chip = statusChipHtml(p, defaultBagV, {
          soldOut: labels.selectBagError || 'Sold out',
          lowStock: labels.lowStock || 'Only [count] left',
        });
        const thumb = p.featured_image
          ? `<div class="tay-builder-choice-thumb">${chip}<img src="${escapeHtml(p.featured_image)}" alt="" loading="lazy" width="280" height="280"></div>`
          : `<div class="tay-builder-choice-thumb">${chip}</div>`;
        return `
          <button type="button" class="tay-builder-choice${sel}" data-action="pick-bag" data-product-id="${p.id}"${blockedAttrs}>
            ${thumb}
            <p class="tay-builder-choice-title">${escapeHtml(p.title)}</p>
          </button>`;
      })
      .join('');

    let variantSwatchesHtml = '';
    if (this._step === 1 && bagProduct && bagProduct.variants.length > 1) {
      variantSwatchesHtml = this.buildBagVariantSwatchesHtml(bagProduct, labels, sectionId);
    }

    const currency = cfg.currency || 'USD';
    const patchesHtml = this.buildPatchCardsHtml(cfg, labels, sectionId, currency);

    const mobileLayout = this.isMobileLayout();

    const browseRow =
      singleBag && cfg.bagsBrowseUrl && labels.browseAllBags && this._step === 1 && !mobileLayout
        ? `<p class="tay-builder-browse"><a href="${escapeHtml(cfg.bagsBrowseUrl)}">${escapeHtml(labels.browseAllBags)}</a></p>`
        : '';

    const bagPickerBlock =
      singleBag || this._step !== 1
        ? ''
        : `
          <p class="tay-builder-panel-title" style="margin-top:1.25rem;">${escapeHtml(labels.chooseBag)}</p>
          <div class="tay-builder-bag-grid">${bagsHtml}</div>`;

    const minP = this.getMinPatches();
    const patchCountHint = '';

    /** @type {'bag' | 'design' | 'review'} */
    let canvasMode = 'bag';
    if (this._step === 2) canvasMode = 'design';
    if (this._step === 3) canvasMode = 'review';

    this._canvasReadOnly = canvasMode !== 'design';

    const bagFaceFooterHtml =
      (this._step === 2 || this._step === 3) && this.hasBagBackPreview()
        ? this.buildBagFaceToggleHtml(labels)
        : '';
    const canvasHtml = this.buildCanvasHtml(previewImage, labels, canvasMode, bagFaceFooterHtml);

    const showSummaryBar =
      this._step === 1 || (this._step === 2 && !mobileLayout);
    const summaryBarHtml = showSummaryBar
      ? this.buildSummaryBarHtml(labels, currency, {
          ...(this._step === 2 ? { minP, patchCount: this._patches.length } : {}),
          ...(mobileLayout ? { hideCaption: true } : {}),
        })
      : '';
    const mobileStep2ActionsHtml =
      this._step === 2 && mobileLayout ? this.buildMobileStep2ActionsHtml(labels) : '';
    const breakdownSheetHtml =
      this._step === 2 && mobileLayout
        ? this.buildBreakdownSheetHtml(labels, currency)
        : '';
    const fullscreenHtml =
      previewImage && (this._step === 2 || this._step === 3)
        ? this.buildFullscreenPreviewHtml(labels)
        : '';

    const progressHtml = this.buildProgressHtml(labels);

    const stepTitle =
      this._step === 1
        ? singleBag && labels.stepBagLocked
          ? labels.stepBagLocked
          : labels.stepBag
        : this._step === 2
          ? labels.stepPatches
          : labels.stepReview || 'Review';

    const stepLede =
      this._step === 1
        ? labels.step1Lede || ''
        : this._step === 2
          ? labels.step2Lede || labels.addPatchesHint
          : '';

    let rightColumnHtml = '';
    let hideMobileControls = false;

    if (this._step === 1) {
      const canContinue = Boolean(bagVariant?.available);
      const bagPreOrder = readPreOrder(bagProduct);
      const bagPreOrderChip = preOrderChipHtml(bagPreOrder);
      const bagPreOrderDetails = preOrderDetailsHtml(bagPreOrder);
      if (mobileLayout) {
        hideMobileControls = true;
        rightColumnHtml = '';
      } else {
        rightColumnHtml = `
        <p class="tay-builder-step-heading">${escapeHtml(stepTitle)}</p>
        ${bagPreOrderChip || bagPreOrderDetails ? `<div class="tay-builder-preorder">${bagPreOrderChip}${bagPreOrderDetails}</div>` : ''}
        ${stepLede ? `<p class="tay-builder-step-lede">${escapeHtml(stepLede)}</p>` : ''}
        <div class="tay-builder-step-actions tay-builder-step-actions--desktop">
          <button type="button" class="tay-btn tay-btn--primary tay-btn--full" data-action="wizard-next"${blockedActionAttrs(canContinue)}>
            ${escapeHtml(labels.nextToPatches || 'Continue')}
          </button>
        </div>`;
      }
    } else if (this._step === 2) {
      const libraryChrome = this.buildPatchLibraryChromeHtml(cfg, labels);
      const canContinue =
        this._patches.length >= minP && this._patches.length <= cfg.maxPatches && Boolean(bagVariant?.available);
      if (mobileLayout) {
        hideMobileControls = true;
        rightColumnHtml = '';
      } else {
        const stripHtml = this.buildPatchStripHtml(labels);
        const tidyHtml = this.buildTidyToolbarHtml(labels);
        const editorHtml = this.buildPatchEditorHtml(labels);
        const toolStackHtml = `${patchCountHint}${stripHtml}${editorHtml}${tidyHtml}`;
        rightColumnHtml = `
        <p class="tay-builder-step-heading">${escapeHtml(stepTitle)}</p>
        ${stepLede ? `<p class="tay-builder-step-lede">${escapeHtml(stepLede)}</p>` : ''}
        ${toolStackHtml}
        <div class="tay-builder-desktop-library">
          <p class="tay-builder-panel-title" style="margin-top:1.25rem;">${escapeHtml(labels.patchLibrary)}</p>
          ${libraryChrome}
          <div class="tay-builder-patch-grid">${patchesHtml}</div>
        </div>
        <div class="tay-builder-step-actions tay-builder-step-actions--split tay-builder-step-actions--desktop">
          <button type="button" class="tay-btn tay-btn--secondary" data-action="wizard-back">${escapeHtml(labels.back || 'Back')}</button>
          <button type="button" class="tay-btn tay-btn--primary" data-action="wizard-next"${blockedActionAttrs(canContinue)}>
            ${escapeHtml(labels.nextToReview || 'Review')}
          </button>
        </div>`;
      }
    } else {
      const reviewHtml = this.buildReviewHtml(labels, currency);
      const canSubmit =
        Boolean(bagVariant?.available) &&
        this._patches.length >= minP &&
        this._patches.length <= cfg.maxPatches;
      rightColumnHtml = `
        ${reviewHtml}
        <div class="tay-builder-step-actions tay-builder-step-actions--split tay-builder-step-actions--desktop">
          <button type="button" class="tay-btn tay-btn--secondary" data-action="wizard-back">${escapeHtml(labels.back || 'Back')}</button>
          <button type="button" class="tay-btn tay-btn--primary" data-ref="submit"${blockedActionAttrs(canSubmit)}>
            ${escapeHtml(this.orderSubmitLabel())}
          </button>
        </div>`;
    }

    const libraryChromeForSheet =
      this._step === 2 ? this.buildPatchLibraryChromeHtml(cfg, labels) : '';
    const patchSheetHtml =
      this._step === 2
        ? this.buildPatchSheetHtml(cfg, labels, sectionId, patchesHtml, libraryChromeForSheet)
        : '';
    const tidySheetHtml = this._step === 2 && mobileLayout ? this.buildTidySheetHtml(labels) : '';
    const canReviewMobile =
      this._step === 2 &&
      this._patches.length >= minP &&
      this._patches.length <= cfg.maxPatches &&
      Boolean(bagVariant?.available);
    const canContinueStep1 = Boolean(bagVariant?.available);
    let mobileDockHtml = '';
    if (this._step === 1) {
      mobileDockHtml = this.buildMobileStep1DockHtml(labels, canContinueStep1);
    } else if (this._step === 2) {
      mobileDockHtml = this.buildMobileDockHtml(labels, minP, canReviewMobile, currency);
    } else if (this._step === 3) {
      const canSubmit =
        Boolean(bagVariant?.available) &&
        this._patches.length >= minP &&
        this._patches.length <= cfg.maxPatches;
      mobileDockHtml = this.buildMobileReviewDockHtml(labels, canSubmit);
    }

    const toolsOpen =
      this._step === 2 && !mobileLayout && (this._editorDetailsOpen || this._tidyDetailsOpen);

    const shellClasses = [
      'tay-builder-shell',
      `tay-builder-shell--step-${this._step}`,
      mobileDockHtml ? 'tay-builder-shell--has-dock' : '',
      this._patchSheetOpen ? 'tay-builder-shell--sheet-open' : '',
      this._tidySheetOpen ? 'tay-builder-shell--tidy-open' : '',
      this._breakdownSheetOpen ? 'tay-builder-shell--breakdown-open' : '',
      this._fullscreenOpen ? 'tay-builder-shell--fs-open' : '',
      this._summaryExpanded ? 'tay-builder-shell--summary-expanded' : '',
      toolsOpen ? 'tay-builder-shell--tools-open' : '',
    ]
      .filter(Boolean)
      .join(' ');

    const scrollY = typeof window !== 'undefined' ? window.scrollY : 0;
    this.capturePatchLibraryScroll();

    this.innerHTML = `
      <div class="${shellClasses}">
        ${progressHtml}
        ${
          this._replaceDesignId
            ? `<p class="tay-builder-editing-note">${escapeHtml(
                labels.editingNote ||
                  'You’re editing a bag that’s already in your order. Save to replace it, then check out as usual.'
              )}</p>`
            : ''
        }
        <div class="tay-builder-layout">
          <div class="tay-builder-panel tay-builder-panel--preview">
            <p class="tay-builder-panel-title tay-builder-panel-title--preview">${escapeHtml(labels.previewHeading || 'Preview')}</p>
            ${canvasHtml}
            ${mobileStep2ActionsHtml}
            ${summaryBarHtml}
            ${this._step === 1 ? variantSwatchesHtml : ''}
            ${this._step === 1 ? browseRow : ''}
            ${this._step === 1 ? bagPickerBlock : ''}
            ${
              hideMobileControls
                ? `<p class="tay-builder-error tay-builder-error--inline" data-ref="error" hidden></p>`
                : ''
            }
          </div>
          ${
            hideMobileControls
              ? ''
              : `<div class="tay-builder-panel tay-builder-panel--controls">
            ${rightColumnHtml}
            <p class="tay-builder-error tay-builder-error--inline" data-ref="error" hidden></p>
          </div>`
          }
        </div>
        <p class="tay-builder-toast" data-ref="toast" hidden role="status"></p>
        ${patchSheetHtml}
        ${tidySheetHtml}
        ${breakdownSheetHtml}
        ${fullscreenHtml}
        ${mobileDockHtml}
      </div>
    `;

    if (typeof window !== 'undefined') {
      window.scrollTo(0, scrollY);
    }
    this.restorePatchLibraryScroll();

    if (this._patchSheetOpen || this._breakdownSheetOpen || this._fullscreenOpen || this._tidySheetOpen) {
      this.bindSheetKeyHandler();
    }
    if (this._patchSheetOpen) {
      this.focusPatchSheetSearch();
    }
    this.bind();
    this.bindMobileToolPanels();
    this.bindPatchSheetSwipe();
    this.setupCanvasInteractions();
    this.bindBagImageGeometry();
    this.bindStageDeselect();
    this.bindRotationSlider(labels);
    this.patchPatchElementPositionsFromState(canvasMode);
    this.applyOnBagPatchArtwork();
    if (this._fullscreenOpen) {
      this.bindFullscreenInteractions();
      this.updateFullscreenGeometry();
      const fsImg = this.querySelector('[data-ref="fs-canvas-stage"] .tay-builder-canvas__bag');
      if (fsImg instanceof HTMLImageElement) {
        if (fsImg.complete) this.updateFullscreenGeometry();
        else fsImg.addEventListener('load', () => this.updateFullscreenGeometry(), { once: true });
      }
    }
    if (this._step === 3) {
      this.scheduleReviewPreviews();
    }
    this.persistDraft();
  }

  bindMobileToolPanels() {
    if (this._step !== 2) return;

    const editorDetails = this.querySelector('.tay-builder-editor-details');
    if (editorDetails instanceof HTMLDetailsElement) {
      editorDetails.addEventListener('toggle', () => {
        this._editorDetailsOpen = editorDetails.open;
        this.syncToolsOpenClass();
      });
    }

    const tidyDetails = this.querySelector('.tay-builder-tidy-details');
    if (tidyDetails instanceof HTMLDetailsElement) {
      tidyDetails.addEventListener('toggle', () => {
        const opening = tidyDetails.open && !this._tidyDetailsOpen;
        this._tidyDetailsOpen = tidyDetails.open;
        this.syncToolsOpenClass();
        if (!opening) return;
        if (this.getTidyTargetsForActiveSide().length > 0) return;
        this.selectAllTidyOnActiveSide();
        if (this.getTidyTargetsForActiveSide().length > 0) this.render();
      });
    }
  }

  syncToolsOpenClass() {
    const shell = this.querySelector('.tay-builder-shell');
    if (shell instanceof HTMLElement) {
      const open = !this.isMobileLayout() && Boolean(this._editorDetailsOpen || this._tidyDetailsOpen);
      shell.classList.toggle('tay-builder-shell--tools-open', open);
    }
    this.scheduleGeometryUpdate();
  }

  bindStageDeselect() {
    if (this._step !== 2) return;
    const stage = this.querySelector('[data-ref="canvas-stage"]');
    if (!(stage instanceof HTMLElement)) return;
    stage.addEventListener(
      'pointerdown',
      (e) => {
        if (e.target !== stage) return;
        this._selectedPatchIndex = null;
        if (!this.isMobileLayout()) this._editorDetailsOpen = false;
        this.render();
      },
      { capture: true }
    );
  }

  /**
   * @param {Record<string, string>} labels
   */
  bindRotationSlider(labels) {
    if (this._step !== 2) return;
    const slider = this.querySelector('[data-ref="rotation-slider"]');
    const degEl = this.querySelector('[data-ref="rotation-degree"]');
    if (!(slider instanceof HTMLInputElement)) return;
    const idx = this._selectedPatchIndex;
    if (idx == null || !this._patches[idx]) return;

    const degSuffix = labels.degreeSuffix || '°';

    const applyRot = (raw) => {
      const v = normalizeRotationDeg(raw);
      this._patches[idx].rot = v;
      slider.value = String(v);
      slider.setAttribute('aria-valuenow', String(v));
      if (degEl) degEl.textContent = `${v}${degSuffix}`;
      const stage = this.querySelector('[data-ref="canvas-stage"]');
      const el = stage?.querySelector(`[data-patch-index="${idx}"]`);
      if (el instanceof HTMLElement) {
        applyPatchRotationChrome(el, v);
      }
      const fsEl = this.querySelector(`[data-ref="fs-canvas-stage"] [data-patch-index="${idx}"]`);
      if (fsEl instanceof HTMLElement) {
        applyPatchRotationChrome(fsEl, v);
      }
      this.refreshPatchActionPills();
    };

    slider.addEventListener('pointerdown', () => this.pushUndo(), { once: true });
    slider.addEventListener('input', () => {
      applyRot(Number(slider.value));
    });
  }

  bind() {
    this.querySelectorAll('[data-action="open-patch-sheet"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        this.openPatchSheet();
        this.render();
      });
    });

    this.querySelectorAll('[data-action="close-patch-sheet"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.closePatchSheet();
        this.render();
      });
    });

    this.querySelectorAll('[data-action="open-tidy-sheet"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        if (isBlockedAction(btn)) {
          this.showError(this._config?.labels?.stripEmpty || 'Add patches to your bag first.');
          return;
        }
        this.openTidySheet();
        this.render();
      });
    });

    this.querySelectorAll('[data-action="close-tidy-sheet"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.closeTidySheet();
        this.render();
      });
    });

    this.querySelectorAll('[data-action="open-breakdown-sheet"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        this.openBreakdownSheet();
        this.render();
      });
    });

    this.querySelectorAll('[data-action="close-breakdown-sheet"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.closeBreakdownSheet();
        this.render();
      });
    });

    this.querySelectorAll('[data-action="undo-patch"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.undoLastChange();
      });
    });

    this.querySelectorAll('[data-action="shuffle-patches"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        if (this.notifyIfBlocked(this.getShuffleBlockerMessage())) return;
        this.pushUndo();
        this.runShufflePatches();
      });
    });

    this.querySelectorAll('[data-action="open-fullscreen-preview"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        this.openFullscreenPreview();
        this.render();
      });
    });

    this.querySelectorAll('[data-action="open-review-face-preview"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        const face = btn.dataset.face;
        if (!isBagFace(face)) return;
        this._bagSide = face;
        this.openFullscreenPreview();
        this.render();
      });
    });

    this.querySelectorAll('[data-action="close-fullscreen-preview"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.closeFullscreenPreview();
        this.render();
      });
    });

    this.querySelectorAll('[data-action="fs-zoom-in"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.adjustFullscreenZoom(0.25);
      });
    });

    this.querySelectorAll('[data-action="fs-zoom-out"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.adjustFullscreenZoom(-0.25);
      });
    });

    this.querySelectorAll('[data-action="toggle-summary-breakdown"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        this._summaryExpanded = !this._summaryExpanded;
        this.render();
      });
    });

    this.querySelectorAll('[data-action="pick-bag"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        if (isBlockedAction(btn)) {
          this.showError(
            this._config?.labels?.selectBagError || 'This bag is sold out. Choose another style.'
          );
          return;
        }
        const pid = Number(btn.dataset.productId);
        const prevPid = this._bagProductId;
        this._bagProductId = pid;
        const product = this._config?.bags.find((/** @type {any} */ p) => p.id === pid);
        const fv = product ? firstAvailableVariant(product.variants) : null;
        this._bagVariantId = fv?.id ?? null;
        if (prevPid !== null && prevPid !== pid) {
          this._patches = [];
          this._selectedPatchIndex = null;
          this._tidyIndices.clear();
          this._bagSide = 'front';
        }
        this.render();
      });
    });

    this.querySelectorAll('[data-action="pick-bag-variant"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        if (isBlockedAction(btn)) {
          this.showError(
            this._config?.labels?.patchUnavailable || 'This color is sold out. Pick another.'
          );
          return;
        }
        const vid = Number(btn.dataset.variantId);
        if (Number.isNaN(vid)) return;
        this._bagVariantId = vid;
        this.render();
      });
    });

    this.querySelectorAll('[data-action="wizard-next"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        const cfg = this._config;
        if (!cfg) return;

        if (this._step === 1) {
          if (this.notifyIfBlocked(this.getStep1BlockerMessage())) return;
          this._step = 2;
          this._patchSheetUserDismissed = false;
          if (this.isMobileLayout()) {
            this._patchSheetOpen = true;
            this.lockBodyForSheet();
          }
          this.ensurePatchSelection();
          this.render();
          return;
        }
        if (this._step === 2) {
          if (this.notifyIfBlocked(this.getStep2BlockerMessage())) return;
          this._step = 3;
          this._editorDetailsOpen = false;
          this._tidyDetailsOpen = false;
          this.closeTidySheet();
          this.render();
        }
      });
    });

    this.querySelectorAll('[data-action="wizard-back"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        if (this._step === 1) {
          this.exitStep1ToBrowse();
        } else if (this._step === 2) {
          this._step = 1;
          this._editorDetailsOpen = false;
          this._tidyDetailsOpen = false;
          this.closePatchSheet();
          this.closeTidySheet();
          this.closeBreakdownSheet();
          this.render();
        } else if (this._step === 3) {
          this._step = 2;
          this._patchSheetUserDismissed = false;
          this.ensurePatchSelection();
          this.render();
        }
      });
    });

    this.querySelectorAll('[data-action="select-patch"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        const index = Number(btn.dataset.index);
        if (Number.isNaN(index)) return;
        const p = this._patches[index];
        if (this.hasBagBackPreview() && p) {
          this._bagSide = patchSideOf(p);
        }
        this._selectedPatchIndex = index;
        if (!this.isMobileLayout()) this._editorDetailsOpen = true;
        this.render();
      });
    });

    this.querySelectorAll('[data-action="bag-side"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        const s = btn.dataset.side;
        if (!isBagFace(s)) return;
        this._bagSide = s;
        this.render();
      });
    });

    this.querySelectorAll('.tay-builder-patch-card').forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      this.syncPatchCard(card);
      const sel = card.querySelector('[data-patch-variant-select]');
      if (sel instanceof HTMLSelectElement) {
        sel.addEventListener('change', () => this.syncPatchCard(card));
      }
    });

    this.querySelectorAll('[data-action="add-patch-product"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        if (isBlockedAction(btn)) {
          const cfg = this._config;
          if (cfg && this._patches.length >= cfg.maxPatches) {
            this.showError(cfg.labels.maxPatchesError);
          } else {
            this.showError(cfg?.labels?.patchUnavailable || 'This patch is sold out.');
          }
          return;
        }
        const card = btn.closest('.tay-builder-patch-card');
        if (!(card instanceof HTMLElement)) return;
        const added = this.tryAddPatchFromCard(card);
        if (added) {
          this.closePatchSheet();
          this.render();
          this.scrollPreviewIntoView();
        }
      });
    });

    this.querySelectorAll('.tay-builder-patch-grid--sheet .tay-builder-patch-card--tap').forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      card.addEventListener('click', (e) => {
        if (card.classList.contains('tay-builder-patch-card--disabled')) {
          const cfg = this._config;
          if (cfg && this._patches.length >= cfg.maxPatches) {
            this.showError(cfg.labels.maxPatchesError);
          } else {
            this.showError(cfg?.labels?.patchUnavailable || 'This patch is sold out.');
          }
          return;
        }
        if (e.target instanceof HTMLElement && e.target.closest('[data-patch-variant-select]')) return;
        if (e.target instanceof HTMLElement && e.target.closest('[data-action="add-patch-product"]')) return;
        const added = this.tryAddPatchFromCard(card);
        if (added) {
          this.closePatchSheet();
          this.render();
          this.scrollPreviewIntoView();
        }
      });
      card.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        card.click();
      });
    });

    this.querySelectorAll('[data-action="remove-patch"]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.removePatchAt(Number(btn.getAttribute('data-index')));
      });
    });

    this.querySelectorAll('[data-action="remove-canvas-patch"]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.removePatchAt(Number(btn.getAttribute('data-patch-index')));
      });
    });

    this.querySelectorAll('[data-action="delete-selected-patch"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = this._selectedPatchIndex;
        if (idx == null || !this._patches[idx]) {
          this.showError(
            this._config?.labels?.selectPatchHint || 'Tap a patch on your bag or in the list below to select it.'
          );
          return;
        }
        this.removePatchAt(idx);
      });
    });

    this.querySelectorAll('[data-action="toggle-tidy-patch"]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!(btn instanceof HTMLButtonElement)) return;
        const index = Number(btn.dataset.index);
        if (Number.isNaN(index)) return;
        if (this._tidyIndices.has(index)) this._tidyIndices.delete(index);
        else this._tidyIndices.add(index);
        this.render();
      });
    });

    this.querySelectorAll('[data-action="tidy-select-all"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this._tidyIndices = new Set(this._patches.map((_, i) => i));
        this.render();
      });
    });

    this.querySelectorAll('[data-action="tidy-clear"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this._tidyIndices.clear();
        this.render();
      });
    });

    this.querySelectorAll('[data-action="tidy-align"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        if (this.notifyIfBlocked(this.getTidyBlockerMessage('align'))) return;
        const mode = btn.dataset.tidyMode;
        if (!mode) return;
        this.pushUndo();
        this.runTidyAlign(mode);
      });
    });

    this.querySelectorAll('[data-action="tidy-distribute"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        if (this.notifyIfBlocked(this.getTidyBlockerMessage('distribute'))) return;
        const axis = btn.dataset.tidyAxis;
        if (axis !== 'h' && axis !== 'v') return;
        this.pushUndo();
        this.runTidyDistribute(axis);
      });
    });

    this.querySelectorAll('[data-action="tidy-stagger"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        if (this.notifyIfBlocked(this.getTidyBlockerMessage('stagger'))) return;
        this.pushUndo();
        this.runTidyStagger();
      });
    });

    this.querySelectorAll('[data-action="delete-tidy-patches"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        if (this.notifyIfBlocked(this.getTidyBlockerMessage('delete'))) return;
        const targets = this.getTidyTargets().sort((a, b) => b - a);
        if (targets.length === 0) return;
        this.pushUndo();
        targets.forEach((i) => this._patches.splice(i, 1));
        this._tidyIndices.clear();
        if (this._patches.length === 0) {
          this._selectedPatchIndex = null;
        } else {
          let sel = this._selectedPatchIndex;
          if (sel == null || sel >= this._patches.length) sel = this._patches.length - 1;
          this._selectedPatchIndex = sel;
        }
        this.ensurePatchSelection();
        this._tidyIndices = new Set(this._patches.map((_, i) => i));
        this.render();
      });
    });

    this.querySelectorAll('[data-action="bring-patch-front"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = this._selectedPatchIndex;
        if (idx == null || !this._patches[idx]) {
          this.showError(
            this._config?.labels?.selectPatchHint || 'Tap a patch on your bag or in the list below to select it.'
          );
          return;
        }
        this._patchStackSeq += 1;
        this._patches[idx].stackOrder = this._patchStackSeq;
        this.render();
      });
    });

    this.querySelectorAll('[data-action="patch-filter-cat"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        this._patchLibraryCategory = btn.dataset.category || 'all';
        this.querySelectorAll('[data-action="patch-filter-cat"]').forEach((b) => {
          if (!(b instanceof HTMLElement)) return;
          const cat = b.dataset.category || 'all';
          b.classList.toggle('is-selected', cat === this._patchLibraryCategory);
        });
        this.applyPatchLibraryFilter();
      });
    });

    this.querySelectorAll('[data-ref="patch-search"]').forEach((searchInput) => {
      if (!(searchInput instanceof HTMLInputElement)) return;
      searchInput.addEventListener('input', () => {
        this._patchLibraryQuery = searchInput.value;
        this.querySelectorAll('[data-ref="patch-search"]').forEach((other) => {
          if (other instanceof HTMLInputElement && other !== searchInput) {
            other.value = searchInput.value;
          }
        });
        this.applyPatchLibraryFilter();
      });
    });

    this.querySelectorAll('[data-action="nudge-patch"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        const idx = this._selectedPatchIndex;
        if (idx == null || !this._patches[idx]) {
          this.showError(
            this._config?.labels?.selectPatchHint || 'Tap a patch on your bag or in the list below to select it.'
          );
          return;
        }
        const dx = Number(btn.dataset.dx);
        const dy = Number(btn.dataset.dy);
        if (!Number.isFinite(dx) && !Number.isFinite(dy)) return;
        this.pushUndo();
        const p = this._patches[idx];
        const nextNx = clamp01((typeof p.nx === 'number' ? p.nx : 0.5) + (Number.isFinite(dx) ? dx : 0));
        const nextNy = clamp01((typeof p.ny === 'number' ? p.ny : 0.5) + (Number.isFinite(dy) ? dy : 0));
        const cleared = this.clampPatchPlacement(nextNx, nextNy, p);
        p.nx = cleared.nx;
        p.ny = cleared.ny;
        const stage = this.querySelector('[data-ref="canvas-stage"]');
        const img = stage?.querySelector('.tay-builder-canvas__bag');
        if (!(stage instanceof HTMLElement) || !(img instanceof HTMLImageElement)) return;
        const bag = computeObjectFitContainRect(
          stage.clientWidth,
          stage.clientHeight,
          img.naturalWidth || stage.clientWidth,
          img.naturalHeight || stage.clientHeight
        );
        const el = stage.querySelector(`[data-patch-index="${idx}"]`);
        if (el instanceof HTMLElement) {
          const cx = bag.left + p.nx * bag.width;
          const cy = bag.top + p.ny * bag.height;
          el.style.left = `${(cx / stage.clientWidth) * 100}%`;
          el.style.top = `${(cy / stage.clientHeight) * 100}%`;
        }
      });
    });

    const rotateDelta = (idx, delta) => {
      if (Number.isNaN(idx) || !this._patches[idx]) return;
      this._patches[idx].rot = normalizeRotationDeg((this._patches[idx].rot || 0) + delta);
      this.applyStrapClampToPatch(this._patches[idx]);
      const stage = this.querySelector('[data-ref="canvas-stage"]');
      const el = stage?.querySelector(`[data-patch-index="${idx}"]`);
      if (el instanceof HTMLElement) {
        applyPatchRotationChrome(el, this._patches[idx].rot);
      }
      const fsEl = this.querySelector(`[data-ref="fs-canvas-stage"] [data-patch-index="${idx}"]`);
      if (fsEl instanceof HTMLElement) {
        applyPatchRotationChrome(fsEl, this._patches[idx].rot);
      }
      this.scheduleGeometryUpdate();
      this.refreshPatchActionPills();
      const slider = this.querySelector('[data-ref="rotation-slider"]');
      const degEl = this.querySelector('[data-ref="rotation-degree"]');
      const labels = this._config?.labels || {};
      const degSuffix = labels.degreeSuffix || '°';
      if (slider instanceof HTMLInputElement && this._selectedPatchIndex === idx) {
        slider.value = String(normalizeRotationDeg(this._patches[idx].rot));
        slider.setAttribute('aria-valuenow', slider.value);
      }
      if (degEl && this._selectedPatchIndex === idx) {
        degEl.textContent = `${normalizeRotationDeg(this._patches[idx].rot)}${degSuffix}`;
      }
    };

    this.querySelectorAll('[data-action="rotate-patch-cw"]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const idx = Number(btn.getAttribute('data-patch-index'));
        this.pushUndo();
        this._selectedPatchIndex = idx;
        rotateDelta(idx, 15);
        this.updateStripSelectionVisual(idx);
      });
    });

    this.querySelectorAll('[data-ref="submit"]').forEach((submit) => {
      if (submit instanceof HTMLButtonElement) {
        submit.addEventListener('click', () => {
          if (this.notifyIfBlocked(this.getSubmitBlockerMessage())) return;
          this.handleAddToCart(submit);
        });
      }
    });

    const titleInput = this.querySelector('[data-ref="design-title"]');
    if (titleInput instanceof HTMLInputElement) {
      titleInput.addEventListener('input', () => {
        this._designTitle = titleInput.value;
        this.persistDraftNow();
      });
    }

    this.querySelectorAll('[data-action="design-another"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.resetBuilderForAnotherBag();
        this.render();
      });
    });

    this.querySelectorAll('[data-action="edit-this-bag"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        void this.reopenLastAddedBag();
      });
    });

    this.applyPatchLibraryFilter();
  }

  setupCanvasInteractions() {
    const stage = this.querySelector('[data-ref="canvas-stage"]');
    if (!(stage instanceof HTMLElement)) return;

    if (this._canvasReadOnly) return;

    let dragDidMove = false;

    const onMove = (/** @type {PointerEvent} */ e) => {
      if (this._dragIndex === null || !this._patches[this._dragIndex]) return;
      dragDidMove = true;

      const img = stage.querySelector('.tay-builder-canvas__bag');
      if (!(img instanceof HTMLImageElement)) return;

      const pt = pointerEventToBagNorm(stage, img, e);
      if (!pt) return;

      const nx = clamp01(pt.nx + this._dragGrabNx);
      const ny = clamp01(pt.ny + this._dragGrabNy);

      const patch = this._patches[this._dragIndex];
      const cleared = this.clampPatchPlacement(nx, ny, patch);
      patch.nx = cleared.nx;
      patch.ny = cleared.ny;

      const el = stage.querySelector(`[data-patch-index="${this._dragIndex}"]`);
      if (el instanceof HTMLElement) {
        const cx = pt.bag.left + cleared.nx * pt.bag.width;
        const cy = pt.bag.top + cleared.ny * pt.bag.height;
        el.style.left = `${(cx / stage.clientWidth) * 100}%`;
        el.style.top = `${(cy / stage.clientHeight) * 100}%`;
        updatePatchActionPills(stage);
      }
    };

    const endDrag = () => {
      if (this._dragCaptureEl != null && this._dragPointerId >= 0) {
        try {
          this._dragCaptureEl.releasePointerCapture(this._dragPointerId);
        } catch (_) {
          /* already released */
        }
        this._dragCaptureEl = null;
        this._dragPointerId = -1;
      }

      const finished = this._dragIndex;
      if (finished != null && this._patches[finished]) {
        this._patchStackSeq += 1;
        this._patches[finished].stackOrder = this._patchStackSeq;
        const el = stage.querySelector(`[data-patch-index="${finished}"]`);
        const layersEl = stage.querySelector('.tay-builder-canvas__layers');
        if (el && layersEl) layersEl.appendChild(el);
      }
      stage.querySelectorAll('.tay-builder-canvas-patch').forEach((n) => {
        if (n instanceof HTMLElement) n.style.zIndex = '';
      });
      this._dragIndex = null;
      this._dragGrabNx = 0;
      this._dragGrabNy = 0;
      if (this._dragListeners) {
        document.removeEventListener('pointermove', this._dragListeners.move);
        document.removeEventListener('pointerup', this._dragListeners.up);
        document.removeEventListener('pointercancel', this._dragListeners.cancel);
        this._dragListeners = null;
      }
      this.scheduleGeometryUpdate();
      if (!dragDidMove && this._step === 2 && finished != null) {
        if (!this.isMobileLayout()) this._editorDetailsOpen = true;
        this.render();
      }
      dragDidMove = false;
    };

    stage.querySelectorAll('[data-patch-index]').forEach((node) => {
      node.addEventListener('pointerdown', (e) => {
        dragDidMove = false;
        if (e.target instanceof HTMLElement && e.target.closest('.tay-builder-canvas-patch__handles')) return;
        if (!(node instanceof HTMLElement)) return;
        const idx = Number(node.dataset.patchIndex);
        if (Number.isNaN(idx)) return;
        e.preventDefault();
        this._selectedPatchIndex = idx;
        this.pushUndo();
        this._dragIndex = idx;

        const img = stage.querySelector('.tay-builder-canvas__bag');
        const p = this._patches[idx];
        const pt = img instanceof HTMLImageElement ? pointerEventToBagNorm(stage, img, e) : null;
        if (pt && p) {
          const pnx = typeof p.nx === 'number' ? p.nx : 0.5;
          const pny = typeof p.ny === 'number' ? p.ny : 0.5;
          this._dragGrabNx = pnx - pt.nx;
          this._dragGrabNy = pny - pt.ny;
        } else {
          this._dragGrabNx = 0;
          this._dragGrabNy = 0;
        }

        try {
          node.setPointerCapture(e.pointerId);
          this._dragCaptureEl = node;
          this._dragPointerId = e.pointerId;
        } catch (_) {
          this._dragCaptureEl = null;
          this._dragPointerId = -1;
        }

        stage.querySelectorAll('.tay-builder-canvas-patch').forEach((n) => {
          if (!(n instanceof HTMLElement)) return;
          const pi = Number(n.dataset.patchIndex);
          if (Number.isNaN(pi)) return;
          n.style.zIndex = pi === idx ? '30' : '10';
        });
        this._dragListeners = { move: onMove, up: endDrag, cancel: endDrag };
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', endDrag);
        document.addEventListener('pointercancel', endDrag);

        const slider = this.querySelector('[data-ref="rotation-slider"]');
        const degEl = this.querySelector('[data-ref="rotation-degree"]');
        const labels = this._config?.labels || {};
        const degSuffix = labels.degreeSuffix || '°';
        if (slider instanceof HTMLInputElement && p) {
          slider.value = String(normalizeRotationDeg(p.rot));
          slider.setAttribute('aria-valuenow', slider.value);
        }
        if (degEl && p) {
          degEl.textContent = `${normalizeRotationDeg(p.rot)}${degSuffix}`;
        }

        stage.querySelectorAll('.tay-builder-canvas-patch').forEach((n) => {
          if (!(n instanceof HTMLElement)) return;
          const pi = Number(n.dataset.patchIndex);
          if (Number.isNaN(pi)) return;
          n.classList.toggle('is-selected', pi === idx);
        });
        updatePatchActionPills(stage);
        this.updateStripSelectionVisual(idx);
      });
    });
  }

  /**
   * @param {HTMLButtonElement} submitBtn
   */
  async handleAddToCart(submitBtn) {
    const cfg = this._config;
    if (!cfg) return;

    const errorEl = this.querySelector('[data-ref="error"]');
    const bagVariant = this.getBagVariant();

    const blocker = this.getSubmitBlockerMessage();
    if (blocker) {
      this.showError(blocker);
      return;
    }

    if (!bagVariant || !bagVariant.available) {
      return;
    }

    if (this._patches.length) {
      try {
        const cart = await fetchCartJson();
        const overlaps = findLoosePatchOverlaps(cart, this._patches);
        if (overlaps.length) {
          const choice = await this.promptLoosePatchReuse(overlaps);
          if (choice === 'cancel') return;
          if (choice === 'sew') {
            try {
              await reduceLoosePatchQuantities(overlaps);
            } catch (e) {
              console.warn('[tay-tote-builder] could not convert loose patches', e);
              this.showError(
                cfg.labels.looseReuseConvertFailed ||
                  'Could not update the loose patch in your cart. Please try again.'
              );
              return;
            }
          }
        }
      } catch (e) {
        console.warn('[tay-tote-builder] loose patch check failed', e);
      }
    }

    const customizationId = randomCustomizationId();
    const bagProduct = this.getBagProduct();
    const patchSummary = buildPatchListSummary(this._patches, cfg.labels);
    const pieceCount = String(1 + this._patches.length);
    const placementStr = serializePatchPlacement(this._patches);
    const designTitle = (this._designTitle || bagProduct?.title || 'My tote').trim();
    const requiresPreviewUpload = Boolean(String(cfg.previewUploadUrl ?? '').trim());
    const bagWIn = Number(bagProduct?.bag_width_in) || Number(cfg.defaultBagWidthIn) || 22.5;
    const bagHIn = Number(bagProduct?.bag_height_in) || Number(cfg.defaultBagHeightIn) || 14.5;
    const bagSummary = clipLineProperty(
      `${bagProduct?.title ?? ''}|${bagVariant.title ?? ''}|${Math.round(bagWIn)}x${Math.round(bagHIn)}in`,
      200
    );

    submitBtn.disabled = true;
    const prevLabel = submitBtn.textContent;
    submitBtn.textContent = cfg.labels.preparingDesign || cfg.labels.adding || 'Saving your design preview…';
    if (errorEl instanceof HTMLElement) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    /** @type {{ front: string | null; back: string | null }} */
    let previewURLs = { front: null, back: null };
    try {
      previewURLs = await this.uploadCheckoutPreviews(customizationId);
    } catch (e) {
      console.error('[tay-tote-builder] preview upload', e);
      const msg =
        cfg.labels.previewUploadFailed ||
        'Could not save your design preview. Please try again.';
      this.showError(msg);
      submitBtn.disabled = false;
      submitBtn.textContent = prevLabel ?? this.orderSubmitLabel();
      return;
    }

    if (
      requiresPreviewUpload &&
      !previewURLs.front &&
      !previewURLs.back &&
      !previewURLs.left &&
      !previewURLs.right
    ) {
      this.showError(
        cfg.labels.previewUploadFailed ||
          'Could not save your design preview. Please try again.'
      );
      submitBtn.disabled = false;
      submitBtn.textContent = prevLabel ?? this.orderSubmitLabel();
      return;
    }

    const frontPlacements = clipLineProperty(placementsJSONForSide(this._patches, 'front'), 255);
    const backPlacements = clipLineProperty(placementsJSONForSide(this._patches, 'back'), 255);
    const leftPlacements = clipLineProperty(placementsJSONForSide(this._patches, 'left'), 255);
    const rightPlacements = clipLineProperty(placementsJSONForSide(this._patches, 'right'), 255);
    const patchTokens = this._patches
      .map((p) => {
        const side = patchSideOf(p);
        const code =
          side === 'back' ? 'B' : side === 'left' ? 'L' : side === 'right' ? 'R' : 'F';
        return `${code}:${p.variantId}`;
      })
      .join(',');

    /** @type {{ id: number; quantity: number; properties: Record<string, string> }[]} */
    const items = [];

    /** @type {Record<string, string>} */
    const bagProps = {
      'Design ID': customizationId,
      'Customization ID': customizationId,
      'Design title': clipLineProperty(designTitle, 200),
      Part: 'Bag',
      Bag: bagSummary,
      'Bag product': bagProduct?.title ?? '',
      Variant: bagVariant.title ?? '',
      SKU: bagVariant.sku ?? '',
      'Patch count': String(this._patches.length),
      'Design pieces': pieceCount,
    };
    if (patchTokens) {
      bagProps.Patches = clipLineProperty(patchTokens, 240);
    }
    if (patchSummary) {
      bagProps['Patch list'] = patchSummary;
    }
    if (placementStr) {
      bagProps['Patch placement'] = placementStr;
      bagProps['_Patch placement'] = placementStr;
    }
    if (frontPlacements && frontPlacements !== '[]') {
      bagProps['Placements front'] = frontPlacements;
    }
    if (backPlacements && backPlacements !== '[]') {
      bagProps['Placements back'] = backPlacements;
    }
    if (leftPlacements && leftPlacements !== '[]') {
      bagProps['Placements left'] = leftPlacements;
    }
    if (rightPlacements && rightPlacements !== '[]') {
      bagProps['Placements right'] = rightPlacements;
    }
    if (previewURLs.front) {
      bagProps['Preview front URL'] = previewURLs.front;
    }
    if (previewURLs.back) {
      bagProps['Preview back URL'] = previewURLs.back;
    }
    if (previewURLs.left) {
      bagProps['Preview left URL'] = previewURLs.left;
    }
    if (previewURLs.right) {
      bagProps['Preview right URL'] = previewURLs.right;
    }
    applyPreOrderLineProps(bagProps, bagProduct);

    items.push({
      id: bagVariant.id,
      quantity: 1,
      properties: bagProps,
    });

    const bagSkuForPatch = bagVariant.sku ?? '';

    this._patches.forEach((patch, index) => {
      const faceLbl = bagFaceLabel(patchSideOf(patch), cfg.labels);
      /** @type {Record<string, string>} */
      const patchProps = {
        'Design ID': customizationId,
        Part: 'Patch',
        'Patch product': patch.productTitle,
        'Patch number': String(index + 1),
        Variant: patch.variantTitle ?? '',
        SKU: patch.sku ?? '',
        'Bag face': faceLbl,
      };
      if (bagSkuForPatch) {
        patchProps['Matches bag SKU'] = bagSkuForPatch;
      }
      const patchProduct = (cfg.patches || []).find(
        (prod) =>
          Array.isArray(prod.variants) &&
          prod.variants.some((v) => Number(v.id) === Number(patch.variantId))
      );
      applyPreOrderLineProps(patchProps, patchProduct);
      items.push({
        id: patch.variantId,
        quantity: 1,
        properties: patchProps,
      });
    });

    let existingIds = [];
    const replaceId = String(this._replaceDesignId || '').trim();
    try {
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (cartRes.ok) {
        const cart = await cartRes.json();
        const seen = new Set();
        for (const line of cart.items || []) {
          const id = cartDesignId(line);
          if (id && id !== replaceId && !seen.has(id)) {
            seen.add(id);
            existingIds.push(id);
          }
        }
      }
    } catch {
      /* continue without prior ids */
    }
    if (!existingIds.includes(customizationId)) existingIds.push(customizationId);

    /** @type {Record<string, string>} */
    const cartAttributes = {
      design_id: customizationId,
      design_ids: clipLineProperty(existingIds.join(','), 255),
      design_title: clipLineProperty(designTitle, 255),
      purchaser_tz: clipLineProperty(
        Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        80
      ),
    };

    submitBtn.textContent = cfg.labels.adding || 'Adding to order…';

    const sectionIds = getCartSectionIds();
    const payload = /** @type {Record<string, unknown>} */ ({
      items,
      attributes: cartAttributes,
      sections_url: window.location.pathname,
    });
    if (sectionIds.length) {
      payload.sections = sectionIds.join(',');
    }

    try {
      const response = await fetch(cartAddUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status) {
        if (errorEl instanceof HTMLElement) {
          errorEl.hidden = false;
          errorEl.textContent = data.message || 'Could not add to cart.';
        }
        submitBtn.disabled = false;
        submitBtn.textContent = prevLabel ?? this.orderSubmitLabel();
        return;
      }

      if (replaceId && replaceId !== customizationId) {
        try {
          await removeCartItemsByDesignId(replaceId);
        } catch (e) {
          console.warn('[tay-tote-builder] could not remove previous bag', e);
        }
      }

      this._replaceDesignId = customizationId;
      this._lastAdded = {
        title: designTitle,
        front: previewURLs.front,
        back: previewURLs.back,
        customizationId,
      };
      this._showAddedPanel = false;
      this.persistDraftNow();
      document.dispatchEvent(
        new CustomEvent('tay:cart-updated', {
          bubbles: true,
          detail: { itemCount: data.item_count, open: true },
        })
      );
      this.showToast(cfg.labels.addedToast || 'Bag added to your order.', 'success');
      this.render();
    } catch (e) {
      console.error('[tay-tote-builder]', e);
      if (errorEl instanceof HTMLElement) {
        errorEl.hidden = false;
        errorEl.textContent = 'Something went wrong. Please try again.';
      }
      submitBtn.disabled = false;
      submitBtn.textContent = prevLabel ?? this.orderSubmitLabel();
      return;
    }
  }
}

if (!customElements.get('tay-tote-builder')) {
  customElements.define('tay-tote-builder', TayToteBuilder);
}
